import React, { useState, useEffect, createContext, useContext, useRef, useMemo, lazy, Suspense, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import '@/App.css';
import '@/index.css';
import SecurityProtection from './security';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ViewerLimitsProvider, useViewerLimits } from './contexts/ViewerLimitsContext';
import ViewerContentOverlay from './components/ViewerContentOverlay';

// Lazy load para páginas principais (refatoradas)
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const CreatorAnalytics = lazy(() => import('./pages/CreatorAnalytics'));
const CreatorProfilePage = lazy(() => import('./pages/CreatorProfile'));
const FeedPage = lazy(() => import('./pages/Feed'));
const MessagesPage = lazy(() => import('./pages/Messages'));
const AplicarPage = lazy(() => import('./pages/AplicarPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const FundadorCadastro = lazy(() => import('./pages/FundadorCadastro'));
const ConviteIndicado = lazy(() => import('./pages/ConviteIndicado'));
const CasaisSolteiras = lazy(() => import('./pages/CasaisSolteiras'));
const GodparentDashboardPage = lazy(() => import('./pages/GodparentDashboardPage'));
const LiveStreamPage = lazy(() => import('./pages/LiveStreamPage'));
const LivesPage = lazy(() => import('./pages/LivesPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

// Lazy load para páginas legais
const TermosDeUso = lazy(() => import('./pages/legal/TermosDeUso'));
const PoliticaPrivacidade = lazy(() => import('./pages/legal/PoliticaPrivacidade'));
const TermosMembroFundador = lazy(() => import('./pages/legal/TermosMembroFundador'));
const PoliticaPagamentos = lazy(() => import('./pages/legal/PoliticaPagamentos'));
const CodigoConduta = lazy(() => import('./pages/legal/CodigoConduta'));

// Lazy load para páginas SEO
const CriadoresAdultos = lazy(() => import('./pages/seo/CriadoresAdultos'));
const MonetizarConteudo = lazy(() => import('./pages/seo/MonetizarConteudo'));
const CasaisLiberais = lazy(() => import('./pages/seo/CasaisLiberais'));
const PlataformaCriadores = lazy(() => import('./pages/seo/PlataformaCriadores'));

// Lazy load para páginas de anúncios (Ads)
const AdsCriadores = lazy(() => import('./pages/ads/AdsCriadores'));
const AdsCasais = lazy(() => import('./pages/ads/AdsCasais'));
const AdsMembroFundador = lazy(() => import('./pages/ads/AdsMembroFundador'));

// Components de Presente
import { GiftButton, GiftHistory } from './components/gifts';

// Components de Menção
import { MentionInput, MentionText } from './components/mentions';

// Push Notifications & Native Gestures
import PushNotificationPrompt from './components/PushNotificationPrompt';

// Components de Padrinho/Madrinha
import { GodparentDashboard } from './components/godparent';

// Components de Fundador
import FundadorDashboard from './components/FundadorDashboard';

// Components de Gamificação
import GamificationProfile, { GamificationMiniCard } from './components/GamificationProfile';

// Components de Dados Bancários
import BankDataForm from './components/BankDataForm';

// Components de Saques
import WithdrawalSection from './components/WithdrawalSection';

// Admin Components
import AdminGodparents from './components/admin/AdminGodparents';
import AdminSocialInvites from './components/admin/AdminSocialInvites';
import AdminSocialApprovals from './components/admin/AdminSocialApprovals';
import MyInvites from './pages/MyInvites';
import AdminCandidatos from './components/admin/AdminCandidatos';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminWithdrawals from './components/admin/AdminWithdrawals';
import AdminMasters from './components/admin/AdminMasters';
import AdminUsersPage from './components/admin/AdminUsers';

// Admin Analytics Pages
const OnlineUsersPage = lazy(() => import('./pages/admin/OnlineUsersPage'));
const VisitsAnalyticsPage = lazy(() => import('./pages/admin/VisitsAnalyticsPage'));
const AdminSupportPage = lazy(() => import('./pages/admin/AdminSupportPage'));
const AdminBannersPage = lazy(() => import('./pages/admin/AdminBannersPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const BannerDetailPage = lazy(() => import('./pages/EventsPage').then(m => ({ default: m.BannerDetailPage })));

// Support Chatbot
import SupportChatbot from './components/SupportChatbot';

// Chat Components
import CreatorChatDashboard from './components/CreatorChatDashboard';
import { PrivateChatButton, UserBroadcastFeed } from './components/UserChatComponents';

// PWA Components
import { PWAInstallPrompt } from './components/pwa';
import UpdateNotification from './components/UpdateNotification';
import PixPostModal from './components/PixPostModal';
import PushNotificationBanner from './components/PushNotificationBanner';
import PixRegistrationBanner from './components/PixRegistrationBanner';

// Legal Footer
import LegalFooter from './components/LegalFooter';

// Navbar Component
import Navbar from './components/Navbar';

// Usar o mesmo domínio que a página está sendo servida
const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : '';
const API = `${BACKEND_URL}/api`;

// Loading Spinner para Lazy Loading
const LazyLoadingSpinner = () => (
  <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#C79F5E] border-t-transparent mx-auto mb-4"></div>
      <p className="text-xl text-gray-400">Carregando...</p>
    </div>
  </div>
);

// AuthContext importado de ./contexts/AuthContext
const ThemeContext = createContext();

const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Carregar tema salvo do localStorage
    const savedTheme = localStorage.getItem('lyberhot-theme');
    return savedTheme || 'dark';
  });

  useEffect(() => {
    // Aplicar tema ao document
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('lyberhot-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      console.log(`🎨 Alternando tema: ${prev} → ${newTheme}`);
      return newTheme;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// AuthProvider importado de ./contexts/AuthContext

const Landing = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [refParam, setRefParam] = useState(null);
  const [godparentInvite, setGodparentInvite] = useState(null);
  const [godparentInfo, setGodparentInfo] = useState(null);
  // ===== 3 portas de entrada =====
  const [socialInviteToken, setSocialInviteToken] = useState('');
  const [socialInviteInfo, setSocialInviteInfo] = useState(null); // { valid, invite_type, title }
  const [entryDoor, setEntryDoor] = useState('free'); // 'free' | 'creator' | 'subscriber'

  // Carregar email salvo se existir
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const validateGodparentInvite = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API}/godparent/validate-invite/${token}`);
      setGodparentInfo(response.data);
    } catch (error) {
      console.error('Convite inválido:', error);
      toast.error('Convite de Padrinho/Madrinha inválido ou já utilizado');
      setGodparentInvite(null);
    }
  }, []);

  const validateSocialInvite = useCallback(async (token) => {
    if (!token || token.trim().length < 4) {
      setSocialInviteInfo(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/social-invites/validate/${token.trim()}`);
      setSocialInviteInfo(response.data);
    } catch (error) {
      setSocialInviteInfo({ valid: false, error: 'Convite social inválido ou já utilizado' });
    }
  }, []);

  useEffect(() => {
    // Capturar parâmetros da URL
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    const mode = params.get('mode');
    const godparentToken = params.get('godparent_invite');
    const socialInv = params.get('social_invite');
    
    if (ref) {
      setRefParam(ref);
      // Salvar no localStorage para persistir após o cadastro/login
      localStorage.setItem('creator_referral', ref);
      // Assinante via link do criador é a porta 3
      setEntryDoor('subscriber');
      setIsRegisterMode(true);
    }
    
    if (mode === 'register') {
      setIsRegisterMode(true);
    }
    
    // Validar convite de godparent
    if (godparentToken) {
      setGodparentInvite(godparentToken);
      setIsRegisterMode(true);
      validateGodparentInvite(godparentToken);
    }
    
    // Convite social (porta 1 — Rede Social)
    if (socialInv) {
      setSocialInviteToken(socialInv);
      setEntryDoor('free');
      setIsRegisterMode(true);
      validateSocialInvite(socialInv);
    }
  }, [location, validateGodparentInvite, validateSocialInvite]);

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/auth-callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Salvar ou remover email baseado no "lembrar-me"
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }
    
    try {
      const success = await login(email.trim(), password, rememberMe);
      if (success) {
        // Verificar se há referência de criador salva
        const creatorRef = localStorage.getItem('creator_referral');
        if (creatorRef) {
          localStorage.removeItem('creator_referral');
          // Buscar ID do criador pelo username
          try {
            const response = await axios.get(`${API}/c/${creatorRef}`);
            const creatorId = response.data.creator.id;
            navigate(`/creator/${creatorId}`);
            return;
          } catch (error) {
            console.error('Erro ao buscar criador:', error);
          }
        }
        navigate('/feed');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (error.response?.status === 401) {
        toast.error('Email ou senha incorretos');
      } else if (error.response?.status === 403) {
        toast.error('Conta bloqueada. Entre em contato com o suporte.');
      } else {
        toast.error('Erro ao fazer login. Tente novamente.');
      }
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validar aceite dos termos
    if (!acceptedTerms) {
      toast.error('Você precisa aceitar os Termos de Uso e Política de Privacidade');
      return;
    }
    
    // Regras do sistema de 3 portas (godparent bypassa)
    if (!godparentInvite) {
      if (entryDoor === 'free' && (!socialInviteInfo || !socialInviteInfo.valid)) {
        toast.error('Convite social inválido. Peça um novo link para o admin.');
        return;
      }
      if (entryDoor === 'subscriber' && !refParam) {
        toast.error('Cadastro de assinante requer um link de criador.');
        return;
      }
    }
    
    try {
      const requestedType = godparentInvite ? 'creator' : entryDoor;
      const registerData = {
        email,
        name,
        password,
        role: godparentInvite ? 'creator' : (entryDoor === 'creator' ? 'creator' : 'fan'),
        accepted_terms: true,
        accepted_terms_version: '1.0.0',
        requested_account_type: requestedType,
      };
      
      if (refParam) {
        registerData.ref = refParam;
      }
      
      if (godparentInvite) {
        registerData.godparent_invite = godparentInvite;
      }
      
      if (entryDoor === 'free' && socialInviteToken) {
        registerData.social_invite_token = socialInviteToken.trim();
      }
      
      const response = await axios.post(`${API}/auth/register`, registerData);
      localStorage.setItem('token', response.data.token);
      
      // Mensagem personalizada por porta
      if (godparentInvite && godparentInfo) {
        toast.success(`Bem-vindo(a)! Você agora é ${godparentInfo.title}!`);
      } else if (entryDoor === 'subscriber') {
        toast.success('Bem-vindo! Nas próximas 24h você acessa o conteúdo do criador que te convidou. Depois disso, liberamos o feed dos outros criadores.', { duration: 8000 });
      } else if (entryDoor === 'free') {
        toast.success(`Bem-vindo à Rede Social LYBERHOT!`);
      } else {
        toast.success('Bem-vindo ao LYBERHOT - A Liberdade em Alto Nível!');
      }
      
      // Verificar se há referência de criador salva
      const creatorRef = localStorage.getItem('creator_referral');
      
      // Godparents vão direto para o dashboard
      if (godparentInvite) {
        navigate('/godparent-dashboard');
        window.location.reload();
        return;
      }
      
      // Sempre redirecionar para o Quiz primeiro (novo usuário)
      if (creatorRef) {
        // Salvar referência para usar depois do quiz
        localStorage.setItem('post_quiz_redirect', `/c/${creatorRef}`);
      }
      
      // Redirecionar para o Quiz de Perfil
      navigate('/quiz');
      window.location.reload();
    } catch (error) {
      // Handle Pydantic validation errors (array of error objects)
      const detail = error.response?.data?.detail;
      let errorMessage = 'Erro ao criar conta';
      
      if (Array.isArray(detail) && detail.length > 0) {
        // Extract message from first validation error
        errorMessage = detail[0]?.msg?.replace('Value error, ', '') || errorMessage;
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      }
      
      toast.error(errorMessage);
    }
  };

  // Estado para criadores online na landing
  const [onlineCreators, setOnlineCreators] = useState([]);

  // Helper para URL de imagem
  const getCreatorImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    if (image.startsWith('/uploads')) {
      const token = localStorage.getItem('token');
      const baseUrl = `${BACKEND_URL}/api${image}`;
      // Adicionar token para posts premium
      if (image.includes('/posts/') && token) {
        return `${baseUrl}?token=${token}`;
      }
      return baseUrl;
    }
    if (image.startsWith('data:')) return image;
    return `data:image/jpeg;base64,${image}`;
  };

  useEffect(() => {
    // Buscar criadores online para a landing
    const fetchOnlineCreators = async () => {
      try {
        const response = await axios.get(`${API}/creators/online?limit=5`);
        setOnlineCreators(response.data || []);
      } catch (error) {
        console.log('Erro ao buscar criadores online:', error);
      }
    };
    fetchOnlineCreators();
  }, []);

  return (
    <div className="min-h-screen flex bg-[#0D0D0D] relative pb-12 items-start">
      {/* Lado Esquerdo - Imagem de Fundo (fixado à altura da tela para não esticar quando o formulário cresce) */}
      <div className="hidden lg:block lg:w-[55%] relative overflow-hidden sticky top-0 self-start h-screen">
        {/* Imagem de fundo com logo LYBERHOT */}
        <div 
          className="absolute inset-0 bg-cover bg-[center_top]"
          style={{
            backgroundImage: 'url(/lyber-home-bg.png)',
            backgroundPosition: 'center 20%',
          }}
        />
        {/* Slogan posicionado abaixo dos textos da imagem — usa % relativo à altura da tela */}
        <div data-testid="hero-slogan" className="absolute top-[52%] left-8 right-8 z-10">
          <p className="text-sm text-gray-400">
            Seu portal para liberdade, comunidade e conteúdo premium
          </p>
        </div>
        {/* Criadores Online */}
        <div className="absolute bottom-8 left-8 z-10">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span className="text-white text-sm font-medium">Criadores Online</span>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <div className="flex -space-x-3">
              {onlineCreators.length > 0 ? (
                onlineCreators.slice(0, 6).map((creator, idx) => (
                  <div key={creator.id || idx} className="relative">
                    {creator.picture ? (
                      <img 
                        src={getCreatorImageUrl(creator.picture)}
                        alt=""
                        className="w-10 h-10 rounded-full border-2 border-[#0D0D0D] object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full border-2 border-[#0D0D0D] bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold">
                        {creator.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-[#0D0D0D] rounded-full"></span>
                  </div>
                ))
              ) : (
                <>
                  <div className="w-10 h-10 rounded-full border-2 border-[#0D0D0D] bg-gradient-to-br from-purple-500 to-pink-500"></div>
                  <div className="w-10 h-10 rounded-full border-2 border-[#0D0D0D] bg-gradient-to-br from-pink-500 to-red-500"></div>
                  <div className="w-10 h-10 rounded-full border-2 border-[#0D0D0D] bg-gradient-to-br from-yellow-500 to-orange-500"></div>
                  <div className="w-10 h-10 rounded-full border-2 border-[#0D0D0D] bg-gradient-to-br from-blue-500 to-cyan-500"></div>
                  <div className="w-10 h-10 rounded-full border-2 border-[#0D0D0D] bg-gradient-to-br from-green-500 to-teal-500"></div>
                  <div className="w-10 h-10 rounded-full border-2 border-[#0D0D0D] bg-gradient-to-br from-indigo-500 to-purple-500"></div>
                </>
              )}
            </div>
            <span className="text-gray-400 text-sm">
              {onlineCreators.length > 0 ? `+${onlineCreators.length} online agora` : '+1.000 criadores'}
            </span>
          </div>
        </div>
      </div>
      
      {/* Lado Direito - Área Preta com Formulário */}
      <div className="w-full lg:w-[45%] min-h-screen bg-[#0D0D0D] flex flex-col justify-center p-4 sm:p-5 lg:p-6 relative">
        {/* Background Mobile */}
        <div 
          className="lg:hidden absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(/lyber-home-bg.png)',
          }}
        />
        <div className="lg:hidden absolute inset-0 bg-[#0D0D0D]/90" />
        
        {/* Conteúdo */}
        <div className="relative z-10 w-full max-w-sm mx-auto">
          {/* Card do Formulário - Fundo preto com borda dourada */}
          <div className="bg-[#0D0D0D] p-5 rounded-2xl border border-[#C79F5E]/40 shadow-[0_0_30px_rgba(199,159,94,0.15)]">
            {/* Logo e Slogan */}
            <div className="flex flex-col items-center mb-4">
              <img 
                src="/lyber-logo.png" 
                alt="LYBERHOT" 
                className="h-14 md:h-16 w-auto mb-1"
              />
              <p className="text-[#C79F5E] text-xs font-medium tracking-wide">Onde o Exclusivo Acontece</p>
            </div>

            {/* Tabs de Login/Cadastro - Destacadas no Topo */}
            <div className="flex rounded-xl bg-black/40 p-1 mb-4">
              <button
                type="button"
                onClick={() => setIsRegisterMode(false)}
                className={`flex-1 py-2.5 px-3 rounded-lg font-bold text-sm transition-all ${
                  !isRegisterMode
                    ? 'bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] text-white shadow-lg'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Fazer Login
              </button>
              <button
                type="button"
                onClick={() => setIsRegisterMode(true)}
                className={`flex-1 py-2.5 px-3 rounded-lg font-bold text-sm transition-all ${
                  isRegisterMode
                    ? 'bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black shadow-lg'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Criar Conta
              </button>
            </div>

            {/* Título com descrição */}
            <div className="mb-4 text-center">
              <p className="text-gray-400 text-sm">
                {isRegisterMode 
                  ? 'Crie sua conta grátis e comece agora' 
                  : 'Bem-vindo de volta!'}
              </p>
            </div>

            {/* Banner de Convite Godparent */}
            {godparentInvite && godparentInfo && (
              <div className="mb-4 p-3 bg-gradient-to-r from-[#4D1A4A]/30 to-[#6B2D5C]/30 border border-[#C79F5E]/50 rounded-xl">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl">🔥</span>
                  <span className="text-[#C79F5E] font-bold text-sm">{godparentInfo.title}</span>
                </div>
                <p className="text-gray-300 text-xs">
                  Você foi convidado para ser {godparentInfo.godparent_type === 'padrinho' ? 'um Padrinho' : 'uma Madrinha'} Hot!
                </p>
              </div>
            )}

            <form onSubmit={isRegisterMode ? handleRegister : handleLogin} className="space-y-3">
              {isRegisterMode && (
                <div>
                  <input
                    type="text"
                    placeholder="Nome do Perfil"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-[#1A1A1A] border border-[#C79F5E]/30 focus:border-[#C79F5E] focus:ring-2 focus:ring-[#C79F5E]/50 rounded-xl px-4 py-2.5 text-white placeholder:text-gray-500 text-sm"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1 ml-1">Seu perfil será criado com esse nome</p>
                </div>
              )}

              {/* SELETOR DE PORTA DE ENTRADA — não mostra para godparent/subscriber com ref/social com token */}
              {isRegisterMode && !godparentInvite && !refParam && !socialInviteToken && (
                <div data-testid="entry-door-selector" className="space-y-2">
                  <p className="text-xs text-gray-400 ml-1">Como você quer entrar?</p>
                  <button
                    type="button"
                    data-testid="door-free"
                    onClick={() => setEntryDoor('free')}
                    className={`w-full text-left rounded-xl p-3 border transition-all ${
                      entryDoor === 'free'
                        ? 'border-[#C79F5E] bg-[#C79F5E]/10 shadow-[0_0_15px_rgba(199,159,94,0.2)]'
                        : 'border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#C79F5E]/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${entryDoor === 'free' ? 'border-[#C79F5E]' : 'border-gray-500'}`}>
                        {entryDoor === 'free' && <span className="w-2 h-2 rounded-full bg-[#C79F5E]" />}
                      </span>
                      <span className="text-sm font-semibold text-white">Rede Social (Casais / Solteiras / Solteiros)</span>
                    </div>
                    <p className="text-xs text-gray-400 ml-6 mt-0.5">Precisa de convite. Curte e posta grátis, sem acesso a conteúdo pago.</p>
                  </button>
                  <button
                    type="button"
                    data-testid="door-creator"
                    onClick={() => setEntryDoor('creator')}
                    className={`w-full text-left rounded-xl p-3 border transition-all ${
                      entryDoor === 'creator'
                        ? 'border-[#C79F5E] bg-[#C79F5E]/10 shadow-[0_0_15px_rgba(199,159,94,0.2)]'
                        : 'border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#C79F5E]/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${entryDoor === 'creator' ? 'border-[#C79F5E]' : 'border-gray-500'}`}>
                        {entryDoor === 'creator' && <span className="w-2 h-2 rounded-full bg-[#C79F5E]" />}
                      </span>
                      <span className="text-sm font-semibold text-white">Criador de Conteúdo</span>
                    </div>
                    <p className="text-xs text-gray-400 ml-6 mt-0.5">Passa pela triagem do admin (Link de Fundador).</p>
                  </button>
                </div>
              )}

              {/* Banner: Assinante via link do criador */}
              {isRegisterMode && refParam && !godparentInvite && (
                <div data-testid="subscriber-banner" className="p-3 bg-gradient-to-r from-[#4D1A4A]/30 to-[#6B2D5C]/30 border border-[#C79F5E]/50 rounded-xl">
                  <p className="text-[#C79F5E] font-bold text-sm mb-0.5">🎟️ Cadastro de Assinante</p>
                  <p className="text-gray-300 text-xs">
                    Você está entrando pelo link de <strong>@{refParam}</strong>. Nas primeiras 24h você acessa só o conteúdo desse criador. Depois, o feed dos demais criadores é liberado.
                  </p>
                </div>
              )}

              {/* Campo de convite social — só quando porta = free e sem token na URL */}
              {isRegisterMode && !godparentInvite && !refParam && entryDoor === 'free' && (
                <div>
                  <input
                    data-testid="social-invite-input"
                    type="text"
                    placeholder="Código do convite social"
                    value={socialInviteToken}
                    onChange={(e) => {
                      setSocialInviteToken(e.target.value);
                      validateSocialInvite(e.target.value);
                    }}
                    className={`w-full bg-[#1A1A1A] border rounded-xl px-4 py-2.5 text-white placeholder:text-gray-500 text-sm ${
                      socialInviteInfo?.valid
                        ? 'border-green-500/60 focus:border-green-500'
                        : socialInviteInfo?.error
                        ? 'border-red-500/60 focus:border-red-500'
                        : 'border-[#C79F5E]/30 focus:border-[#C79F5E]'
                    }`}
                  />
                  {socialInviteInfo?.valid && (
                    <p data-testid="social-invite-valid" className="text-xs text-green-400 mt-1 ml-1">
                      ✓ Convite válido — <strong>{socialInviteInfo.title}</strong>
                    </p>
                  )}
                  {socialInviteInfo?.error && (
                    <p data-testid="social-invite-error" className="text-xs text-red-400 mt-1 ml-1">✗ {socialInviteInfo.error}</p>
                  )}
                  {!socialInviteInfo && (
                    <p className="text-xs text-gray-400 mt-1 ml-1">Cole aqui o código do convite que você recebeu</p>
                  )}
                </div>
              )}

              {/* Confirmação de convite social preenchido via URL */}
              {isRegisterMode && !godparentInvite && socialInviteInfo?.valid && socialInviteToken && (
                <div data-testid="social-invite-banner" className="p-3 bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-500/40 rounded-xl">
                  <p className="text-green-400 font-bold text-sm">✓ Convite Social — {socialInviteInfo.title}</p>
                </div>
              )}
              
              <div>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#1A1A1A] border border-[#C79F5E]/30 focus:border-[#C79F5E] focus:ring-2 focus:ring-[#C79F5E]/50 rounded-xl px-4 py-2.5 text-white placeholder:text-gray-500 text-sm"
                  required
                />
              </div>

              {/* Campo de senha com botão mostrar/ocultar */}
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#1A1A1A] border border-[#C79F5E]/30 focus:border-[#C79F5E] focus:ring-2 focus:ring-[#C79F5E]/50 rounded-xl px-4 py-2.5 pr-11 text-white placeholder:text-gray-500 text-sm"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#C79F5E] transition-colors p-1"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Checkbox Lembrar-me (apenas no login) */}
              {!isRegisterMode && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-600 bg-[#1A1A1A] text-[#C79F5E] focus:ring-[#C79F5E] focus:ring-offset-0 cursor-pointer"
                    />
                    <span className="ml-2 text-xs text-gray-400 group-hover:text-gray-300 transition-colors">
                      Lembrar-me
                    </span>
                  </label>
                  <button
                    type="button"
                    className="text-xs text-[#C79F5E] hover:underline"
                    onClick={() => window.location.href = '/reset-password'}
                  >
                    Esqueceu a senha?
                  </button>
                </div>
              )}

              {/* Checkbox de Termos - Apenas no Cadastro */}
              {isRegisterMode && (
                <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-2 border-[#C79F5E]/50 bg-transparent checked:bg-[#C79F5E] checked:border-[#C79F5E] focus:ring-[#C79F5E] focus:ring-offset-0 cursor-pointer"
                    />
                    <span className="text-xs text-gray-400 leading-relaxed">
                      Li e concordo com os{' '}
                      <a href="/legal/termos-de-uso" target="_blank" rel="noopener noreferrer" className="text-[#C79F5E] hover:underline">
                        Termos de Uso
                      </a>
                      {' '}e a{' '}
                      <a href="/legal/privacidade" target="_blank" rel="noopener noreferrer" className="text-[#C79F5E] hover:underline">
                        Política de Privacidade
                      </a>.
                    </span>
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={isRegisterMode && !acceptedTerms}
                className={`w-full py-3 rounded-xl text-base font-bold transition-all ${
                  isRegisterMode && !acceptedTerms
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] hover:shadow-[0_0_25px_rgba(199,159,94,0.5)] text-black hover:scale-[1.02]'
                }`}
              >
                {isRegisterMode ? 'Criar Conta' : 'Entrar'}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#C79F5E]/30"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-[#0D0D0D] text-gray-500">ou</span>
              </div>
            </div>

            {/* Google Login */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full bg-white hover:bg-gray-100 text-gray-900 py-2.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all text-sm"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar com Google
            </button>

            {/* Toggle Mode - Destacado */}
            <div className="mt-4 text-center">
              {!isRegisterMode ? (
                <div className="bg-gradient-to-r from-[#C79F5E]/10 to-[#A67C3D]/10 border border-[#C79F5E]/30 rounded-xl p-3">
                  <p className="text-gray-300 text-sm">
                    Primeira vez aqui?{' '}
                    <button
                      type="button"
                      onClick={() => setIsRegisterMode(true)}
                      className="text-[#C79F5E] hover:text-[#D4AF37] font-bold underline"
                    >
                      Crie sua conta grátis →
                    </button>
                  </p>
                </div>
              ) : (
                <p className="text-gray-400 text-sm">
                  Já tem uma conta?{' '}
                  <button
                    type="button"
                    onClick={() => setIsRegisterMode(false)}
                    className="text-[#6B2D5C] hover:text-[#C79F5E] font-semibold underline"
                  >
                    Faça login
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Rodapé Legal */}
      <LegalFooter />
    </div>
  );
};

const AuthModal = ({ mode, setMode, onClose, refParam }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  // Se vem com refParam, sempre é fan. Se não, padrão é fan
  const [selectedRole, setSelectedRole] = useState(refParam ? 'fan' : 'fan');
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Carregar email salvo se existir
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'login') {
      // Salvar ou remover email baseado no "lembrar-me"
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      const success = await login(email, password, rememberMe);
      if (success) {
        navigate('/feed');
      }
    } else {
      // Usar register custom com refParam
      try {
        const registerData = {
          email,
          name,
          password,
          role: selectedRole
        };
        
        // Adicionar ref se existir
        if (refParam) {
          registerData.ref = refParam;
        }
        
        const response = await axios.post(`${API}/auth/register`, registerData);
        localStorage.setItem('token', response.data.token);
        toast.success('Cadastro realizado com sucesso!');
        
        // Se há refParam, redirecionar para o perfil do criador
        if (refParam && selectedRole === 'fan') {
          navigate(`/c/${refParam}`);
          window.location.reload(); // Reload para atualizar auth context
        } else if (selectedRole === 'fan') {
          navigate('/feed');
          window.location.reload();
        } else {
          navigate('/creator-setup');
          window.location.reload();
        }
      } catch (error) {
        // Handle Pydantic validation errors (array of error objects)
        const detail = error.response?.data?.detail;
        let errorMessage = 'Falha no cadastro';
        
        if (Array.isArray(detail) && detail.length > 0) {
          errorMessage = detail[0]?.msg?.replace('Value error, ', '') || errorMessage;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        
        toast.error(errorMessage);
      }
    }
  };

  return (
    <div data-testid="auth-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 max-w-lg w-full p-6 md:p-8 rounded-2xl my-8">
        {/* Logo e Slogan */}
        <div className="flex flex-col items-center mb-4">
          <img 
            src="/lyber-logo.png" 
            alt="LYBERHOT" 
            className="h-16 md:h-20 w-auto mb-2"
          />
          <p className="text-[#C79F5E] text-xs md:text-sm font-medium tracking-wide">Onde o Exclusivo Acontece</p>
        </div>

        {/* Botão Fechar */}
        <button 
          data-testid="close-auth-modal-btn" 
          onClick={onClose} 
          className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl bg-black/20 rounded-full w-8 h-8 flex items-center justify-center"
        >
          ✕
        </button>

        {/* Tabs de Login/Cadastro - Destacado */}
        <div className="flex rounded-xl bg-black/30 p-1 mb-6">
          <button
            type="button"
            onClick={() => setMode('login')}
            className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-all ${
              mode === 'login'
                ? 'bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] text-white shadow-lg'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Fazer Login
          </button>
          <button
            type="button"
            onClick={() => setMode('register')}
            className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-all ${
              mode === 'register'
                ? 'bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black shadow-lg'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Criar Conta
          </button>
        </div>

        {/* Título com descrição */}
        <div className="mb-5">
          <h2 className="text-xl md:text-2xl font-bold text-center">
            {mode === 'login' ? 'Bem-vindo de volta!' : 'Crie sua conta grátis'}
          </h2>
          <p className="text-center text-gray-400 text-sm mt-1">
            {mode === 'login' 
              ? 'Entre para acessar conteúdo exclusivo' 
              : 'Comece a explorar conteúdos exclusivos agora'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
          {mode === 'register' && (
            <>
              {/* Role Selection - Visual Cards (esconder se vir do link do criador) */}
              {!refParam && (
                <div className="mb-4 md:mb-6">
                  <label className="text-sm text-gray-400 mb-3 block font-semibold">Escolha seu perfil:</label>
                  <div className="grid grid-cols-2 gap-3 md:gap-4">
                    <button
                      type="button"
                      data-testid="select-fan-btn"
                      onClick={() => setSelectedRole('fan')}
                      className={`p-4 md:p-6 rounded-xl border-2 transition-all ${
                        selectedRole === 'fan'
                          ? 'border-[#4D1A4A] bg-[#6B2D5C]/10 shadow-lg shadow-red-500/20'
                          : 'border-white/10 bg-white/5 hover:border-white/30'
                      }`}
                    >
                      <div className="text-3xl md:text-4xl mb-2 md:mb-3">👤</div>
                      <h3 className="text-lg md:text-xl font-bold mb-1">Fã</h3>
                      <p className="text-xs text-gray-400 hidden md:block">Acompanhe criadores e acesse conteúdo exclusivo</p>
                      <p className="text-xs text-gray-400 md:hidden">Acompanhe criadores</p>
                    </button>

                    <button
                      type="button"
                      data-testid="select-creator-btn"
                      onClick={() => setSelectedRole('creator')}
                      className={`p-4 md:p-6 rounded-xl border-2 transition-all ${
                        selectedRole === 'creator'
                          ? 'border-[#4D1A4A] bg-[#6B2D5C]/10 shadow-lg shadow-red-500/20'
                          : 'border-white/10 bg-white/5 hover:border-white/30'
                    }`}
                  >
                    <div className="text-3xl md:text-4xl mb-2 md:mb-3">⭐</div>
                    <h3 className="text-lg md:text-xl font-bold mb-1">Criador</h3>
                    <p className="text-xs text-gray-400 hidden md:block">Monetize seu conteúdo e construa sua audiência</p>
                    <p className="text-xs text-gray-400 md:hidden">Monetize conteúdo</p>
                  </button>
                </div>
              </div>
              )}

              {/* Mensagem quando vem do link do criador */}
              {refParam && (
                <div className="bg-gradient-to-r from-[#4D1A4A]/20 to-[#6B2D5C]/20 border border-[#C79F5E]/30 p-4 rounded-xl mb-4">
                  <p className="text-center text-sm">
                    <span className="text-[#C79F5E] font-semibold">✨ Cadastro VIP</span>
                    <br />
                    <span className="text-gray-300">Você está se cadastrando para acessar conteúdo exclusivo</span>
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm text-gray-400 mb-2 block font-semibold">Nome do Perfil</label>
                <input
                  data-testid="register-name-input"
                  type="text"
                  placeholder="Nome do Perfil"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 focus:border-[#C79F5E] focus:ring-2 focus:ring-[#C79F5E]/50 rounded-lg px-4 py-3 text-white placeholder:text-gray-300 font-medium"
                  required
                />
                <p className="text-xs text-gray-400 mt-1 ml-1">Seu perfil será criado com esse nome</p>
              </div>
            </>
          )}
          
          <input
            data-testid="auth-email-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-white/10 border border-white/20 focus:border-[#C79F5E] focus:ring-2 focus:ring-[#C79F5E]/50 rounded-lg px-4 py-3 text-white placeholder:text-gray-300 font-medium"
            required
          />
          
          {/* Campo de senha com botão mostrar/ocultar */}
          <div className="relative">
            <input
              data-testid="auth-password-input"
              type={showPassword ? "text" : "password"}
              placeholder="Senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/10 border border-white/20 focus:border-[#C79F5E] focus:ring-2 focus:ring-[#C79F5E]/50 rounded-lg px-4 py-3 pr-12 text-white placeholder:text-gray-300 font-medium"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#C79F5E] transition-colors p-1"
              tabIndex={-1}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          
          {/* Checkbox Lembrar-me (apenas no login) */}
          {mode === 'login' && (
            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-white/10 text-[#C79F5E] focus:ring-[#C79F5E] focus:ring-offset-0 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                  Lembrar-me
                </span>
              </label>
              <button
                type="button"
                className="text-sm text-[#C79F5E] hover:underline"
                onClick={() => window.location.href = '/reset-password'}
              >
                Esqueceu a senha?
              </button>
            </div>
          )}
          
          <button
            data-testid="auth-submit-btn"
            type="submit"
            className="w-full bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] hover:from-red-700 hover:to-pink-700 text-white py-4 rounded-full font-bold text-lg transition-all shadow-lg hover:scale-105"
          >
            {mode === 'login' ? 'Entrar' : 'Criar Conta'}
          </button>
        </form>

        <p className="text-center text-gray-400 mt-6 text-sm">
          {mode === 'login' ? (
            <>
              Primeira vez aqui?{' '}
              <button
                data-testid="toggle-auth-mode-btn"
                onClick={() => setMode('register')}
                className="text-[#C79F5E] hover:text-[#D4AF37] hover:underline font-bold"
              >
                Crie sua conta grátis →
              </button>
            </>
          ) : (
            <>
              Já possui uma conta?{' '}
              <button
                data-testid="toggle-auth-mode-btn"
                onClick={() => setMode('login')}
                className="text-[#6B2D5C] hover:text-[#C79F5E] hover:underline font-semibold"
              >
                Faça login
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const [processing, setProcessing] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const processedRef = useRef(false);

  useEffect(() => {
    // Timeout de segurança - FORÇAR redirecionamento após 15s (aumentado)
    const forceRedirect = setTimeout(() => {
      console.log('Force redirecting to feed after timeout...');
      window.location.replace('/feed');
    }, 15000);

    const processSession = async () => {
      // Evitar processamento duplicado
      if (processedRef.current) return;
      processedRef.current = true;

      const hash = location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        clearTimeout(forceRedirect);
        toast.error('Sessão expirada. Por favor, tente novamente.');
        window.location.replace('/');
        return;
      }

      try {
        // Verificar se há referência de criador salva
        const creatorRef = localStorage.getItem('creator_referral');
        
        const headers = { 'X-Session-ID': sessionId };
        if (creatorRef) {
          headers['X-Creator-Ref'] = creatorRef;
        }

        const response = await axios.post(
          `${API}/auth/session`,
          {},
          {
            headers,
            withCredentials: true,
            timeout: 12000 // 12 segundos timeout
          }
        );

        if (response.data.user) {
          // Limpar timeout de segurança
          clearTimeout(forceRedirect);
          
          // Marcar como autenticado
          sessionStorage.setItem('just_authenticated', 'true');
          
          // Salvar session_token no localStorage para uso em requests
          if (response.data.session_token) {
            localStorage.setItem('session_token', response.data.session_token);
          }
          
          // Também gerar um JWT token para compatibilidade
          // O cookie já foi setado pelo backend, mas vamos garantir autenticação
          
          toast.success('🔥 Bem-vindo ao LYBERHOT!');
          
          // Aguardar um momento para o cookie ser processado
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Recarregar autenticação
          await checkAuth();
          
          // Se tinha referência de criador, redirecionar para o perfil
          if (creatorRef) {
            localStorage.removeItem('creator_referral');
            try {
              const creatorResponse = await axios.get(`${API}/c/${creatorRef}`, { timeout: 5000 });
              const creatorId = creatorResponse.data.creator.id;
              window.location.replace(`/creator/${creatorId}`);
              return;
            } catch (error) {
              console.error('Erro ao buscar criador:', error);
            }
          }
          
          // Redirecionar para feed
          window.location.replace('/feed');
          return;
        }
      } catch (error) {
        clearTimeout(forceRedirect);
        console.error('Session error:', error);
        setErrorMessage('Não foi possível completar o login. Tente novamente.');
        toast.error('Erro ao entrar. Por favor, tente novamente.');
        setProcessing(false);
        setTimeout(() => window.location.replace('/'), 2000);
      }
    };

    processSession();
    
    return () => clearTimeout(forceRedirect);
  }, []); // Dependências vazias para executar apenas uma vez

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
      <div className="text-center px-4">
        {processing ? (
          <>
            <div className="mb-6">
              <img 
                src="/lyber-logo.png" 
                alt="LYBERHOT" 
                className="h-16 mx-auto mb-4"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#C79F5E] border-t-transparent mx-auto mb-4"></div>
            <p className="text-2xl font-bold text-[#C79F5E] mb-2">Entrando no LYBERHOT...</p>
            <p className="text-gray-400">Aguarde enquanto preparamos tudo para você</p>
          </>
        ) : errorMessage ? (
          <>
            <div className="mb-6">
              <img 
                src="/lyber-logo.png" 
                alt="LYBERHOT" 
                className="h-16 mx-auto mb-4 opacity-50"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div className="text-[#C79F5E] text-6xl mb-4">⚠️</div>
            <p className="text-xl text-[#C79F5E] mb-2">{errorMessage}</p>
            <p className="text-sm text-gray-500">Redirecionando para a página inicial...</p>
          </>
        ) : (
          <>
            <div className="mb-6">
              <img 
                src="/lyber-logo.png" 
                alt="LYBERHOT" 
                className="h-16 mx-auto mb-4"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div className="text-[#C79F5E] text-6xl mb-4">✅</div>
            <p className="text-2xl font-bold text-[#C79F5E]">Bem-vindo ao LYBERHOT!</p>
            <p className="text-gray-400 mt-2">Carregando seu feed...</p>
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#C79F5E] border-t-transparent mx-auto mt-4"></div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== QUIZ DE PERFIL ====================
const ProfileQuiz = () => {
  const { user, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    gender: '',
    relationship_status: '',
    sexual_orientation: '',
    interests: [],
    looking_for: [],
    age: '',
    city: '',
    state: '',
    main_interest: '',
    // Redes sociais
    instagram_url: '',
    twitter_url: '',
    // Campos específicos para casal
    partner_info: {
      ele_idade: '',
      ele_orientacao: '',
      ela_idade: '',
      ela_orientacao: ''
    }
  });

  const totalSteps = 6;

  const genderOptions = [
    { id: 'casal', label: '💑 Casal', icon: '💑', desc: 'Casais podem criar e compartilhar conteúdo' },
    { id: 'mulher', label: '👩 Solteira', icon: '👩', desc: 'Mulheres podem criar e compartilhar conteúdo' },
    { id: 'homem', label: '👨 Homem', icon: '👨', desc: 'Visualize e interaja com criadores' },
    { id: 'outro', label: '🌈 Outro', icon: '🌈', desc: 'Crie e compartilhe conteúdo' }
  ];

  const relationshipOptions = [
    { id: 'solteiro', label: 'Solteiro(a)' },
    { id: 'casado', label: 'Casado(a)' },
    { id: 'casal', label: 'Casal' },
    { id: 'relacionamento_aberto', label: 'Relacionamento Aberto' },
    { id: 'poliamor', label: 'Poliamor' }
  ];

  const orientationOptions = [
    { id: 'heterossexual', label: 'Heterossexual' },
    { id: 'homossexual', label: 'Homossexual' },
    { id: 'bissexual', label: 'Bissexual' },
    { id: 'pansexual', label: 'Pansexual' },
    { id: 'outro', label: 'Outro / Prefiro não dizer' }
  ];

  const interestOptions = [
    { id: 'casais', label: '💑 Casais' },
    { id: 'mulheres', label: '👩 Mulheres' },
    { id: 'homens', label: '👨 Homens' },
    { id: 'swing', label: '🔥 Swing' },
    { id: 'fetiche', label: '⛓️ Fetiche' },
    { id: 'bdsm', label: '🖤 BDSM' },
    { id: 'voyeur', label: '👀 Voyeur' },
    { id: 'exibicionismo', label: '📸 Exibicionismo' },
    { id: 'amador', label: '📱 Amador' },
    { id: 'lgbtqia', label: '🏳️‍🌈 LGBTQIA+' }
  ];

  const lookingForOptions = [
    { id: 'explorar', label: '🔍 Explorar conteúdo', desc: 'Descobrir novos criadores e conteúdos' },
    { id: 'interagir', label: '💬 Interagir', desc: 'Conversar e se conectar com criadores' },
    { id: 'consumir', label: '🎬 Consumir conteúdo', desc: 'Assistir e curtir conteúdos exclusivos' },
    { id: 'criar', label: '📱 Criar conteúdo', desc: 'Compartilhar meu próprio conteúdo' }
  ];

  const brazilianStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  const toggleInterest = (id) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(id)
        ? prev.interests.filter(i => i !== id)
        : [...prev.interests, id]
    }));
  };

  const selectMainInterest = (id) => {
    setFormData(prev => ({
      ...prev,
      main_interest: id,
      looking_for: [id]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/users/quiz`,
        {
          ...formData,
          age: formData.age ? parseInt(formData.age) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newRole = response.data.role;
      let message = 'Perfil configurado com sucesso! 🎉';
      if (newRole === 'free') {
        message = 'Perfil configurado! Você pode criar e compartilhar conteúdo 🎉';
      } else if (newRole === 'viewer') {
        message = 'Perfil configurado! Explore os melhores conteúdos 🎉';
      }
      
      toast.success(message);
      await checkAuth();
      
      // Se veio de link de criador, redirecionar para o perfil
      const creatorRef = localStorage.getItem('creator_referral');
      if (creatorRef) {
        localStorage.removeItem('creator_referral');
        try {
          const creatorResponse = await axios.get(`${API}/c/${creatorRef}`);
          const creatorId = creatorResponse.data.creator.id;
          navigate(`/creator/${creatorId}`, { replace: true });
          return;
        } catch (error) {
          console.error('Erro ao buscar criador:', error);
        }
      }
      
      navigate('/feed');
    } catch (error) {
      console.error('Quiz error:', error);
      toast.error('Erro ao salvar perfil');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.gender !== '';
      case 2: return formData.age && parseInt(formData.age) >= 18 && formData.city && formData.state;
      case 3: 
        // Para casal, verificar se informações de ambos estão preenchidas
        if (formData.gender === 'casal') {
          return (
            formData.partner_info.ele_idade && 
            parseInt(formData.partner_info.ele_idade) >= 18 &&
            formData.partner_info.ele_orientacao &&
            formData.partner_info.ela_idade && 
            parseInt(formData.partner_info.ela_idade) >= 18 &&
            formData.partner_info.ela_orientacao
          );
        }
        return formData.relationship_status !== '' && formData.sexual_orientation !== '';
      case 4: return formData.interests.length > 0;
      case 5: return formData.main_interest !== '';
      case 6: return true; // Redes sociais são opcionais
      default: return false;
    }
  };

  const skipQuiz = () => {
    navigate('/feed');
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-[#C79F5E]/20">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/lyber-logo.png" alt="LYBERHOT" className="h-10" />
            <span className="text-[#C79F5E] font-bold">Complete seu Perfil</span>
          </div>
          <button
            onClick={skipQuiz}
            className="text-gray-500 hover:text-gray-300 text-sm"
          >
            Pular por agora
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="max-w-2xl mx-auto w-full px-4 py-4">
        <div className="flex items-center gap-2 mb-2">
          {[1, 2, 3, 4, 5, 6].map((s) => (
            <div
              key={s}
              className={`flex-1 h-2 rounded-full transition-all ${
                s <= step ? 'bg-[#C79F5E]' : 'bg-[#1A1A1A]'
              }`}
            />
          ))}
        </div>
        <p className="text-gray-500 text-sm text-center">Passo {step} de {totalSteps}</p>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-2xl mx-auto w-full px-4 py-6">
        {/* Step 1: Tipo de Perfil */}
        {step === 1 && (
          <div className="animate-fadeIn">
            <h2 className="text-2xl font-bold mb-2 text-center">Tipo de Perfil</h2>
            <p className="text-gray-400 text-center mb-8">Selecione a opção que melhor te representa</p>
            
            <div className="grid grid-cols-2 gap-4">
              {genderOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setFormData(prev => ({ ...prev, gender: option.id }))}
                  className={`p-6 rounded-2xl border-2 transition-all ${
                    formData.gender === option.id
                      ? 'bg-[#C79F5E]/20 border-[#C79F5E] text-white'
                      : 'bg-[#1A1A1A] border-[#1A1A1A] hover:border-[#C79F5E]/50'
                  }`}
                >
                  <div className="text-4xl mb-2">{option.icon}</div>
                  <div className="font-semibold">{option.label.split(' ')[1]}</div>
                  <div className="text-xs text-gray-400 mt-1">{option.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Idade, Cidade e Região */}
        {step === 2 && (
          <div className="animate-fadeIn">
            <h2 className="text-2xl font-bold mb-2 text-center">Idade e Localização</h2>
            <p className="text-gray-400 text-center mb-6">Confirme que você é maior de 18 anos</p>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Idade (+18 obrigatório)</label>
                <input
                  type="number"
                  min="18"
                  max="99"
                  value={formData.age}
                  onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                  className="w-full bg-[#1A1A1A] border border-[#C79F5E]/30 rounded-xl px-4 py-3 text-white focus:border-[#C79F5E] outline-none"
                  placeholder="Sua idade"
                />
                {formData.age && parseInt(formData.age) < 18 && (
                  <p className="text-red-500 text-sm mt-1">⚠️ Você precisa ter 18 anos ou mais</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Cidade</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full bg-[#1A1A1A] border border-[#C79F5E]/30 rounded-xl px-4 py-3 text-white focus:border-[#C79F5E] outline-none"
                  placeholder="Sua cidade"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Estado</label>
                <select
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                  className="w-full bg-[#1A1A1A] border border-[#C79F5E]/30 rounded-xl px-4 py-3 text-white focus:border-[#C79F5E] outline-none"
                >
                  <option value="">Selecione seu estado</option>
                  {brazilianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Status e Orientação */}
        {step === 3 && (
          <div className="animate-fadeIn">
            <h2 className="text-2xl font-bold mb-2 text-center">
              {formData.gender === 'casal' ? 'Sobre Vocês' : 'Sobre Você'}
            </h2>
            <p className="text-gray-400 text-center mb-6">
              {formData.gender === 'casal' ? 'Conte um pouco sobre o casal' : 'Conte um pouco mais sobre você'}
            </p>
            
            <div className="space-y-6">
              {/* Campos específicos para CASAL */}
              {formData.gender === 'casal' ? (
                <>
                  {/* Informações DELE */}
                  <div className="bg-[#1A1A1A] p-4 rounded-xl border border-[#C79F5E]/20">
                    <h3 className="text-lg font-semibold text-[#C79F5E] mb-4 flex items-center gap-2">
                      <span>👨</span> Informações Dele
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Idade</label>
                        <input
                          type="number"
                          min="18"
                          max="99"
                          placeholder="Idade dele"
                          value={formData.partner_info.ele_idade}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            partner_info: { ...prev.partner_info, ele_idade: e.target.value }
                          }))}
                          className="w-full p-3 rounded-lg bg-[#0D0D0D] border border-[#C79F5E]/30 focus:border-[#C79F5E] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Orientação Sexual</label>
                        <div className="flex flex-wrap gap-2">
                          {orientationOptions.map((option) => (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                partner_info: { ...prev.partner_info, ele_orientacao: option.id }
                              }))}
                              className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                                formData.partner_info.ele_orientacao === option.id
                                  ? 'bg-[#C79F5E] border-[#C79F5E] text-black font-semibold'
                                  : 'bg-[#0D0D0D] border-[#C79F5E]/30 hover:border-[#C79F5E]'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Informações DELA */}
                  <div className="bg-[#1A1A1A] p-4 rounded-xl border border-pink-500/20">
                    <h3 className="text-lg font-semibold text-pink-400 mb-4 flex items-center gap-2">
                      <span>👩</span> Informações Dela
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Idade</label>
                        <input
                          type="number"
                          min="18"
                          max="99"
                          placeholder="Idade dela"
                          value={formData.partner_info.ela_idade}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            partner_info: { ...prev.partner_info, ela_idade: e.target.value }
                          }))}
                          className="w-full p-3 rounded-lg bg-[#0D0D0D] border border-pink-500/30 focus:border-pink-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Orientação Sexual</label>
                        <div className="flex flex-wrap gap-2">
                          {orientationOptions.map((option) => (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                partner_info: { ...prev.partner_info, ela_orientacao: option.id }
                              }))}
                              className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                                formData.partner_info.ela_orientacao === option.id
                                  ? 'bg-pink-500 border-pink-500 text-white font-semibold'
                                  : 'bg-[#0D0D0D] border-pink-500/30 hover:border-pink-500'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Campos normais para não-casal */}
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Status</label>
                    <div className="flex flex-wrap gap-2">
                      {relationshipOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => setFormData(prev => ({ ...prev, relationship_status: option.id }))}
                          className={`px-4 py-2 rounded-full border transition-all ${
                            formData.relationship_status === option.id
                              ? 'bg-[#C79F5E] border-[#C79F5E] text-black font-semibold'
                              : 'bg-[#1A1A1A] border-[#C79F5E]/30 hover:border-[#C79F5E]'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Orientação Sexual</label>
                    <div className="flex flex-wrap gap-2">
                      {orientationOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => setFormData(prev => ({ ...prev, sexual_orientation: option.id }))}
                          className={`px-4 py-2 rounded-full border transition-all ${
                            formData.sexual_orientation === option.id
                              ? 'bg-[#C79F5E] border-[#C79F5E] text-black font-semibold'
                              : 'bg-[#1A1A1A] border-[#C79F5E]/30 hover:border-[#C79F5E]'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Preferências de Conteúdo (Tags) */}
        {step === 4 && (
          <div className="animate-fadeIn">
            <h2 className="text-2xl font-bold mb-2 text-center">Preferências de Conteúdo</h2>
            <p className="text-gray-400 text-center mb-6">Selecione todos que te interessam</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {interestOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => toggleInterest(option.id)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.interests.includes(option.id)
                      ? 'bg-[#C79F5E]/20 border-[#C79F5E] text-white'
                      : 'bg-[#1A1A1A] border-[#1A1A1A] hover:border-[#C79F5E]/50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            
            <p className="text-center text-gray-500 text-sm mt-4">
              {formData.interests.length} selecionado(s)
            </p>
          </div>
        )}

        {/* Step 5: Interesse Principal */}
        {step === 5 && (
          <div className="animate-fadeIn">
            <h2 className="text-2xl font-bold mb-2 text-center">Qual seu interesse principal?</h2>
            <p className="text-gray-400 text-center mb-6">Isso nos ajuda a personalizar seu feed</p>
            
            <div className="grid grid-cols-1 gap-4">
              {lookingForOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => selectMainInterest(option.id)}
                  className={`p-6 rounded-2xl border-2 text-left transition-all ${
                    formData.main_interest === option.id
                      ? 'bg-[#C79F5E]/20 border-[#C79F5E] text-white'
                      : 'bg-[#1A1A1A] border-[#1A1A1A] hover:border-[#C79F5E]/50'
                  }`}
                >
                  <div className="text-xl font-semibold mb-1">{option.label}</div>
                  <div className="text-sm text-gray-400">{option.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 6: Redes Sociais (Opcional) */}
        {step === 6 && (
          <div className="animate-fadeIn">
            <h2 className="text-2xl font-bold mb-2 text-center">Suas Redes Sociais</h2>
            <p className="text-gray-400 text-center mb-6">Opcional - ajuda seguidores a te encontrarem</p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Formulário */}
              <div className="space-y-6">
                {/* Instagram */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5 text-pink-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    Instagram
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">@</span>
                    <input
                      type="text"
                      value={formData.instagram_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, instagram_url: e.target.value.replace('@', '') }))}
                      className="w-full bg-[#1A1A1A] border border-[#C79F5E]/30 rounded-xl pl-10 pr-4 py-3 text-white focus:border-[#C79F5E] outline-none"
                      placeholder="seu_usuario"
                    />
                  </div>
                </div>

                {/* X/Twitter */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    X (Twitter)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-gray-500">@</span>
                    <input
                      type="text"
                      value={formData.twitter_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, twitter_url: e.target.value.replace('@', '') }))}
                      className="w-full bg-[#1A1A1A] border border-[#C79F5E]/30 rounded-xl pl-10 pr-4 py-3 text-white focus:border-[#C79F5E] outline-none"
                      placeholder="seu_usuario"
                    />
                  </div>
                </div>

                <div className="bg-[#C79F5E]/10 border border-[#C79F5E]/20 rounded-xl p-4">
                  <p className="text-sm text-gray-300">
                    💡 <strong>Dica:</strong> Adicionar redes sociais aumenta sua visibilidade e ajuda seguidores a te encontrarem em outras plataformas.
                  </p>
                </div>
              </div>

              {/* Preview do Perfil */}
              <div className="hidden lg:block">
                <div className="bg-[#0D0D0D] rounded-xl border border-[#C79F5E]/20 overflow-hidden">
                  <div className="p-4 border-b border-[#C79F5E]/10">
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Preview do Perfil</p>
                  </div>
                  
                  {/* Mini Profile Card */}
                  <div className="p-4">
                    {/* Cover */}
                    <div className="h-20 rounded-lg bg-gradient-to-r from-[#4D1A4A]/40 to-[#C79F5E]/20 mb-4" />
                    
                    {/* Avatar + Name */}
                    <div className="flex items-start gap-3 -mt-8 px-2">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] flex items-center justify-center text-white text-xl font-bold border-4 border-[#0D0D0D]">
                        {user?.name?.charAt(0) || '?'}
                      </div>
                      <div className="pt-4">
                        <h3 className="font-semibold text-white text-sm">{user?.name || 'Seu Nome'}</h3>
                        <p className="text-xs text-gray-500">@{user?.name?.toLowerCase().replace(/\s/g, '_') || 'seu_usuario'}</p>
                      </div>
                    </div>

                    {/* Social Links Preview */}
                    <div className="mt-4 pt-3 border-t border-white/10">
                      <p className="text-xs text-gray-500 mb-2">Suas redes aparecerão assim:</p>
                      
                      {(formData.instagram_url || formData.twitter_url) ? (
                        <div className="flex flex-wrap items-center gap-2">
                          {formData.instagram_url && (
                            <div className="flex items-center gap-1.5 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-pink-500/30 px-2.5 py-1.5 rounded-full">
                              <svg className="w-3.5 h-3.5 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                              </svg>
                              <span className="text-xs text-pink-300 font-medium">@{formData.instagram_url}</span>
                            </div>
                          )}
                          {formData.twitter_url && (
                            <div className="flex items-center gap-1.5 bg-white/5 border border-white/20 px-2.5 py-1.5 rounded-full">
                              <svg className="w-3.5 h-3.5 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                              </svg>
                              <span className="text-xs text-gray-300 font-medium">@{formData.twitter_url}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-gray-500">
                          <div className="w-8 h-8 rounded-full bg-white/5 border border-dashed border-white/20 flex items-center justify-center">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                          <span className="text-xs">Digite suas redes para ver o preview</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Preview */}
            {(formData.instagram_url || formData.twitter_url) && (
              <div className="lg:hidden mt-6 bg-[#0D0D0D] rounded-xl border border-[#C79F5E]/20 p-4">
                <p className="text-xs text-gray-500 mb-3">📱 Preview das suas redes:</p>
                <div className="flex flex-wrap items-center gap-2">
                  {formData.instagram_url && (
                    <div className="flex items-center gap-1.5 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-pink-500/30 px-2.5 py-1.5 rounded-full">
                      <svg className="w-3.5 h-3.5 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      <span className="text-xs text-pink-300 font-medium">@{formData.instagram_url}</span>
                    </div>
                  )}
                  {formData.twitter_url && (
                    <div className="flex items-center gap-1.5 bg-white/5 border border-white/20 px-2.5 py-1.5 rounded-full">
                      <svg className="w-3.5 h-3.5 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      <span className="text-xs text-gray-300 font-medium">@{formData.twitter_url}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-[#C79F5E]/20">
        <div className="max-w-2xl mx-auto flex gap-3">
          {step > 1 && (
            <button
              onClick={() => setStep(prev => prev - 1)}
              className="flex-1 py-3 rounded-xl border border-[#C79F5E]/30 text-white font-semibold hover:bg-[#1A1A1A] transition-all"
            >
              Voltar
            </button>
          )}
          
          {step < totalSteps ? (
            <button
              onClick={() => setStep(prev => prev + 1)}
              disabled={!canProceed()}
              className={`flex-1 py-3 rounded-xl font-semibold transition-all ${
                canProceed()
                  ? 'bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black hover:shadow-[0_0_20px_rgba(199,159,94,0.4)]'
                  : 'bg-[#1A1A1A] text-gray-500 cursor-not-allowed'
              }`}
            >
              Continuar
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canProceed() || loading}
              className={`flex-1 py-3 rounded-xl font-semibold transition-all ${
                canProceed() && !loading
                  ? 'bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black hover:shadow-[0_0_20px_rgba(199,159,94,0.4)]'
                  : 'bg-[#1A1A1A] text-gray-500 cursor-not-allowed'
              }`}
            >
              {loading ? 'Salvando...' : '✨ Finalizar Perfil'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Story Creator Modal (Instagram Style)
const StoryCreatorModal = ({ show, onClose, onPublish }) => {
  const [step, setStep] = useState('upload'); // 'upload', 'edit', 'preview', 'camera', 'recording'
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [text, setText] = useState('');
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [location, setLocation] = useState('');
  const [showLocationSearch, setShowLocationSearch] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraVideoRef = useRef(null);

  const filters = [
    { name: 'none', label: 'Original', filter: 'none' },
    { name: 'grayscale', label: 'P&B', filter: 'grayscale(100%)' },
    { name: 'sepia', label: 'Sepia', filter: 'sepia(100%)' },
    { name: 'warm', label: 'Quente', filter: 'sepia(50%) saturate(1.2)' },
    { name: 'cold', label: 'Frio', filter: 'hue-rotate(180deg) saturate(1.2)' },
    { name: 'vintage', label: 'Vintage', filter: 'sepia(30%) contrast(1.1)' },
    { name: 'bright', label: 'Brilho', filter: 'brightness(1.2) contrast(1.1)' },
  ];

  const suggestedLocations = [
    'São Paulo, SP',
    'Rio de Janeiro, RJ',
    'Belo Horizonte, MG',
    'Brasília, DF',
    'Salvador, BA',
  ];

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast.error('Apenas imagens ou vídeos são permitidos');
      return;
    }

    // Validar duração do vídeo (1 minuto)
    if (file.type.startsWith('video/')) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        if (video.duration > 60) {
          toast.error('Vídeo não pode ter mais de 1 minuto');
          return;
        }
        setMediaFile(file);
        setMediaType('video');
        setMediaPreview(URL.createObjectURL(file));
        setStep('edit');
      };
      video.src = URL.createObjectURL(file);
    } else {
      setMediaFile(file);
      setMediaType('image');
      setMediaPreview(URL.createObjectURL(file));
      setStep('edit');
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: { ideal: 1080 }, height: { ideal: 1920 } },
        audio: true 
      });
      setCameraStream(stream);
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
      }
      setStep('camera');
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Erro ao acessar câmera. Verifique as permissões.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  };

  const capturePhoto = () => {
    if (!cameraVideoRef.current || !canvasRef.current) return;

    const video = cameraVideoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
      setMediaFile(file);
      setMediaType('image');
      setMediaPreview(URL.createObjectURL(file));
      stopCamera();
      setStep('edit');
      toast.success('Foto capturada!');
    }, 'image/jpeg', 0.9);
  };

  const startRecording = () => {
    if (!cameraStream) return;

    const recorder = new MediaRecorder(cameraStream, {
      mimeType: 'video/webm;codecs=vp9',
      videoBitsPerSecond: 2500000
    });

    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      const file = new File([blob], 'camera-video.webm', { type: 'video/webm' });
      setMediaFile(file);
      setMediaType('video');
      setMediaPreview(URL.createObjectURL(file));
      stopCamera();
      setStep('edit');
      setIsRecording(false);
      setRecordingTime(0);
      toast.success('Vídeo gravado!');
    };

    recorder.start();
    setMediaRecorder(recorder);
    setIsRecording(true);
    setRecordedChunks([]);
    
    // Timer de gravação (máx 60s)
    let time = 0;
    const timer = setInterval(() => {
      time++;
      setRecordingTime(time);
      if (time >= 60) {
        stopRecording();
        clearInterval(timer);
      }
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
  };

  const handlePublish = async () => {
    if (!mediaFile) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', mediaFile);
      
      // Adicionar metadados opcionais
      if (text) formData.append('text', text);
      if (location) formData.append('location', location);
      if (selectedFilter !== 'none') formData.append('filter', selectedFilter);

      const response = await axios.post(`${API}/stories/create`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Story publicado!');
      if (onPublish) onPublish();
      handleClose();
    } catch (error) {
      toast.error('Erro ao publicar story');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    stopCamera();
    if (isRecording) {
      stopRecording();
    }
    setStep('upload');
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    setSelectedFilter('none');
    setText('');
    setLocation('');
    setIsRecording(false);
    setRecordingTime(0);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent">
        <button
          onClick={handleClose}
          className="text-white hover:text-gray-300 transition-colors"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h2 className="text-white font-bold text-lg">Criar Story</h2>
        {step === 'edit' && (
          <button
            onClick={handlePublish}
            disabled={uploading}
            className="bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] text-white px-6 py-2 rounded-full font-semibold disabled:opacity-50"
          >
            {uploading ? 'Publicando...' : 'Publicar'}
          </button>
        )}
        {step === 'upload' && <div className="w-8" />}
      </div>

      {/* Content */}
      <div className="w-full h-full flex items-center justify-center">
        {step === 'upload' && (
          <div className="max-w-md w-full px-4">
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-8 rounded-3xl">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">📸</div>
                <h3 className="text-2xl font-bold text-white mb-2">Criar Story</h3>
                <p className="text-gray-400">Compartilhe um momento</p>
              </div>

              {/* Upload Options */}
              <div className="space-y-3">
                {/* Foto */}
                <button
                  onClick={() => document.getElementById('story-photo-input').click()}
                  className="w-full bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] hover:from-[#6B2D5C] hover:to-[#C79F5E] text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-3 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Escolher Foto
                </button>
                <input
                  id="story-photo-input"
                  type="file"
                  accept="image/*,.heic,.heif"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {/* Vídeo */}
                <button
                  onClick={() => document.getElementById('story-video-input').click()}
                  className="w-full bg-white/10 hover:bg-white/20 border border-white/20 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-3 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Escolher Vídeo (até 1 min)
                </button>
                <input
                  id="story-video-input"
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {/* Câmera */}
                <button
                  onClick={startCamera}
                  className="w-full bg-white/10 hover:bg-white/20 border border-white/20 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-3 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Usar Câmera
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 'edit' && mediaPreview && (
          <div className="w-full max-w-md h-full flex flex-col">
            {/* Preview Area */}
            <div className="flex-1 relative flex items-center justify-center bg-black">
              {mediaType === 'video' ? (
                <video
                  ref={videoRef}
                  src={mediaPreview}
                  className="max-w-full max-h-full object-contain"
                  style={{ filter: filters.find(f => f.name === selectedFilter)?.filter }}
                  controls
                />
              ) : (
                <div className="relative">
                  <img
                    src={mediaPreview}
                    alt="Preview"
                    className="max-w-full max-h-[70vh] object-contain"
                    style={{ filter: filters.find(f => f.name === selectedFilter)?.filter }}
                  />
                  {/* Text Overlay */}
                  {text && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <p
                        className="text-4xl font-bold text-center px-4 py-2"
                        style={{
                          color: textColor,
                          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                        }}
                      >
                        {text}
                      </p>
                    </div>
                  )}
                  {/* Location Overlay */}
                  {location && (
                    <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-white text-sm font-semibold">{location}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Editing Tools */}
            <div className="bg-gradient-to-t from-black via-black to-transparent p-4 space-y-4">
              {/* Filters */}
              <div>
                <p className="text-white text-sm font-semibold mb-2">Filtros</p>
                <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
                  {filters.map((filter) => (
                    <button
                      key={filter.name}
                      onClick={() => setSelectedFilter(filter.name)}
                      className={`flex-shrink-0 px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                        selectedFilter === filter.name
                          ? 'bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] text-white'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Text Input */}
              <div>
                <input
                  type="text"
                  placeholder="Adicionar texto..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 text-white placeholder:text-gray-400 px-4 py-3 rounded-xl"
                />
              </div>

              {/* Location */}
              <div className="relative">
                <button
                  onClick={() => setShowLocationSearch(!showLocationSearch)}
                  className="w-full bg-white/10 border border-white/20 text-white px-4 py-3 rounded-xl flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  <span>{location || 'Adicionar localização'}</span>
                </button>
                
                {showLocationSearch && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#121212] border border-white/10 rounded-xl overflow-hidden">
                    {suggestedLocations.map((loc) => (
                      <button
                        key={loc}
                        onClick={() => {
                          setLocation(loc);
                          setShowLocationSearch(false);
                        }}
                        className="w-full text-left px-4 py-3 text-white hover:bg-white/10 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        {loc}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Camera View */}
        {step === 'camera' && (
          <div className="w-full h-full relative flex items-center justify-center bg-black">
            {/* Camera Preview */}
            <video
              ref={cameraVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            
            {/* Hidden Canvas for capturing */}
            <canvas ref={canvasRef} className="hidden" />
            
            {/* Recording Timer */}
            {isRecording && (
              <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-4 py-2 rounded-full flex items-center gap-2 animate-pulse">
                <div className="w-3 h-3 bg-white rounded-full" />
                <span className="font-semibold">{recordingTime}s / 60s</span>
              </div>
            )}
            
            {/* Camera Controls */}
            <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center gap-6">
              {/* Cancel Button */}
              <button
                onClick={() => {
                  stopCamera();
                  setStep('upload');
                }}
                className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Capture/Record Button */}
              <button
                onClick={isRecording ? stopRecording : (mediaType === 'video' ? startRecording : capturePhoto)}
                onMouseDown={(e) => {
                  // Long press para iniciar gravação de vídeo
                  const timer = setTimeout(() => {
                    setMediaType('video');
                    startRecording();
                  }, 500);
                  e.currentTarget.dataset.timer = timer;
                }}
                onMouseUp={(e) => {
                  const timer = e.currentTarget.dataset.timer;
                  if (timer && !isRecording) {
                    clearTimeout(timer);
                    setMediaType('image');
                    capturePhoto();
                  }
                }}
                onMouseLeave={(e) => {
                  const timer = e.currentTarget.dataset.timer;
                  if (timer) {
                    clearTimeout(timer);
                  }
                }}
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                  isRecording 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-white hover:scale-110'
                }`}
              >
                {isRecording ? (
                  <div className="w-8 h-8 bg-white rounded-sm" />
                ) : (
                  <div className="w-16 h-16 border-4 border-gray-800 rounded-full" />
                )}
              </button>

              {/* Switch Camera (futuro) */}
              <button
                className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all"
                onClick={async () => {
                  // Toggle entre câmera frontal e traseira
                  stopCamera();
                  try {
                    const stream = await navigator.mediaDevices.getUserMedia({ 
                      video: { facingMode: cameraVideoRef.current?.dataset?.facing === 'user' ? 'environment' : 'user' },
                      audio: true 
                    });
                    setCameraStream(stream);
                    if (cameraVideoRef.current) {
                      cameraVideoRef.current.srcObject = stream;
                      cameraVideoRef.current.dataset.facing = cameraVideoRef.current.dataset.facing === 'user' ? 'environment' : 'user';
                    }
                  } catch (error) {
                    console.error('Switch camera error:', error);
                    startCamera(); // Fallback para câmera padrão
                  }
                }}
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>

            {/* Hint Text */}
            {!isRecording && (
              <div className="absolute bottom-32 left-0 right-0 text-center">
                <p className="text-white text-sm bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full inline-block">
                  Toque para foto • Segure para vídeo
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Feed = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [followingPosts, setFollowingPosts] = useState([]);
  const [recommendedCreators, setRecommendedCreators] = useState([]);
  const [onlineCreators, setOnlineCreators] = useState([]);
  const [trendingCreators, setTrendingCreators] = useState([]);
  const [trendingFilter, setTrendingFilter] = useState('hot');
  const [allStories, setAllStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('for-you');
  const [showStoryCreator, setShowStoryCreator] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [purchasedPosts, setPurchasedPosts] = useState([]);
  const navigate = useNavigate();
  
  // Admin tem acesso grátis a todo conteúdo
  const isAdmin = user?.role === 'admin';

  // Carregar posts comprados pelo usuário
  const loadPurchasedPosts = async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      // Buscar posts comprados de todos os criadores
      const response = await axios.get(`${API}/posts/purchased/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchasedPosts(response.data.post_ids || []);
    } catch (error) {
      console.error('Load purchased posts error:', error);
    }
  };

  // Helper function to get image URL
  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    if (image.startsWith('/uploads')) {
      const token = localStorage.getItem('token');
      const baseUrl = `${BACKEND_URL}/api${image}`;
      // Adicionar token para posts premium
      if (image.includes('/posts/') && token) {
        return `${baseUrl}?token=${token}`;
      }
      return baseUrl;
    }
    if (image.startsWith('data:')) return image;
    return `data:image/jpeg;base64,${image}`;
  };

  useEffect(() => {
    loadFeed();
    loadRecommendedCreators();
    loadOnlineCreators();
    loadTrendingCreators('hot');
    if (user) {
      loadFollowingFeed();
      loadPurchasedPosts();
    }
    loadAllStories();
  }, [user]);

  // Auto-refresh quando app volta ao foco (importante para mobile/PWA)
  useEffect(() => {
    const handleAppVisible = () => {
      console.log('[Feed] App visível - recarregando dados...');
      loadFeed();
      loadRecommendedCreators();
      loadTrendingCreators(trendingFilter);
      loadOnlineCreators();
      if (user) {
        loadFollowingFeed();
      }
      loadAllStories();
    };

    const handleAppOnline = () => {
      console.log('[Feed] Conexão restaurada - recarregando dados...');
      handleAppVisible();
    };

    // Listeners customizados do index.js
    window.addEventListener('app-visible', handleAppVisible);
    window.addEventListener('app-online', handleAppOnline);
    
    // Listener nativo de visibilidade
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleAppVisible();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('app-visible', handleAppVisible);
      window.removeEventListener('app-online', handleAppOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, trendingFilter]);

  const loadTrendingCreators = async (sortBy) => {
    try {
      // Adicionar timestamp para evitar cache e forçar dados frescos
      const timestamp = Date.now();
      const response = await axios.get(`${API}/creators/trending?sort_by=${sortBy}&limit=10&_t=${timestamp}`);
      setTrendingCreators(response.data);
    } catch (error) {
      console.error('Trending creators error:', error);
    }
  };

  const handleTrendingFilterChange = (filter) => {
    setTrendingFilter(filter);
    loadTrendingCreators(filter);
  };

  const loadOnlineCreators = async () => {
    try {
      const response = await axios.get(`${API}/creators/online?limit=15`);
      setOnlineCreators(response.data);
    } catch (error) {
      console.error('Online creators error:', error);
    }
  };

  const loadAllStories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stories/active/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllStories(response.data);
    } catch (error) {
      console.error('Stories error:', error);
    }
  };

  const loadFeed = async () => {
    try {
      // Adicionar timestamp para evitar cache e garantir posts mais recentes
      const timestamp = Date.now();
      const response = await axios.get(`${API}/posts/feed?_t=${timestamp}`);
      // Garantir ordenação por data mais recente
      const sortedPosts = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setPosts(sortedPosts);
    } catch (error) {
      console.error('Feed error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFollowingFeed = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/posts/following`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setFollowingPosts(response.data);
    } catch (error) {
      console.error('Following feed error:', error);
      setFollowingPosts([]);
    }
  };

  const loadRecommendedCreators = async () => {
    try {
      // Usar endpoint trending para recomendações dinâmicas baseadas em ranking
      // Adicionar timestamp para evitar cache
      const timestamp = Date.now();
      const response = await axios.get(`${API}/creators/trending?sort_by=hot&limit=10&_t=${timestamp}`);
      // Embaralhar levemente para variar a cada atualização
      const creators = response.data;
      const shuffled = creators.sort(() => Math.random() - 0.5).slice(0, 5);
      setRecommendedCreators(shuffled);
    } catch (error) {
      console.error('Creators error:', error);
      // Fallback para endpoint básico
      try {
        const timestamp = Date.now();
        const fallbackResponse = await axios.get(`${API}/creators?limit=5&sort_online_first=true&_t=${timestamp}`);
        setRecommendedCreators(fallbackResponse.data);
      } catch (e) {
        console.error('Fallback creators error:', e);
      }
    }
  };

  const handleLike = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Post curtido!');
      loadFeed();
      if (user) loadFollowingFeed();
    } catch (error) {
      toast.error('Erro ao curtir post');
    }
  };

  const goToCreator = (creatorId) => {
    navigate(`/creator/${creatorId}`);
  };

  const handleFollow = async (creatorId, creatorName) => {
    if (!user) {
      toast.error('Faça login para seguir criadores');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/follow/${creatorId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success(`Seguindo ${creatorName}!`);
      loadFollowingFeed(); // Recarregar feed de seguindo
    } catch (error) {
      toast.error('Erro ao seguir criador');
    }
  };

  const currentPosts = activeTab === 'following' ? followingPosts : posts;

  return (
    <div data-testid="feed-page" className="min-h-screen bg-[#0D0D0D] text-white pb-20 md:pb-0">
      <Navbar NotificationsPanel={NotificationsPanel} />
      
      {/* Criadores Online + Stories - Linha Única */}
      {(onlineCreators.length > 0 || allStories.length > 0) && (
        <div className="border-b border-white/5 py-4">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                Criadores Online e Stories
              </h3>
              <button
                onClick={() => navigate('/creators?online=true')}
                className="text-sm text-[#C79F5E] hover:text-[#6B2D5C] font-semibold transition-colors flex items-center gap-1"
              >
                Ver todos
                <span>→</span>
              </button>
            </div>
            
            {/* Scroll Horizontal Unificado */}
            <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {(() => {
                // Criar lista unificada de criadores (online + com stories)
                const creatorsMap = new Map();
                
                // Adicionar criadores online
                onlineCreators.forEach(creator => {
                  creatorsMap.set(creator.id, {
                    ...creator,
                    isOnline: true,
                    hasStory: false,
                    storyData: null
                  });
                });
                
                // Adicionar/atualizar criadores com stories
                allStories.forEach(item => {
                  const existing = creatorsMap.get(item.creator.id);
                  if (existing) {
                    existing.hasStory = true;
                    existing.storyData = item;
                  } else {
                    creatorsMap.set(item.creator.id, {
                      id: item.creator.id,
                      name: item.creator.name,
                      picture: item.creator.picture,
                      isOnline: false,
                      hasStory: true,
                      storyData: item
                    });
                  }
                });
                
                // Converter para array e ordenar (online primeiro, depois com story)
                const unifiedList = Array.from(creatorsMap.values()).sort((a, b) => {
                  if (a.isOnline && !b.isOnline) return -1;
                  if (!a.isOnline && b.isOnline) return 1;
                  return 0;
                });
                
                return unifiedList.map((creator) => {
                  const getAvatarUrl = (picture) => {
                    if (!picture) return null;
                    if (picture.startsWith('data:image')) return picture;
                    if (picture.startsWith('http')) return picture;
                    if (picture.startsWith('/uploads')) return `${BACKEND_URL}/api${picture}`;
                    return `data:image/jpeg;base64,${picture}`;
                  };
                  const avatarUrl = getAvatarUrl(creator.picture);
                  
                  const handleClick = async () => {
                    if (creator.hasStory && creator.storyData) {
                      // Abrir story
                      try {
                        const token = localStorage.getItem('token');
                        const response = await axios.get(`${API}/stories/${creator.id}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        const event = new CustomEvent('openStory', { 
                          detail: { 
                            creator: creator.storyData.creator, 
                            stories: response.data 
                          } 
                        });
                        window.dispatchEvent(event);
                      } catch (error) {
                        console.error('Load stories error:', error);
                      }
                    } else {
                      // Ir para perfil
                      navigate(`/creator/${creator.id}`);
                    }
                  };
                  
                  return (
                    <div
                      key={creator.id}
                      onClick={handleClick}
                      className="flex-shrink-0 cursor-pointer group text-center"
                    >
                      <div className="relative w-16 h-16 sm:w-18 sm:h-18 mx-auto mb-1.5">
                        {/* Borda: Gradiente colorido se tem story, Verde se só online, Cinza se nenhum */}
                        <div className={`absolute inset-0 rounded-full p-[2px] sm:p-[3px] ${
                          creator.hasStory 
                            ? 'bg-gradient-to-tr from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E]'
                            : creator.isOnline 
                              ? 'bg-gradient-to-r from-green-400 to-green-600'
                              : 'bg-gray-600'
                        }`}>
                          <div className="w-full h-full rounded-full bg-[#1A1A1A] p-[2px]">
                            <div className="w-full h-full rounded-full overflow-hidden">
                              {avatarUrl ? (
                                <img
                                  src={avatarUrl}
                                  alt={creator.name}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-lg sm:text-xl font-bold bg-gradient-to-br from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E]">
                                  {creator.name?.charAt(0) || '?'}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Bolinha verde APENAS se online */}
                        {creator.isOnline && (
                          <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-[#1A1A1A] rounded-full">
                            <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-50"></span>
                          </div>
                        )}
                      </div>
                      {/* Nome do Criador */}
                      <p className="text-[10px] sm:text-xs text-gray-300 truncate max-w-[70px] mx-auto group-hover:text-white transition-colors">
                        {creator.name}
                      </p>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto py-4 sm:py-6 lg:py-8 px-3 sm:px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-12">
          
          {/* Coluna Esquerda - Apenas Desktop */}
          <div className="hidden lg:block lg:col-span-3">
            {/* Espaço para futuros widgets ou ads */}
          </div>
          
          {/* Main Feed Column - Centro */}
          <div className="lg:col-span-6">
            {/* Feed Tabs - Removido Stories pois já está no topo */}
            <div className="flex mb-4 sm:mb-6 bg-[#1A1A1A] border border-[#C79F5E]/20 rounded-xl p-1">
              <button
                onClick={() => setActiveTab('for-you')}
                className={`flex-1 py-2 sm:py-3 px-3 sm:px-6 rounded-lg font-semibold text-sm sm:text-base transition-all ${
                  activeTab === 'for-you'
                    ? 'bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black'
                    : 'text-gray-400 hover:text-[#C79F5E]'
                }`}
              >
                🔥 Para você
              </button>
              <button
                onClick={() => setActiveTab('following')}
                className={`flex-1 py-2 sm:py-3 px-3 sm:px-6 rounded-lg font-semibold text-sm sm:text-base transition-all ${
                  activeTab === 'following'
                    ? 'bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black'
                    : 'text-gray-400 hover:text-[#C79F5E]'
                }`}
              >
                ⭐ Seguindo
              </button>
              {/* Botão de Atualizar */}
              <button
                onClick={() => {
                  loadFeed();
                  loadRecommendedCreators();
                  loadTrendingCreators(trendingFilter);
                  if (user) loadFollowingFeed();
                  loadAllStories();
                  toast.success('Feed atualizado!');
                }}
                className="p-2 sm:p-3 rounded-lg text-gray-400 hover:text-[#C79F5E] hover:bg-[#C79F5E]/10 transition-all"
                title="Atualizar feed"
              >
                🔄
              </button>
            </div>

            {loading ? (
              <div className="text-center text-gray-400 py-12">
                <div className="text-4xl mb-4">⏳</div>
                <p>Carregando feed...</p>
              </div>
            ) : currentPosts.length === 0 ? (
              <div className="text-center text-gray-400 mt-12">
                <div className="text-6xl mb-4">
                  {activeTab === 'following' ? '👥' : '📭'}
                </div>
                <p className="text-2xl font-bold mb-2">
                  {activeTab === 'following' ? 'Nenhum seguindo' : 'Feed vazio'}
                </p>
                <p className="text-gray-500">
                  {activeTab === 'following' 
                    ? 'Siga criadores para ver conteúdo aqui'
                    : 'Siga criadores para ver conteúdo'
                  }
                </p>
                {activeTab === 'following' && (
                  <button
                    onClick={() => setActiveTab('for-you')}
                    className="mt-4 bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base"
                  >
                    Descobrir criadores
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {currentPosts.map((post, index) => (
                  <React.Fragment key={post.id}>
                    {/* Seção Em Alta - Aparece após o 2º post */}
                    {index === 2 && trendingCreators.length > 0 && (
                      <div className="bg-gradient-to-r from-[#1A1A1A] to-[#252525] border border-[#C79F5E]/20 rounded-xl p-4">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">🔥</span>
                            <h3 className="text-base font-bold text-white">Em alta</h3>
                          </div>
                          <button 
                            onClick={() => navigate('/creators?tab=trending')}
                            className="text-[#C79F5E] text-sm font-semibold hover:underline flex items-center gap-1"
                          >
                            Ver mais <span>›</span>
                          </button>
                        </div>
                        
                        {/* Scroll Horizontal de Criadores - Apenas fotos */}
                        <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                          {trendingCreators.slice(0, 8).map((creator) => (
                            <div
                              key={creator.id}
                              onClick={() => goToCreator(creator.id)}
                              className="flex-shrink-0 w-24 sm:w-28 cursor-pointer group"
                            >
                              {/* Foto do Criador */}
                              <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-gradient-to-br from-[#4D1A4A]/40 to-[#C79F5E]/40">
                                {creator.picture ? (
                                  <img
                                    src={getImageUrl(creator.picture)}
                                    alt={creator.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-3xl text-white/50">
                                    {creator.name?.charAt(0)}
                                  </div>
                                )}
                                
                                {/* Badge Online */}
                                {creator.is_online && (
                                  <div className="absolute top-1.5 right-1.5 bg-green-500 w-2.5 h-2.5 rounded-full border border-black">
                                    <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-50"></span>
                                  </div>
                                )}
                                
                                {/* Gradiente inferior */}
                                <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/80 to-transparent"></div>
                                
                                {/* Nome */}
                                <div className="absolute bottom-1.5 left-1.5 right-1.5">
                                  <p className="text-white font-medium text-xs truncate">{creator.name}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Post */}
                    <div data-testid={`post-${post.id}`} className="bg-[#1A1A1A] border border-[#C79F5E]/10 rounded-xl overflow-hidden">
                      {/* Post Header */}
                      <div className="p-3 sm:p-4 flex items-center justify-between">
                        <div 
                          className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-80"
                          onClick={() => goToCreator(post.creator_id)}
                        >
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E]">
                            {post.creator?.picture ? (
                              <img 
                                src={getImageUrl(post.creator.picture)}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white font-bold text-sm sm:text-base">
                                {post.creator?.name?.charAt(0) || '?'}
                              </div>
                            )}
                          </div>
                        <div>
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <p className="font-semibold text-white text-sm sm:text-base">{post.creator?.name || 'Criador'}</p>
                            {post.creator?.verified && (
                              <span className="text-[#6B2D5C] text-xs sm:text-sm">✓</span>
                            )}
                          </div>
                          <p className="text-[10px] sm:text-xs text-gray-400">
                            {new Date(post.created_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {user && activeTab === 'for-you' && (
                          <button
                            onClick={() => handleFollow(post.creator_id, post.creator?.name)}
                            className="bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] hover:shadow-[0_0_15px_rgba(199,159,94,0.4)] text-black px-4 py-2 rounded-full font-semibold text-xs transition-all active:scale-95 min-h-[36px]"
                          >
                            Seguir
                          </button>
                        )}
                        <button className="text-gray-400 hover:text-[#C79F5E] p-2 rounded-full hover:bg-white/5 active:scale-95 min-w-[40px] min-h-[40px] flex items-center justify-center">
                          <span className="text-xl">⋯</span>
                        </button>
                      </div>
                    </div>

                    {/* Descrição do Post - Antes da Mídia */}
                    {post.content && (
                      <div className="px-3 sm:px-4 pb-2">
                        <div className="text-xs sm:text-sm">
                          <span 
                            className="font-semibold text-white cursor-pointer hover:opacity-70"
                            onClick={() => goToCreator(post.creator_id)}
                          >
                            {post.creator?.name}
                          </span>
                          <span className="text-gray-300 ml-2">{post.content}</span>
                        </div>
                      </div>
                    )}

                    {/* Post Media */}
                    <div className="relative bg-black">
                      {post.media_urls && post.media_urls.length > 0 ? (
                        <MediaCarousel 
                          mediaUrls={post.media_urls} 
                          isPremium={post.is_premium}
                          price={post.price}
                          postId={post.id}
                          creatorId={post.creator_id}
                          isSubscribed={false}
                          isOwner={post.creator_id === user?.id}
                          isPurchased={purchasedPosts.includes(post.id)}
                        />
                      ) : post.gallery_images && post.gallery_images.length > 0 ? (
                        <div 
                          className="cursor-pointer"
                          onClick={() => goToCreator(post.creator_id)}
                        >
                          <div className="relative aspect-square">
                            <img
                              src={`data:image/jpeg;base64,${post.gallery_images[0]}`}
                              alt=""
                              className={`w-full h-full object-cover ${post.is_premium && !isAdmin ? 'blur-xl scale-110' : ''}`}
                            />
                            {post.is_premium && !isAdmin && (
                              <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                                <div className="text-6xl mb-4">🔒</div>
                                <p className="text-xl font-bold text-white mb-2">Conteúdo Premium</p>
                                <p className="text-[#C79F5E] font-bold text-2xl">R$ {post.price}</p>
                                <button 
                                  className="mt-4 bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] text-white px-6 py-2 rounded-full font-semibold hover:scale-105 transition-transform"
                                  data-testid={`unlock-premium-${post.id}`}
                                >
                                  Desbloquear
                                </button>
                              </div>
                            )}
                            {post.gallery_images.length > 1 && (
                              <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-semibold">
                                📷 {post.gallery_images.length}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : post.media_base64 && (
                        <div 
                          className="cursor-pointer"
                          onClick={() => goToCreator(post.creator_id)}
                        >
                          <div className="relative overflow-hidden">
                            <img
                              src={`data:image/jpeg;base64,${post.media_base64}`}
                              alt=""
                              className={`w-full ${post.is_premium && !isAdmin ? 'blur-xl scale-110' : ''}`}
                            />
                            {post.is_premium && !isAdmin && (
                              <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                                <div className="text-6xl mb-4">🔒</div>
                                <p className="text-xl font-bold text-white mb-2">Conteúdo Premium</p>
                                <p className="text-[#C79F5E] font-bold text-2xl">R$ {post.price}</p>
                                <button 
                                  className="mt-4 bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] text-white px-6 py-2 rounded-full font-semibold hover:scale-105 transition-transform"
                                  data-testid={`unlock-premium-base64-${post.id}`}
                                >
                                  Desbloquear
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Card de Criadores Marcados - Prévia do Perfil (após a mídia) */}
                    {post.mentioned_creators && post.mentioned_creators.length > 0 && (
                      <div className="px-3 sm:px-4 pb-2 space-y-2">
                        {post.mentioned_creators.map((mentioned) => (
                          <div 
                            key={mentioned.id}
                            onClick={() => goToCreator(mentioned.id)}
                            className="rounded-lg overflow-hidden border border-white/10 bg-[#0D0D0D] cursor-pointer hover:border-[#C79F5E]/30 transition-all group"
                          >
                            {/* Capa do Perfil - Compacta */}
                            <div className="relative h-16 sm:h-20">
                              {mentioned.cover_image ? (
                                <img 
                                  src={getImageUrl(mentioned.cover_image)}
                                  alt=""
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                />
                              ) : mentioned.picture ? (
                                <img 
                                  src={getImageUrl(mentioned.picture)}
                                  alt=""
                                  className="w-full h-full object-cover blur-sm opacity-50 group-hover:scale-105 transition-transform"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E]" />
                              )}
                              {/* Gradiente overlay */}
                              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-transparent" />
                              
                              {/* Avatar posicionado na capa */}
                              <div className="absolute bottom-0 left-3 translate-y-1/2">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-[#0D0D0D] bg-[#0D0D0D]">
                                  {mentioned.picture ? (
                                    <img 
                                      src={getImageUrl(mentioned.picture)}
                                      alt={mentioned.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] flex items-center justify-center text-white font-bold text-sm">
                                      {mentioned.name?.charAt(0)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Nome do Criador */}
                            <div className="pt-6 pb-3 px-3 flex items-center justify-between">
                              <div className="flex items-center gap-1">
                                <p className="font-semibold text-white text-sm truncate">{mentioned.name}</p>
                                {mentioned.verified && <span className="text-[#C79F5E] text-xs">✓</span>}
                              </div>
                              <span className="text-gray-500 text-xs group-hover:text-[#C79F5E] transition-colors">Ver perfil →</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Post Actions */}
                    <div className="p-3 sm:p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <button
                          data-testid={`like-post-${post.id}-btn`}
                          onClick={() => handleLike(post.id)}
                          className="p-2 rounded-full hover:bg-white/5 active:scale-90 transition-all min-w-[44px] min-h-[44px] flex items-center justify-center"
                        >
                          <span className="text-2xl">❤️</span>
                        </button>
                        <button className="p-2 rounded-full hover:bg-white/5 active:scale-90 transition-all min-w-[44px] min-h-[44px] flex items-center justify-center">
                          <span className="text-2xl">💬</span>
                        </button>
                        <button className="p-2 rounded-full hover:bg-white/5 active:scale-90 transition-all min-w-[44px] min-h-[44px] flex items-center justify-center">
                          <span className="text-2xl">🎁</span>
                        </button>
                        <button className="ml-auto p-2 rounded-full hover:bg-white/5 active:scale-90 transition-all min-w-[44px] min-h-[44px] flex items-center justify-center">
                          <span className="text-2xl">🔖</span>
                        </button>
                      </div>

                      <p className="font-semibold text-white mb-2 text-sm sm:text-base">{(post.likes || 0).toLocaleString('pt-BR')} curtidas</p>

                      {post.is_premium && (
                        <div className="mt-2 inline-flex items-center gap-1.5 sm:gap-2 bg-[#6B2D5C]/10 border border-[#4D1A4A]/30 text-[#6B2D5C] px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold">
                          🔒 Premium R$ {post.price}
                        </div>
                      )}

                      {/* Comments Section */}
                      <CommentsSection postId={post.id} />
                    </div>
                  </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar Direita - 20% - Recomendações */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/10 rounded-xl p-4 sticky top-4">
              <h3 className="text-base font-bold mb-4 flex items-center gap-2">
                ⭐ Recomendações
              </h3>
              
              <div className="space-y-3">
                {recommendedCreators.map((creator) => (
                  <div 
                    key={creator.id} 
                    onClick={() => goToCreator(creator.id)}
                    className="bg-[#1a1a1a] rounded-lg overflow-hidden border border-white/10 hover:border-[#4D1A4A]/50 transition-all cursor-pointer group"
                  >
                    {/* Cover Image */}
                    <div className="relative h-14 xl:h-16 bg-gradient-to-br from-[#4D1A4A]/40 to-[#4B0000]/40">
                      {creator.cover_image ? (
                        <img
                          src={getImageUrl(creator.cover_image)}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#4D1A4A]/50 to-[#4B0000]/50" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent" />
                      
                      {/* Avatar positioned over cover */}
                      <div className="absolute -bottom-6 left-3">
                        <div className="relative w-12 h-12 xl:w-14 xl:h-14 rounded-full overflow-hidden bg-gradient-to-br from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] border-2 border-[#1a1a1a] group-hover:scale-110 transition-transform flex items-center justify-center">
                          {creator.picture ? (
                            <img
                              src={getImageUrl(creator.picture)}
                              alt=""
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-white text-lg xl:text-xl font-bold">
                              {creator.name?.charAt(0) || '?'}
                            </span>
                          )}
                          {/* Online indicator */}
                          {creator.is_online && (
                            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-[#1a1a1a] rounded-full z-10"></div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Creator Info */}
                    <div className="p-3 pt-8">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 mb-0.5">
                            <p className="font-bold text-white text-xs xl:text-sm truncate">
                              {creator.name}
                            </p>
                            {creator.verified && (
                              <span className="text-[#6B2D5C] text-xs flex-shrink-0">✓</span>
                            )}
                          </div>
                          <p className="text-gray-400 text-[10px] xl:text-xs">@{creator.name.toLowerCase().replace(/\s+/g, '')}</p>
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-2 mb-2 text-[10px] xl:text-xs text-gray-400">
                        <span>👥 {creator.subscribers_count || 0}</span>
                      </div>
                      
                      {/* Follow Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFollow(creator.id, creator.name);
                        }}
                        className="w-full bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] hover:shadow-[0_0_15px_rgba(199,159,94,0.4)] text-black py-1.5 rounded-lg font-semibold text-xs transition-all"
                      >
                        Seguir
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Ver Mais */}
              <button
                onClick={() => navigate('/creators')}
                className="w-full mt-4 text-[#6B2D5C] hover:text-[#C79F5E] font-semibold text-xs xl:text-sm transition-colors"
              >
                Ver mais criadores →
              </button>

              {/* Top Esta Semana */}
              <div className="mt-5 pt-4 border-t border-white/10">
                <h4 className="font-semibold text-white text-xs xl:text-sm mb-3 flex items-center gap-2">
                  🔥 Top
                </h4>
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2 cursor-pointer hover:opacity-80" onClick={() => navigate('/creator/61f901de-041e-40e8-96a2-890625fae962')}>
                    <span className="text-[#6B2D5C] font-bold text-xs">1°</span>
                    <div className="w-8 h-8 rounded-md overflow-hidden bg-gray-800">
                      <div className="w-full h-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center text-xs">
                        CS
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-semibold truncate">Casalwesexy</p>
                      <p className="text-gray-400 text-[10px]">+2.1k fãs</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 font-bold text-xs">2°</span>
                    <div className="w-8 h-8 rounded-md overflow-hidden bg-gray-800">
                      <div className="w-full h-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-xs">
                        RS
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-semibold truncate">Raquel Sexy</p>
                      <p className="text-gray-400 text-[10px]">+1.8k fãs</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 font-bold text-xs">3°</span>
                    <div className="w-8 h-8 rounded-md overflow-hidden bg-gray-800">
                      <div className="w-full h-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-xs">
                        LC
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-semibold truncate">Lara do Corno</p>
                      <p className="text-gray-400 text-[10px]">+1.5k fãs</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Story Creator Modal */}
      <StoryCreatorModal
        show={showStoryCreator}
        onClose={() => setShowStoryCreator(false)}
        onPublish={() => {
          loadAllStories();
          setActiveTab('stories');
        }}
      />

      {/* Create Post Modal */}
      {showCreatePost && <CreatePostModal onClose={() => {
        setShowCreatePost(false);
        loadFeed();
        loadFollowingFeed();
      }} />}

      {/* Floating Action Button (FAB) - Botão dourado único para criar post */}
      {(user?.role === 'creator' || user?.role === 'admin' || user?.role === 'free' || user?.role === 'fan') && (
        <button
          onClick={() => setShowCreatePost(true)}
          className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] hover:shadow-[0_0_25px_rgba(199,159,94,0.5)]"
          data-testid="create-post-fab"
        >
          <svg className="w-7 h-7 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      )}
    </div>
  );
};

const Creators = () => {
  const [creators, setCreators] = useState([]);
  const [showOnlineOnly, setShowOnlineOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [showNearby, setShowNearby] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Categorias por tipo de perfil
  const categories = [
    { id: 'all', label: 'Todos', icon: '👥' },
    { id: 'casal', label: 'Casais', icon: '💑' },
    { id: 'mulher', label: 'Solteiras', icon: '👩' },
    { id: 'homem', label: 'Solteiros', icon: '👨' },
    { id: 'swing', label: 'Swing', icon: '🔥' },
    { id: 'novo', label: 'Novos', icon: '✨' },
  ];

  // Obter localização do usuário
  const getUserLocation = () => {
    setLoadingLocation(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(newLocation);
          setLoadingLocation(false);
          setShowNearby(true);
          // Recarregar criadores com a nova localização para calcular distâncias
          loadCreators(showOnlineOnly, newLocation.lat, newLocation.lng);
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast.error('Não foi possível obter sua localização');
          setLoadingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      toast.error('Geolocalização não suportada neste navegador');
      setLoadingLocation(false);
    }
  };

  // Calcular distância entre dois pontos
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return null;
    const R = 6371; // Raio da Terra em km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c);
  };

  const loadCreators = useCallback(async (sortOnlineFirst = false, userLat = null, userLng = null) => {
    try {
      let url = `${API}/creators?limit=100&sort_online_first=${sortOnlineFirst}`;
      // Passar coordenadas do usuário para calcular distância no backend
      if (userLat && userLng) {
        url += `&user_lat=${userLat}&user_lng=${userLng}`;
      }
      const response = await axios.get(url);
      setCreators(response.data);
    } catch (error) {
      console.error('Creators error:', error);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const onlineParam = params.get('online') === 'true';
    setShowOnlineOnly(onlineParam);
    // Passar coordenadas do usuário se disponíveis
    loadCreators(onlineParam, userLocation?.lat, userLocation?.lng);
  }, [location.search, loadCreators, userLocation]);

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    if (image.startsWith('/uploads')) {
      const token = localStorage.getItem('token');
      const baseUrl = `${BACKEND_URL}/api${image}`;
      if (image.includes('/posts/') && token) {
        return `${baseUrl}?token=${token}`;
      }
      return baseUrl;
    }
    if (image.startsWith('data:')) return image;
    return `data:image/jpeg;base64,${image}`;
  };

  const getCreatorImage = (creator) => {
    const image = creator?.cover_image || creator?.picture;
    return image ? getImageUrl(image) : null;
  };

  const toggleOnlineFilter = () => {
    const newValue = !showOnlineOnly;
    setShowOnlineOnly(newValue);
    navigate(newValue ? '/creators?online=true' : '/creators', { replace: true });
    loadCreators(newValue);
  };

  // Filtrar e ordenar criadores
  const filteredCreators = useMemo(() => {
    let result = [...creators];
    
    // Filtro de busca
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c => 
        c.name?.toLowerCase().includes(query) ||
        c.username?.toLowerCase().includes(query) ||
        c.bio?.toLowerCase().includes(query) ||
        c.cidade?.toLowerCase().includes(query) ||
        c.estado?.toLowerCase().includes(query)
      );
    }
    
    // Filtro de categoria (tipo de perfil)
    if (selectedCategory !== 'all') {
      if (selectedCategory === 'novo') {
        // Novos = cadastrados nos últimos 7 dias
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        result = result.filter(c => new Date(c.created_at) > weekAgo);
      } else {
        result = result.filter(c => c.profile_type === selectedCategory || c.gender === selectedCategory);
      }
    }
    
    // Filtro online
    if (showOnlineOnly) {
      result = result.filter(c => c.is_online);
    }
    
    // Adicionar distância se temos localização
    if (userLocation && showNearby) {
      result = result.map(c => ({
        ...c,
        distance: calculateDistance(
          userLocation.lat, userLocation.lng,
          c.latitude, c.longitude
        )
      })).filter(c => c.distance !== null);
    }
    
    // Ordenação
    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => (b.subscriber_count || 0) - (a.subscriber_count || 0));
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'price_low':
        result.sort((a, b) => (a.price_monthly || 0) - (b.price_monthly || 0));
        break;
      case 'price_high':
        result.sort((a, b) => (b.price_monthly || 0) - (a.price_monthly || 0));
        break;
      case 'distance':
        if (showNearby) {
          result.sort((a, b) => (a.distance || 9999) - (b.distance || 9999));
        }
        break;
      default:
        break;
    }
    
    // Online sempre primeiro
    result.sort((a, b) => (b.is_online ? 1 : 0) - (a.is_online ? 1 : 0));
    
    return result;
  }, [creators, searchQuery, selectedCategory, showOnlineOnly, sortBy, userLocation, showNearby]);

  return (
    <div data-testid="creators-page" className="min-h-screen bg-[#0D0D0D] text-white pb-24 md:pb-0">
      <Navbar NotificationsPanel={NotificationsPanel} />
      <div className="max-w-7xl mx-auto py-4 sm:py-8 px-3 sm:px-4">
        {/* Header - Mobile Optimized */}
        <div className="mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#C79F5E] mb-1 sm:mb-2">Explorar</h1>
          <p className="text-sm sm:text-base text-gray-400">Encontre pessoas incríveis perto de você</p>
        </div>

        {/* Filtros por Tipo de Perfil - Scroll Horizontal no Mobile */}
        <div className="mb-4 overflow-x-auto scrollbar-hide -mx-3 px-3">
          <div className="flex gap-2 min-w-max pb-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full font-medium text-sm whitespace-nowrap transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-gradient-to-r from-[#6B2D5C] to-[#C79F5E] text-white shadow-lg'
                    : 'bg-[#1A1A1A] text-gray-400 border border-white/10 hover:border-[#C79F5E]/50'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Barra de Busca e Filtros - Mobile Optimized */}
        <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6">
          {/* Busca */}
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar por nome ou cidade..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#0D0D0D] border border-white/10 rounded-xl px-4 py-3.5 pl-12 text-white placeholder-gray-500 focus:outline-none focus:border-[#C79F5E]/50 text-base"
              data-testid="search-input"
            />
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white p-1"
              >
                ✕
              </button>
            )}
          </div>

          {/* Ordenar, Online e Localização */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {/* Ordenar */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-[#0D0D0D] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#C79F5E]/50 flex-shrink-0"
              data-testid="sort-select"
            >
              <option value="popular">🔥 Populares</option>
              <option value="newest">✨ Novos</option>
              <option value="price_low">💰 Menor preço</option>
              <option value="price_high">💎 Maior preço</option>
              {showNearby && <option value="distance">📍 Mais perto</option>}
            </select>

            {/* Toggle Online */}
            <button
              onClick={toggleOnlineFilter}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all flex-shrink-0 ${
                showOnlineOnly
                  ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                  : 'bg-[#0D0D0D] text-gray-400 border border-white/10 hover:text-white'
              }`}
              data-testid="online-filter"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className={`${showOnlineOnly ? 'animate-ping' : ''} absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75`}></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </span>
              Online
            </button>

            {/* Botão de Localização */}
            <button
              onClick={showNearby ? () => setShowNearby(false) : getUserLocation}
              disabled={loadingLocation}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all flex-shrink-0 ${
                showNearby
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                  : 'bg-[#0D0D0D] text-gray-400 border border-white/10 hover:text-white'
              }`}
              data-testid="location-filter"
            >
              {loadingLocation ? (
                <span className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></span>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
              {showNearby ? 'Perto' : 'Radar'}
            </button>
          </div>
        </div>

        {/* Resultados */}
        {searchQuery && (
          <p className="text-gray-400 mb-4 text-sm sm:text-base">
            {filteredCreators.length} resultado{filteredCreators.length !== 1 ? 's' : ''} para "{searchQuery}"
          </p>
        )}

        {/* Grid de Criadores - Mobile Optimized */}
        {filteredCreators.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            {filteredCreators.map((creator) => {
              const creatorImage = getCreatorImage(creator);
              return (
                <div
                  key={creator.id}
                  data-testid={`creator-${creator.id}`}
                  onClick={() => navigate(`/creator/${creator.id}`)}
                  className="creator-card aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer relative group shadow-lg"
                  style={{
                    backgroundImage: creatorImage
                      ? `url(${creatorImage})`
                      : 'linear-gradient(135deg, #4D1A4A 0%, #6B2D5C 50%, #C79F5E 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {/* Badge Online */}
                  {creator.is_online && (
                    <div className="absolute top-2 right-2 z-20 flex items-center gap-1 bg-black/70 backdrop-blur-sm px-2 py-1 rounded-full">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      <span className="text-[10px] sm:text-xs text-green-400 font-bold">ON</span>
                    </div>
                  )}

                  {/* Badge Novo */}
                  {(() => {
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return new Date(creator.created_at) > weekAgo;
                  })() && (
                    <div className="absolute top-2 left-2 z-20 bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full">
                      NOVO
                    </div>
                  )}

                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
                  
                  {/* Info do Criador - Mobile Optimized */}
                  <div className="relative z-10 h-full flex flex-col justify-end p-3 sm:p-4">
                    {/* Nome */}
                    <h3 className="text-base sm:text-lg font-bold mb-0.5 truncate leading-tight">{creator.name}</h3>
                    
                    {/* Tipo de perfil */}
                    <p className="text-[10px] sm:text-xs text-[#C79F5E] font-medium mb-1">
                      {creator.profile_type === 'casal' ? '💑 Casal' : 
                       creator.profile_type === 'mulher' || creator.gender === 'mulher' ? '👩 Solteira' :
                       creator.profile_type === 'homem' || creator.gender === 'homem' ? '👨 Solteiro' : 
                       '👤 Perfil'}
                    </p>
                    
                    {/* Localização e distância */}
                    {(creator.cidade || creator.distance_formatted || creator.distance_km) && (
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-1 truncate flex items-center gap-1">
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                        {creator.distance_formatted ? creator.distance_formatted : 
                         creator.distance_km ? `${creator.distance_km} km` : 
                         creator.cidade}
                        {creator.estado && !creator.distance_formatted && !creator.distance_km && `, ${creator.estado}`}
                      </p>
                    )}
                    
                    {/* Stats e Redes Sociais */}
                    <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-300">
                      <span>{creator.subscribers_count || 0} seguidores</span>
                      
                      {/* Ícones de Redes Sociais */}
                      {(creator.instagram_url || creator.twitter_url) && (
                        <div className="flex items-center gap-2">
                          {creator.instagram_url && (
                            <a
                              href={`https://instagram.com/${creator.instagram_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-pink-400 hover:text-pink-300 transition-colors"
                              title="Instagram"
                            >
                              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                              </svg>
                            </a>
                          )}
                          {creator.twitter_url && (
                            <a
                              href={`https://x.com/${creator.twitter_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-gray-400 hover:text-white transition-colors"
                              title="X (Twitter)"
                            >
                              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 sm:py-16">
            <div className="text-5xl mb-4">🔍</div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-400 mb-2">
              {showOnlineOnly ? 'Nenhum perfil online agora' : 'Nenhum perfil encontrado'}
            </h3>
            <p className="text-sm sm:text-base text-gray-500 mb-4">
              {showOnlineOnly ? 'Volte mais tarde ou veja todos' : 'Tente ajustar os filtros'}
            </p>
            {showOnlineOnly && (
              <button
                onClick={() => {
                  setShowOnlineOnly(false);
                  navigate('/creators', { replace: true });
                }}
                className="px-6 py-3 bg-gradient-to-r from-[#6B2D5C] to-[#C79F5E] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
              >
                Ver todos
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Media Carousel Component with Viewer Limits
const MediaCarousel = ({ mediaUrls, isPremium, price, postId, creatorId, creatorName, isSubscribed, isOwner, isPurchased }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewerBlocked, setViewerBlocked] = useState(false);
  const [blockInfo, setBlockInfo] = useState(null);
  const [hasTracked, setHasTracked] = useState(false);
  const { user } = useAuth();
  const { isViewer, hasLimits, trackContentView, checkCreatorLimit } = useViewerLimits();
  const navigate = useNavigate();

  if (!mediaUrls || mediaUrls.length === 0) return null;
  
  // Admin tem acesso grátis a todo conteúdo
  const isAdmin = user?.role === 'admin';
  
  // Lógica de acesso:
  // - PACK LYBERHOT (com preço): requer compra individual, mesmo para assinantes
  // - Premium SEM preço: assinantes têm acesso
  const hasPackPrice = price && price > 0;
  const showPremiumOverlay = isPremium && !isOwner && !isAdmin && !isPurchased && (hasPackPrice || !isSubscribed);

  // Verificar limites de viewer ao montar
  useEffect(() => {
    const checkViewerLimits = async () => {
      // Para packs com preço, sempre verificar (mesmo assinantes precisam comprar)
      const needsPackPurchase = hasPackPrice && !isPurchased && !isOwner && !isAdmin;
      if (needsPackPurchase) {
        setViewerBlocked(false); // Não bloquear como viewer, deixar overlay de pack
        return;
      }
      
      if (!hasLimits || !creatorId || isSubscribed || isOwner || isAdmin) {
        setViewerBlocked(false);
        return;
      }

      // Verificar limites para este criador
      const limits = await checkCreatorLimit(creatorId);
      if (limits.show_cta) {
        setViewerBlocked(true);
        setBlockInfo({
          limitType: limits.global_blocked ? 'global' : 'profile',
          photosViewed: limits.photos_viewed,
          totalContent: limits.total_creator_content
        });
      }
    };

    checkViewerLimits();
  }, [hasLimits, creatorId, isSubscribed, isOwner, isAdmin, checkCreatorLimit]);

  // Registrar visualização quando conteúdo é mostrado (apenas uma vez)
  useEffect(() => {
    const trackView = async () => {
      if (hasTracked || !hasLimits || !creatorId || viewerBlocked || showPremiumOverlay) return;
      
      const contentType = isVideo(mediaUrls[currentIndex]) ? 'video' : 'photo';
      const result = await trackContentView(creatorId, contentType, postId);
      
      if (!result.allowed) {
        setViewerBlocked(true);
        setBlockInfo({
          limitType: result.limit_type === 'global_daily' ? 'global' : 'profile',
          message: result.message,
          description: result.description
        });
      }
      setHasTracked(true);
    };

    trackView();
  }, [hasLimits, creatorId, postId, currentIndex, hasTracked, viewerBlocked, showPremiumOverlay, trackContentView, mediaUrls]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % mediaUrls.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + mediaUrls.length) % mediaUrls.length);
  };

  const getMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) {
      const token = localStorage.getItem('token');
      const baseUrl = `${BACKEND_URL}/api${url}`;
      // Adicionar token para posts premium
      if (url.includes('/posts/') && token) {
        return `${baseUrl}?token=${token}`;
      }
      return baseUrl;
    }
    if (url.startsWith('data:')) return url;
    return `data:image/jpeg;base64,${url}`;
  };

  const isVideo = (url) => {
    return url && (url.includes('.mp4') || url.includes('.webm') || url.includes('.mov'));
  };

  return (
    <div className="relative group">
      {/* Media Display */}
      <div className="relative w-full aspect-square bg-black overflow-hidden">
        {/* Mídia normal quando não é premium ou viewer bloqueado */}
        {!showPremiumOverlay && (
          <>
            {isVideo(mediaUrls[currentIndex]) ? (
              <video
                src={getMediaUrl(mediaUrls[currentIndex])}
                className={`w-full h-full object-contain ${viewerBlocked ? 'blur-xl scale-110' : ''}`}
                controls={!viewerBlocked}
                playsInline
              />
            ) : (
              <img
                src={getMediaUrl(mediaUrls[currentIndex])}
                alt=""
                className={`w-full h-full object-cover ${viewerBlocked ? 'blur-xl scale-110' : ''}`}
              />
            )}
          </>
        )}
        
        {/* Background gradiente atraente para conteúdo premium */}
        {showPremiumOverlay && (
          <div className="absolute inset-0 overflow-hidden">
            <div className="w-full h-full bg-gradient-to-br from-[#4D1A4A] via-[#6B2D5C]/80 to-[#C79F5E]/60 animate-pulse" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMjAgMjBMMCAyMEwwIDBMMjAgMEwyMCAyMFoiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wMykiLz48L3N2Zz4=')] opacity-50" />
          </div>
        )}

        {/* Viewer Limit Overlay (Priority over Premium) */}
        {viewerBlocked && !showPremiumOverlay && (
          <ViewerContentOverlay
            creatorId={creatorId}
            creatorName={creatorName}
            limitType={blockInfo?.limitType || 'profile'}
            photosViewed={blockInfo?.photosViewed || 0}
            totalContent={blockInfo?.totalContent || 0}
          />
        )}

        {/* Premium Overlay com informações diferenciadas */}
        {showPremiumOverlay && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <div className="text-center px-4">
              {price ? (
                // Pack Lyberhot - compra avulsa
                <>
                  <div className="text-5xl mb-2">🔥</div>
                  <div className="bg-gradient-to-r from-[#C79F5E] to-[#D4AF37] text-black px-4 py-1 rounded-full text-sm font-bold mb-3 inline-block">
                    PACK LYBERHOT
                  </div>
                  <p className="text-lg font-bold text-white mb-1">Conteúdo Exclusivo</p>
                  <p className="text-gray-300 text-sm mb-3">Compra única • Acesso vitalício</p>
                  <p className="text-[#D4AF37] text-3xl font-black mb-4">R$ {price.toFixed(2)}</p>
                  <button 
                    onClick={() => {
                      if (!user) {
                        toast.error('Faça login para comprar');
                        navigate('/?mode=login');
                        return;
                      }
                      const params = new URLSearchParams({
                        type: 'post',
                        post_id: postId,
                        creator_id: creatorId || '',
                        amount: price.toString()
                      });
                      navigate(`/payment?${params.toString()}`);
                    }}
                    className="bg-gradient-to-r from-[#C79F5E] to-[#D4AF37] text-black px-8 py-3 rounded-full font-bold hover:scale-105 transition-transform shadow-lg"
                    data-testid="buy-lyberhot-btn"
                  >
                    🔥 Comprar Pack
                  </button>
                </>
              ) : (
                // Premium para assinantes
                <>
                  <div className="text-5xl mb-2">⭐</div>
                  <div className="bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] text-white px-4 py-1 rounded-full text-sm font-bold mb-3 inline-block">
                    EXCLUSIVO ASSINANTES
                  </div>
                  <p className="text-lg font-bold text-white mb-1">Conteúdo Premium</p>
                  <p className="text-gray-300 text-sm mb-4">Assine para desbloquear todo o conteúdo</p>
                  <button 
                    onClick={() => {
                      if (!user) {
                        toast.error('Faça login para assinar');
                        navigate('/?mode=login');
                        return;
                      }
                      // Ir direto para página de pagamento
                      const params = new URLSearchParams({
                        type: 'subscription',
                        creator_id: creatorId || '',
                        creator_name: creatorName || 'Criador',
                        plan: 'monthly',
                        amount: '29.90'
                      });
                      navigate(`/payment?${params.toString()}`);
                    }}
                    className="bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] text-white px-8 py-3 rounded-full font-bold hover:scale-105 transition-transform shadow-lg"
                    data-testid="subscribe-premium-btn"
                  >
                    ⭐ Assinar Agora
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Arrows */}
      {mediaUrls.length > 1 && !viewerBlocked && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
          >
            ←
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
          >
            →
          </button>

          {/* Dots Indicator */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {mediaUrls.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-white w-6'
                    : 'bg-white/50 hover:bg-white/80'
                }`}
              />
            ))}
          </div>

          {/* Counter */}
          <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
            {currentIndex + 1} / {mediaUrls.length}
          </div>
        </>
      )}

      {/* Video Badge */}
      {isVideo(mediaUrls[currentIndex]) && !viewerBlocked && (
        <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2">
          🎥 Vídeo
        </div>
      )}

      {/* Viewer Limits Badge */}
      {isViewer && !viewerBlocked && hasLimits && (
        <div className="absolute top-4 left-4 bg-[#C79F5E]/80 text-black px-3 py-1 rounded-full text-xs font-semibold">
          👁️ Visualização gratuita
        </div>
      )}
    </div>
  );
};

// Comments Component
const CommentsSection = ({ postId }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    if (image.startsWith('/uploads')) {
      const token = localStorage.getItem('token');
      const baseUrl = `${BACKEND_URL}/api${image}`;
      // Adicionar token para posts premium
      if (image.includes('/posts/') && token) {
        return `${baseUrl}?token=${token}`;
      }
      return baseUrl;
    }
    if (image.startsWith('data:')) return image;
    return `data:image/jpeg;base64,${image}`;
  };

  useEffect(() => {
    loadComments();
  }, [postId]);

  const loadComments = async () => {
    try {
      const response = await axios.get(`${API}/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Comments error:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    if (!user) {
      toast.error('Faça login para comentar');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/posts/${postId}/comments`,
        { content: newComment },
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true 
        }
      );
      
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comentário adicionado!');
    } catch (error) {
      console.error('Add comment error:', error);
      toast.error('Erro ao adicionar comentário');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Comentário removido');
    } catch (error) {
      console.error('Delete comment error:', error);
      toast.error('Erro ao remover comentário');
    }
  };

  return (
    <div className="border-t border-white/5 pt-4 mt-4">
      {/* Comments List */}
      <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
        {comments.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4">Seja o primeiro a comentar!</p>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="flex gap-3">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-700 flex-shrink-0">
                {comment.user?.picture ? (
                  <img
                    src={getImageUrl(comment.user.picture)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs">👤</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm">
                  <span className="font-semibold text-white">
                    {comment.user?.name}
                    {comment.user?.verified && <span className="text-[#6B2D5C] ml-1">✓</span>}:
                  </span>
                  <MentionText text={comment.content} className="text-gray-300 ml-2" />
                </p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-gray-500">
                    {new Date(comment.created_at).toLocaleDateString('pt-BR')}
                  </span>
                  {user && user.id === comment.user_id && (
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-xs text-[#6B2D5C] hover:text-[#C79F5E]"
                    >
                      Deletar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Comment Form */}
      <form onSubmit={handleAddComment} className="flex gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={user ? "Adicione um comentário... use @ para mencionar" : "Faça login para comentar"}
            disabled={!user || loading}
            className="w-full bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder:text-gray-500 focus:border-[#4D1A4A] focus:ring-1 focus:ring-[#4D1A4A] disabled:opacity-50"
          />
        </div>
        <button
          type="submit"
          disabled={!user || !newComment.trim() || loading}
          className="text-[#6B2D5C] font-semibold text-sm hover:text-[#C79F5E] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Enviando...' : 'Publicar'}
        </button>
      </form>
    </div>
  );
};


// Analytics Panel Component
const AnalyticsPanel = () => {
  const [analytics, setAnalytics] = useState(null);
  const [username, setUsername] = useState('');
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/my-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
      setUsername(response.data.username || '');
    } catch (error) {
      console.error('Analytics error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetUsername = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/users/set-username?username=${newUsername}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Username definido com sucesso!');
      setShowUsernameModal(false);
      loadAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao definir username');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copiado!');
  };

  if (loading) {
    return <div className="text-center py-8">Carregando analytics...</div>;
  }

  return (
    <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-8 rounded-2xl mb-8">
      <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <span>📊</span> Link de Divulgação & Analytics
      </h2>

      {/* URL Section */}
      {username ? (
        <div className="mb-8">
          <label className="text-sm text-gray-400 mb-2 block">Seu Link Exclusivo:</label>
          <div className="flex gap-2">
            <div className="flex-1 glass px-4 py-3 rounded-lg font-mono text-[#C79F5E]">
              {window.location.origin}/c/{username}
            </div>
            <button
              onClick={() => copyToClipboard(`${window.location.origin}/c/${username}`)}
              className="px-6 py-3 bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] rounded-lg font-semibold hover:scale-105 transition-transform"
            >
              📋 Copiar
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Compartilhe este link para rastrear cadastros e assinaturas vindos das suas divulgações
          </p>
        </div>
      ) : (
        <div className="mb-8 p-6 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
          <p className="text-yellow-400 mb-4">
            ⚠️ Configure seu username para ativar o link de divulgação e analytics
          </p>
          <button
            onClick={() => setShowUsernameModal(true)}
            className="px-6 py-2 bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] rounded-lg font-semibold"
          >
            Definir Username
          </button>
        </div>
      )}

      {/* Funnel Stats */}
      {analytics && username && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 p-6 rounded-xl border border-blue-500/30">
              <div className="text-3xl font-bold text-blue-400">{analytics.funnel.clicks}</div>
              <div className="text-sm text-gray-400">Cliques no Link</div>
            </div>
            <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 p-6 rounded-xl border border-green-500/30">
              <div className="text-3xl font-bold text-green-400">{analytics.funnel.registrations}</div>
              <div className="text-sm text-gray-400">Cadastros</div>
            </div>
            <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 p-6 rounded-xl border border-yellow-500/30">
              <div className="text-3xl font-bold text-yellow-400">{analytics.funnel.subscriptions}</div>
              <div className="text-sm text-gray-400">Assinaturas</div>
            </div>
          </div>

          {/* Conversion Rates */}
          <div className="bg-white/5 p-6 rounded-xl">
            <h3 className="font-bold mb-4">📈 Taxa de Conversão</h3>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Cliques → Cadastros</span>
                  <span className="text-[#C79F5E]">{analytics.conversion_rate.click_to_register}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-green-500"
                    style={{ width: `${analytics.conversion_rate.click_to_register}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Cadastros → Assinaturas</span>
                  <span className="text-[#C79F5E]">{analytics.conversion_rate.register_to_subscribe}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-500 to-yellow-500"
                    style={{ width: `${analytics.conversion_rate.register_to_subscribe}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Cliques → Assinaturas (Total)</span>
                  <span className="text-[#C79F5E]">{analytics.conversion_rate.click_to_subscribe}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-yellow-500"
                    style={{ width: `${analytics.conversion_rate.click_to_subscribe}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Username Modal */}
      {showUsernameModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 max-w-md w-full p-6 rounded-2xl">
            <h3 className="text-2xl font-bold mb-4">Definir Username</h3>
            <p className="text-gray-400 mb-4 text-sm">
              Escolha um username único para seu link de divulgação. Use apenas letras, números, _ ou -
            </p>
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value.toLowerCase())}
              placeholder="ex: casalwesexy"
              className="w-full glass px-4 py-3 rounded-lg mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setShowUsernameModal(false)}
                className="flex-1 px-4 py-2 bg-white/10 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleSetUsername}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] rounded-lg font-semibold"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);

  const loadVerificationStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/verification/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Verification status error:', error);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const userId = user?.id || user?._id;
      if (!userId) return;
      const response = await axios.get(`${API}/creators/${userId}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Stats error:', error);
    }
  }, [user]);

  const loadSubscribers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/creators/my-subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data);
    } catch (error) {
      console.error('Subscribers error:', error);
    }
  }, []);

  const loadSubscriptions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/my-subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Subscriptions error:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'creator' || user?.role === 'admin') {
      loadStats();
      loadVerificationStatus();
      loadSubscribers();
    } else {
      loadSubscriptions();
    }
  }, [user, loadStats, loadVerificationStatus, loadSubscribers, loadSubscriptions]);

  const handleChatWithSubscriber = (subscriberId) => {
    navigate('/messages', { state: { openChatWith: subscriberId } });
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    if (image.startsWith('/uploads')) {
      const token = localStorage.getItem('token');
      const baseUrl = `${BACKEND_URL}/api${image}`;
      // Adicionar token para posts premium
      if (image.includes('/posts/') && token) {
        return `${baseUrl}?token=${token}`;
      }
      return baseUrl;
    }
    if (image.startsWith('data:')) return image;
    return `data:image/jpeg;base64,${image}`;
  };

  // Dashboard para FÃS/ASSINANTES
  if (user?.role !== 'creator' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white pb-20 md:pb-0">
        <Navbar NotificationsPanel={NotificationsPanel} />
        <div className="max-w-6xl mx-auto py-8 px-4">
          <h1 className="text-3xl sm:text-4xl font-bold mb-6 sm:mb-8 text-[#C79F5E]">Meu Perfil</h1>

          {/* Card Seja um Criador - Destaque */}
          <div className="bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] p-1 rounded-2xl mb-6 sm:mb-8">
            <div className="bg-[#0D0D0D] rounded-2xl p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <div className="text-5xl sm:text-6xl">🌟</div>
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Torne-se um Criador!</h2>
                  <p className="text-gray-400 text-sm sm:text-base mb-4">
                    Compartilhe seu conteúdo exclusivo, ganhe dinheiro com assinantes e construa sua comunidade.
                  </p>
                  <ul className="text-sm text-gray-300 space-y-1 mb-4">
                    <li className="flex items-center gap-2 justify-center sm:justify-start"><span className="text-[#C79F5E]">✓</span> Receba pagamentos de assinantes</li>
                    <li className="flex items-center gap-2 justify-center sm:justify-start"><span className="text-[#C79F5E]">✓</span> Publique conteúdo exclusivo</li>
                    <li className="flex items-center gap-2 justify-center sm:justify-start"><span className="text-[#C79F5E]">✓</span> Chat direto com seus fãs</li>
                  </ul>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      await axios.put(
                        `${API}/users/me`,
                        { role: 'creator' },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      toast.success('Bem-vindo como criador! Complete seu perfil.');
                      navigate('/creator-setup');
                      window.location.reload();
                    } catch (error) {
                      toast.error('Erro ao atualizar perfil');
                    }
                  }}
                  className="bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] hover:shadow-[0_0_25px_rgba(199,159,94,0.5)] text-black px-8 py-4 rounded-xl font-bold text-base sm:text-lg transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
                >
                  🌟 Começar Agora
                </button>
              </div>
            </div>
          </div>

          {/* Dados Pessoais */}
          <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 sm:p-8 rounded-2xl mb-6 sm:mb-8">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-white">
              <span>👤</span> Dados Pessoais
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Nome</label>
                <p className="text-white text-lg font-semibold">{user?.name}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Email</label>
                <p className="text-white text-lg font-semibold">{user?.email}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Tipo de Conta</label>
                <span className="inline-block px-3 py-1 bg-[#C79F5E]/20 text-[#C79F5E] rounded-full text-sm font-semibold">
                  Assinante
                </span>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Membro desde</label>
                <p className="text-white text-lg font-semibold">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Seção de Gamificação */}
          <div className="mb-6 sm:mb-8">
            <GamificationProfile 
              userId={user?.id} 
              showLeaderboard={user?.role === 'admin' || user?.is_fundador}
            />
          </div>

          {/* Minhas Assinaturas */}
          <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-8 rounded-2xl">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-white">
              <span>⭐</span> Minhas Assinaturas
            </h2>
            
            {subscriptions.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">💫</div>
                <p className="text-gray-400 text-lg mb-6">Você ainda não assinou nenhum criador</p>
                <button
                  onClick={() => window.location.href = '/creators'}
                  className="bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] hover:shadow-[0_0_20px_rgba(199,159,94,0.4)] text-black px-8 py-3 rounded-full font-semibold transition-all"
                >
                  Explorar Criadores
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {subscriptions.map((sub) => (
                  <div key={sub.id} className="bg-[#0D0D0D] border border-[#C79F5E]/10 rounded-xl p-6 hover:border-[#C79F5E]/30 transition-all">
                    <div className="flex items-center gap-4">
                      {/* Creator Avatar */}
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-[#4D1A4A] to-[#C79F5E]">
                        {sub.creator?.picture ? (
                          <img 
                            src={getImageUrl(sub.creator.picture)}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white text-2xl font-bold">
                            {sub.creator?.name?.charAt(0) || '?'}
                          </div>
                        )}
                      </div>

                      {/* Creator Info */}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">{sub.creator?.name}</h3>
                        <p className="text-gray-400 text-sm">Plano: {sub.tier || 'Básico'}</p>
                      </div>

                      {/* Subscription Info */}
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[#C79F5E] mb-1">R$ {sub.price?.toFixed(2)}</p>
                        <p className="text-sm text-gray-400">
                          Vence: {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString('pt-BR') : 'Nunca'}
                        </p>
                        <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${
                          sub.status === 'active' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-[#6B2D5C]/20 text-[#C79F5E]'
                        }`}>
                          {sub.status === 'active' ? 'Ativa' : 'Inativa'}
                        </span>
                      </div>

                      {/* Actions */}
                      <button
                        onClick={() => window.location.href = `/creator/${sub.creator_id}`}
                        className="bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] hover:from-red-700 hover:to-pink-700 text-white px-6 py-2 rounded-full font-semibold transition-all"
                      >
                        Ver Perfil
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Botões de Ação para Fãs/Assinantes */}
          <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-8 rounded-2xl">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-white">
              <span>⚙️</span> Configurações da Conta
            </h2>
            
            <div className="space-y-4">
              {/* Botão Sair */}
              <button
                onClick={() => {
                  if (window.confirm('Tem certeza que deseja sair da sua conta?')) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('session_token');
                    window.location.href = '/';
                  }
                }}
                className="w-full bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
              >
                <span>🚪</span> Sair da Conta
              </button>

              {/* Botão Excluir Conta */}
              <button
                onClick={() => {
                  if (window.confirm('⚠️ ATENÇÃO: Esta ação é irreversível!\n\nTem certeza que deseja excluir permanentemente sua conta? Todos os seus dados serão perdidos.')) {
                    // Aqui você pode implementar a lógica de exclusão da conta
                    toast.error('Funcionalidade em desenvolvimento. Entre em contato com o suporte.');
                  }
                }}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
              >
                <span>🗑️</span> Excluir Conta
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard para CRIADORES (mantém o original)
  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[#0D0D0D] text-white">
      <Navbar NotificationsPanel={NotificationsPanel} />
      
      {/* Banner de Verificação */}
      {verificationStatus && verificationStatus.status !== 'approved' && verificationStatus.status !== 'not_required' && (
        <div className="max-w-6xl mx-auto px-4 pt-8">
          {verificationStatus.status === 'none' && (
            <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/50 rounded-2xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="text-4xl">⚠️</div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-yellow-400 mb-2">Verificação Necessária</h3>
                  <p className="text-gray-300 mb-4">
                    Para criar conteúdo pago, você precisa verificar sua identidade. 
                    Este processo é rápido e garante a segurança da plataforma.
                  </p>
                  <button
                    onClick={() => navigate('/verification')}
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black px-6 py-2 rounded-full font-semibold transition-all"
                  >
                    Verificar Agora
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {verificationStatus.status === 'pending' && (
            <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/50 rounded-2xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="text-4xl">⏳</div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-blue-400 mb-2">Verificação Pendente</h3>
                  <p className="text-gray-300">
                    Seus documentos estão sendo analisados. Isso pode levar até 24 horas. 
                    Você será notificado quando a verificação for concluída.
                  </p>
                  {verificationStatus.submitted_at && (
                    <p className="text-sm text-gray-400 mt-2">
                      Enviado em: {new Date(verificationStatus.submitted_at).toLocaleString('pt-BR')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {verificationStatus.status === 'rejected' && (
            <div className="bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-500/50 rounded-2xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="text-4xl">❌</div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-red-400 mb-2">Verificação Rejeitada</h3>
                  <p className="text-gray-300 mb-2">
                    Sua verificação foi rejeitada. Por favor, envie novos documentos.
                  </p>
                  {verificationStatus.rejection_reason && (
                    <p className="text-yellow-300 mb-4">
                      <strong>Motivo:</strong> {verificationStatus.rejection_reason}
                    </p>
                  )}
                  <button
                    onClick={() => navigate('/verification')}
                    className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-2 rounded-full font-semibold transition-all"
                  >
                    Enviar Novamente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="max-w-6xl mx-auto py-8 px-4">
        {/* Banner PIX para criadores/fundadores */}
        <PixRegistrationBanner />

        {/* Header do Dashboard com Selo de Fundador */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl sm:text-4xl font-bold text-[#C79F5E]">Dashboard</h1>
            {user?.is_fundador && (
              <div className="flex items-center gap-2 bg-gradient-to-r from-[#D4AF37]/20 to-[#FFD700]/20 border border-[#D4AF37] px-3 py-1.5 rounded-full">
                <span className="text-lg">💎</span>
                <span className="text-[#D4AF37] font-bold text-sm">MEMBRO FUNDADOR</span>
                <span className="text-lg">✓</span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <button
              onClick={async () => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*,video/*';
                input.onchange = async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  
                  try {
                    const token = localStorage.getItem('token');
                    const formData = new FormData();
                    formData.append('file', file);
                    
                    toast.info('Postando story...');
                    await axios.post(`${API}/stories/create`, formData, {
                      headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                      }
                    });
                    toast.success('Story postado com sucesso!');
                  } catch (error) {
                    toast.error('Erro ao postar story');
                  }
                };
                input.click();
              }}
              className="bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] hover:from-[#6B2D5C] hover:to-[#C79F5E] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all text-sm sm:text-base"
            >
              ⭐ Story
            </button>
            <button
              onClick={() => navigate('/creator-chat')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all text-sm sm:text-base"
            >
              💬 Chat
            </button>
            <button
              onClick={() => navigate('/analytics')}
              className="bg-[#1A1A1A] border border-[#C79F5E]/30 hover:border-[#C79F5E] text-[#C79F5E] px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all text-sm sm:text-base"
            >
              📊 Analytics
            </button>
          </div>
        </div>

        {/* Link de Divulgação Unificado */}
        {user?.username && (
          <div className="bg-[#1A1A1A] border border-[#C79F5E]/30 p-4 sm:p-6 rounded-2xl mb-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">🔗</span>
              <h2 className="text-xl sm:text-2xl font-bold text-[#C79F5E]">Seu Link de Divulgação</h2>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 bg-[#0D0D0D] border border-[#C79F5E]/20 px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-mono text-[#C79F5E] text-sm sm:text-lg overflow-x-auto">
                {window.location.origin}/c/{user.username}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/c/${user.username}`);
                  toast.success('Link copiado!');
                }}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black rounded-lg font-semibold hover:scale-105 hover:shadow-[0_0_20px_rgba(199,159,94,0.4)] transition-all whitespace-nowrap text-sm sm:text-base"
              >
                📋 Copiar Link
              </button>
            </div>
            <p className="text-xs sm:text-sm text-gray-400 mt-2">
              💡 Compartilhe para rastrear cliques, cadastros e assinaturas
            </p>
          </div>
        )}

        {/* Cards de Estatísticas Melhorados */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            {/* Assinantes */}
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-4 sm:p-6 rounded-xl">
              <p className="text-gray-400 text-sm mb-1">Assinantes</p>
              <p className="text-2xl sm:text-4xl font-bold">{stats.subscribers}</p>
            </div>
            
            {/* Posts */}
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-4 sm:p-6 rounded-xl">
              <p className="text-gray-400 text-sm mb-1">Posts</p>
              <p className="text-2xl sm:text-4xl font-bold">{stats.posts}</p>
            </div>
            
            {/* Faturamento Bruto */}
            <div className="bg-[#1A1A1A] border border-blue-500/20 p-4 sm:p-6 rounded-xl">
              <p className="text-gray-400 text-sm mb-1">Faturamento Bruto</p>
              <p className="text-2xl sm:text-4xl font-bold text-blue-400">
                R$ {(stats.gross_revenue || 0).toFixed(2)}
              </p>
            </div>
            
            {/* Receita Líquida (Seu Ganho) */}
            <div className="bg-gradient-to-br from-[#1A1A1A] to-[#2A2A2A] border border-green-500/30 p-4 sm:p-6 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-gray-400 text-sm">Sua Receita</p>
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                  {stats.creator_rate || 85}%
                </span>
              </div>
              <p className="text-2xl sm:text-4xl font-bold text-green-400">
                R$ {(stats.revenue || 0).toFixed(2)}
              </p>
            </div>
          </div>
        )}

        {/* Info de Percentuais */}
        {stats && (
          <div className="bg-[#1A1A1A]/50 border border-[#C79F5E]/10 p-4 rounded-xl mb-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Tipo:</span>
                  <span className={`font-bold ${stats.is_fundador ? 'text-[#D4AF37]' : 'text-[#C79F5E]'}`}>
                    {stats.is_fundador ? '💎 Membro Fundador' : '⭐ Criador'}
                  </span>
                </div>
                <div className="text-gray-600 hidden sm:block">|</div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Você:</span>
                  <span className="font-bold text-green-400">{stats.creator_rate || 82}%</span>
                </div>
                <div className="text-gray-600 hidden sm:block">|</div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Lyberhot:</span>
                  <span className="text-gray-400">{stats.platform_rate || 18}%</span>
                </div>
              </div>
              
              {/* Ganhos de Afiliados (apenas fundadores) */}
              {stats.is_fundador && stats.affiliate_revenue > 0 && (
                <div className="flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-3 py-1.5 rounded-lg">
                  <span className="text-sm text-gray-400">Comissão Afiliados:</span>
                  <span className="font-bold text-[#D4AF37]">
                    R$ {(stats.affiliate_revenue || 0).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Detalhamento de Receita por Tipo */}
        {stats && stats.revenue_breakdown && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            {/* Assinaturas */}
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-4 rounded-xl text-center">
              <span className="text-2xl block mb-1">👥</span>
              <p className="text-xs text-gray-400 mb-1">Assinaturas</p>
              <p className="text-lg font-bold text-[#C79F5E]">
                R$ {(stats.revenue_breakdown.subscriptions || 0).toFixed(2)}
              </p>
            </div>
            
            {/* Lyberhot Exclusivo */}
            <div className="bg-[#1A1A1A] border border-purple-500/20 p-4 rounded-xl text-center">
              <span className="text-2xl block mb-1">🔥</span>
              <p className="text-xs text-gray-400 mb-1">Lyberhot Exclusivo</p>
              <p className="text-lg font-bold text-purple-400">
                R$ {(stats.revenue_breakdown.exclusive_posts || 0).toFixed(2)}
              </p>
            </div>
            
            {/* Chat Pago */}
            <div className="bg-[#1A1A1A] border border-pink-500/20 p-4 rounded-xl text-center">
              <span className="text-2xl block mb-1">💬</span>
              <p className="text-xs text-gray-400 mb-1">Chat Pago</p>
              <p className="text-lg font-bold text-pink-400">
                R$ {(stats.revenue_breakdown.chat || 0).toFixed(2)}
              </p>
            </div>
          </div>
        )}

        {/* Dashboard de Padrinho/Madrinha Hot */}
        {user && (
          <div className="mb-6">
            <GodparentDashboard userId={user.id} />
          </div>
        )}

        {/* Dashboard de Fundador */}
        {user && user.is_fundador && (
          <div className="mb-6">
            <FundadorDashboard user={user} />
          </div>
        )}

        {/* Seção de Gamificação */}
        {user && (
          <div className="mb-6">
            <GamificationProfile 
              userId={user.id} 
              compact={false}
              showLeaderboard={false}
            />
          </div>
        )}

        {/* Seção de Dados Bancários para Pagamento */}
        {user && (
          <div className="mb-6">
            <BankDataForm />
          </div>
        )}

        {/* Seção de Saques */}
        {user && (
          <div className="mb-6">
            <WithdrawalSection />
          </div>
        )}

        {/* Seção de Assinantes com Chat */}
        <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <span>👥</span> 
              Meus Assinantes
              {subscribers.length > 0 && (
                <span className="bg-[#6B2D5C] text-white text-sm px-3 py-1 rounded-full">
                  {subscribers.length}
                </span>
              )}
            </h2>
          </div>

          {subscribers.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">💫</div>
              <p className="text-gray-400 text-lg mb-2">Você ainda não tem assinantes</p>
              <p className="text-gray-500 text-sm">Compartilhe seu link de divulgação para atrair mais fãs!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {subscribers.map((item) => (
                <div 
                  key={item.fan.id} 
                  className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all"
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar do Assinante */}
                    <div className="relative">
                      <div className="w-14 h-14 rounded-full overflow-hidden bg-gradient-to-br from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E]">
                        {item.fan.picture ? (
                          <img 
                            src={getImageUrl(item.fan.picture)}
                            alt={item.fan.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white text-xl font-bold">
                            {item.fan.name?.charAt(0) || '?'}
                          </div>
                        )}
                      </div>
                      {/* Indicador Online */}
                      {item.fan.is_online && (
                        <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-[#1A1A1A] rounded-full">
                          <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-50"></span>
                        </div>
                      )}
                    </div>

                    {/* Info do Assinante */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-white truncate">{item.fan.name}</h3>
                        {item.fan.is_online && (
                          <span className="text-xs text-green-400 font-medium">Online</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400 truncate">{item.fan.email}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-gray-500">
                          Assinante desde {item.subscription.created_at ? new Date(item.subscription.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          item.subscription.status === 'active' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {item.subscription.status === 'active' ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                    </div>

                    {/* Botão de Chat */}
                    <button
                      onClick={() => handleChatWithSubscriber(item.fan.id)}
                      className="flex items-center gap-2 bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] hover:from-red-700 hover:to-pink-700 text-white px-5 py-2.5 rounded-full font-semibold transition-all shadow-lg hover:shadow-red-500/20"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      Chat
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Configurações da Conta - Criadores */}
        <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-8 rounded-2xl mb-8">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-white">
            <span>⚙️</span> Configurações da Conta
          </h2>
          
          <div className="space-y-4">
            {/* Botão Sair */}
            <button
              onClick={() => {
                if (window.confirm('Tem certeza que deseja sair da sua conta?')) {
                  localStorage.removeItem('token');
                  localStorage.removeItem('session_token');
                  window.location.href = '/';
                }
              }}
              className="w-full bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
              data-testid="logout-btn"
            >
              <span>🚪</span> Sair da Conta
            </button>

            {/* Botão Excluir Conta */}
            <button
              onClick={() => {
                if (window.confirm('⚠️ ATENÇÃO: Esta ação é irreversível!\n\nTem certeza que deseja excluir permanentemente sua conta? Todos os seus dados, posts e assinantes serão perdidos.')) {
                  toast.error('Funcionalidade em desenvolvimento. Entre em contato com o suporte.');
                }
              }}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
              data-testid="delete-account-btn"
            >
              <span>🗑️</span> Excluir Conta
            </button>
          </div>
        </div>
      </div>

      {showCreatePost && <CreatePostModal onClose={() => setShowCreatePost(false)} />}
    </div>
  );
};

const CreatePostModal = ({ onClose }) => {
  const [content, setContent] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [price, setPrice] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = React.useRef(null);

  // Usuários free e fan não podem criar conteúdo premium (apenas grátis)
  const isFreeUser = user?.role === 'free' || user?.role === 'fan';

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Limite de 10 arquivos
    const newFiles = files.slice(0, 10 - mediaFiles.length);
    
    // Criar previews
    const newPreviews = newFiles.map(file => ({
      url: URL.createObjectURL(file),
      type: file.type.startsWith('video/') ? 'video' : 'image',
      name: file.name
    }));

    setMediaFiles(prev => [...prev, ...newFiles]);
    setMediaPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeMedia = (index) => {
    URL.revokeObjectURL(mediaPreviews[index].url);
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
    setMediaPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(`${API}/upload`, formData, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(progress);
      }
    });

    return response.data.url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que tem mídia
    if (mediaFiles.length === 0) {
      toast.error('Adicione pelo menos uma foto ou vídeo!');
      return;
    }

    // Verificar conteúdo premium para usuários free
    if (isFreeUser && isPremium) {
      toast.error('Usuários free só podem criar conteúdo gratuito');
      return;
    }

    setUploading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Upload de todas as mídias
      const uploadedUrls = [];
      for (let i = 0; i < mediaFiles.length; i++) {
        setUploadProgress(Math.round((i / mediaFiles.length) * 100));
        const url = await uploadFile(mediaFiles[i]);
        uploadedUrls.push(url);
      }

      // Determinar tipo de mídia
      const firstFileType = mediaFiles[0].type.startsWith('video/') ? 'video' : 'image';

      // Criar o post
      await axios.post(
        `${API}/posts`,
        {
          content: content || '',
          media_type: firstFileType,
          media_url: uploadedUrls[0],
          media_urls: uploadedUrls,
          gallery_images: uploadedUrls,
          is_premium: !isFreeUser && isPremium,
          price: !isFreeUser && isPremium ? parseFloat(price) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Post publicado com sucesso! 🎉');
      onClose();
      // Recarregar a página para mostrar o novo post
      window.location.reload();
    } catch (error) {
      console.error('Erro ao criar post:', error);
      toast.error(error.response?.data?.detail || 'Erro ao publicar post');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 max-w-lg w-full max-h-[90vh] overflow-y-auto rounded-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-[#1A1A1A] border-b border-white/10 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Nova Publicação</h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-white text-2xl"
            data-testid="close-post-modal"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Área de Upload */}
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-[#C79F5E]/40 hover:border-[#C79F5E] rounded-xl p-6 cursor-pointer transition-all text-center"
          >
            {mediaPreviews.length === 0 ? (
              <div className="space-y-2">
                <div className="text-5xl">📷</div>
                <p className="text-white font-medium">Clique para adicionar fotos ou vídeos</p>
                <p className="text-gray-400 text-sm">Suporta imagens e vídeos (máx. 10 arquivos)</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {mediaPreviews.map((preview, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
                    {preview.type === 'video' ? (
                      <video src={preview.url} className="w-full h-full object-cover" />
                    ) : (
                      <img src={preview.url} alt="" className="w-full h-full object-cover" />
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeMedia(index); }}
                      className="absolute top-1 right-1 bg-red-500 text-white w-6 h-6 rounded-full text-sm flex items-center justify-center"
                    >
                      ✕
                    </button>
                    {preview.type === 'video' && (
                      <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                        🎬 Vídeo
                      </div>
                    )}
                  </div>
                ))}
                {mediaPreviews.length < 10 && (
                  <div className="aspect-square rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center text-3xl text-white/40">
                    +
                  </div>
                )}
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*,.heic,.heif"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            data-testid="media-file-input"
          />

          {/* Legenda */}
          <textarea
            data-testid="post-content-input"
            placeholder="Escreva uma legenda... (opcional)"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full bg-white/10 border border-white/20 focus:border-[#C79F5E] focus:ring-2 focus:ring-[#C79F5E]/50 rounded-xl px-4 py-3 text-white placeholder:text-gray-400 min-h-[80px] resize-none"
          />

          {/* Opções Premium - Apenas para criadores */}
          {!isFreeUser && (
            <div className="bg-white/5 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <input
                  data-testid="post-premium-checkbox"
                  type="checkbox"
                  id="premium-check"
                  checked={isPremium}
                  onChange={(e) => setIsPremium(e.target.checked)}
                  className="w-5 h-5 rounded accent-[#C79F5E]"
                />
                <label htmlFor="premium-check" className="text-white font-medium cursor-pointer">
                  💎 Conteúdo Premium (pago)
                </label>
              </div>
              
              {isPremium && (
                <input
                  data-testid="post-price-input"
                  type="number"
                  step="0.01"
                  min="1"
                  placeholder="Valor em R$"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 focus:border-[#C79F5E] rounded-lg px-4 py-2 text-white"
                  required={isPremium}
                />
              )}
            </div>
          )}

          {/* Indicador Free */}
          {isFreeUser && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 flex items-center gap-2">
              <span className="text-green-400">✓</span>
              <span className="text-green-400 text-sm">Publicação gratuita - sem limites!</span>
            </div>
          )}

          {/* Barra de Progresso */}
          {uploading && (
            <div className="space-y-2">
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-center text-sm text-gray-400">Enviando... {uploadProgress}%</p>
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-full font-semibold transition-all disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              data-testid="submit-post-btn"
              type="submit"
              disabled={uploading || mediaFiles.length === 0}
              className="flex-1 bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] hover:shadow-[0_0_20px_rgba(199,159,94,0.4)] text-black py-3 rounded-full font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Publicando...' : 'Publicar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreatorSetup = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [bio, setBio] = useState('');
  const [profileLink, setProfileLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [acceptedCreatorTerms, setAcceptedCreatorTerms] = useState(false);

  const categoryOptions = [
    { id: 'casais', label: '💑 Casais' },
    { id: 'mulheres', label: '👩 Mulheres' },
    { id: 'homens', label: '👨 Homens' },
    { id: 'swing', label: '🔥 Swing' },
    { id: 'fetiche', label: '⛓️ Fetiche' },
    { id: 'lgbtqia', label: '🏳️‍🌈 LGBTQIA+' },
    { id: 'voyeur', label: '👀 Voyeur' },
    { id: 'amador', label: '📱 Amador' }
  ];

  const toggleInterest = (id) => {
    setSelectedInterests(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (user) {
      // Gerar link baseado no nome do usuário
      const username = user.name.toLowerCase().replace(/\s+/g, '');
      setProfileLink(`lyber.com.br/@${username}`);
      
      // Load existing images if any
      if (user.picture) {
        setAvatarPreview(`${BACKEND_URL}${user.picture}`);
      }
      if (user.cover_image) {
        setCoverPreview(`${BACKEND_URL}${user.cover_image}`);
      }
    }
  }, [user]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`https://${profileLink}`);
    setCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Imagem muito grande! Máximo 5MB');
        return;
      }
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Imagem muito grande! Máximo 10MB');
        return;
      }
      setCoverFile(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return;
    
    const formData = new FormData();
    formData.append('file', avatarFile);
    
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API}/upload/avatar`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  };

  const uploadCover = async () => {
    if (!coverFile) return;
    
    const formData = new FormData();
    formData.append('file', coverFile);
    
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API}/upload/cover`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  };

  const handleComplete = async () => {
    // Validar aceite dos termos de criador
    if (!acceptedCreatorTerms) {
      toast.error('Você precisa aceitar os Termos do Programa Membro Fundador e a Política de Pagamentos');
      return;
    }
    
    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      
      // Upload images first
      if (avatarFile) {
        await uploadAvatar();
        toast.success('Avatar atualizado!');
      }
      
      if (coverFile) {
        await uploadCover();
        toast.success('Capa atualizada!');
      }
      
      // Update bio, category and interests
      const updateData = {
        accepted_creator_terms: true,
        accepted_creator_terms_version: '1.0.0'
      };
      if (bio) updateData.bio = bio;
      if (selectedCategory) updateData.category = selectedCategory;
      if (selectedInterests.length > 0) updateData.interests = selectedInterests;
      
      await axios.put(
        `${API}/users/me`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Registrar aceite dos termos no backend
      try {
        await axios.post(
          `${API}/legal/accept-multiple`,
          ['termos_membro_fundador', 'politica_pagamentos'],
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.log('Erro ao registrar aceite de termos:', err);
      }
      
      // Refresh user data
      await checkAuth();
      
      toast.success('Perfil configurado com sucesso!');
      // Redirecionar para verificação KYC
      navigate('/verification');
    } catch (error) {
      console.error('Setup error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar perfil');
    } finally {
      setUploading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <div className="max-w-2xl mx-auto py-12 px-4">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Passo {step} de 2</span>
            <span className="text-sm text-gray-400">{step * 50}%</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] transition-all duration-500"
              style={{ width: `${step * 50}%` }}
            />
          </div>
        </div>

        {step === 1 ? (
          <div data-testid="creator-setup-step1" className="space-y-8">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">🎉</div>
              <h1 className="text-4xl font-bold mb-4">Bem-vindo, Criador!</h1>
              <p className="text-xl text-gray-400">Seu link de divulgação foi gerado</p>
            </div>

            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-8 rounded-2xl">
              <label className="text-sm text-gray-400 mb-3 block">Seu Link Personalizado</label>
              <div className="flex gap-3">
                <div className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-4 flex items-center gap-3">
                  <span className="text-2xl">🔗</span>
                  <span className="text-lg font-mono">{profileLink}</span>
                </div>
                <button
                  onClick={handleCopyLink}
                  className="bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] hover:from-red-700 hover:to-pink-700 text-white px-8 py-4 rounded-lg font-semibold transition-all"
                >
                  {copied ? '✓ Copiado' : 'Copiar'}
                </button>
              </div>
              <p className="text-sm text-gray-400 mt-3">
                Compartilhe este link nas suas redes sociais para que seus fãs possam te encontrar!
              </p>
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] hover:from-red-700 hover:to-pink-700 text-white py-4 rounded-full font-bold text-lg transition-all shadow-lg hover:scale-105"
            >
              Continuar
            </button>
          </div>
        ) : (
          <div data-testid="creator-setup-step2" className="space-y-8">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">✨</div>
              <h1 className="text-4xl font-bold mb-4">Complete seu Perfil</h1>
              <p className="text-xl text-gray-400">Personalize seu perfil e conte sobre você</p>
            </div>

            {/* Cover Image Upload */}
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl">
              <label className="text-sm text-gray-400 mb-3 block">Imagem de Capa</label>
              <div className="relative">
                <div className="w-full h-48 rounded-xl overflow-hidden bg-gradient-to-br from-[#4D1A4A]/40 to-[#4B0000]/40 border-2 border-dashed border-white/20 hover:border-[#4D1A4A]/50 transition-all cursor-pointer group">
                  {coverPreview ? (
                    <img src={coverPreview} alt="Capa" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      <span className="text-4xl mb-2 group-hover:scale-110 transition-transform">🖼️</span>
                      <p className="text-gray-400 text-sm">Clique para adicionar imagem de capa</p>
                      <p className="text-gray-500 text-xs mt-1">Recomendado: 1920x400px (Máx: 20MB)</p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    onChange={handleCoverChange}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Avatar Upload */}
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl">
              <label className="text-sm text-gray-400 mb-3 block">Foto de Perfil (Avatar)</label>
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] border-4 border-white/10 cursor-pointer hover:border-[#4D1A4A]/50 transition-all group">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-5xl group-hover:scale-110 transition-transform">👤</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*,.heic,.heif"
                      onChange={handleAvatarChange}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-white font-semibold mb-1">Adicione sua foto</p>
                  <p className="text-sm text-gray-400 mb-3">Recomendado: 500x500px (Máx: 10MB)</p>
                  <button
                    onClick={() => document.querySelector('input[type="file"][accept="image/*,.heic,.heif"]').click()}
                    className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                  >
                    Escolher Arquivo
                  </button>
                </div>
              </div>
            </div>

            {/* Bio */}
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-3 block">Nome do Criador</label>
                <input
                  type="text"
                  value={user.name}
                  disabled
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-3 block">Biografia</label>
                <textarea
                  data-testid="creator-bio-input"
                  placeholder="Conte sobre você, seu conteúdo e o que seus fãs podem esperar..."
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  maxLength={500}
                  className="w-full bg-white/10 border border-white/20 focus:border-[#C79F5E] focus:ring-2 focus:ring-[#C79F5E]/50 rounded-lg px-4 py-3 text-white placeholder:text-gray-300 font-medium min-h-[150px] resize-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-2">{bio.length}/500 caracteres</p>
              </div>
            </div>

            {/* Categoria Principal */}
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl">
              <label className="text-sm text-gray-400 mb-3 block">Categoria Principal *</label>
              <p className="text-xs text-gray-500 mb-4">Escolha a categoria que melhor representa seu conteúdo</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {categoryOptions.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                      selectedCategory === cat.id
                        ? 'bg-[#C79F5E]/20 border-[#C79F5E] text-white'
                        : 'bg-[#0D0D0D] border-[#1A1A1A] hover:border-[#C79F5E]/50 text-gray-400'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Interesses/Tags */}
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl">
              <label className="text-sm text-gray-400 mb-3 block">Tags Adicionais</label>
              <p className="text-xs text-gray-500 mb-4">Selecione outras tags que descrevem seu conteúdo (opcional)</p>
              <div className="flex flex-wrap gap-2">
                {categoryOptions.filter(c => c.id !== selectedCategory).map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleInterest(cat.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      selectedInterests.includes(cat.id)
                        ? 'bg-[#C79F5E] text-black'
                        : 'bg-[#0D0D0D] border border-[#C79F5E]/30 text-gray-400 hover:text-white'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              {selectedInterests.length > 0 && (
                <p className="text-xs text-[#C79F5E] mt-3">{selectedInterests.length} tag(s) selecionada(s)</p>
              )}
            </div>

              <div className="bg-[#6B2D5C]/10 border border-[#4D1A4A]/30 rounded-lg p-4">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <span>💡</span> Dica
                </h3>
                <p className="text-sm text-gray-300">
                  Uma boa biografia e fotos de perfil aumentam em 70% as chances de ganhar novos assinantes. Seja autêntico e mostre sua personalidade!
                </p>
              </div>

            {/* Checkbox de Termos de Criador - OBRIGATÓRIO */}
            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedCreatorTerms}
                  onChange={(e) => setAcceptedCreatorTerms(e.target.checked)}
                  className="mt-0.5 w-5 h-5 rounded border-2 border-[#C79F5E]/50 bg-transparent checked:bg-[#C79F5E] checked:border-[#C79F5E] focus:ring-[#C79F5E] focus:ring-offset-0 cursor-pointer"
                />
                <span className="text-sm text-gray-400 leading-relaxed">
                  Li e concordo com os{' '}
                  <a href="/legal/membro-fundador" target="_blank" rel="noopener noreferrer" className="text-[#C79F5E] hover:underline font-semibold">
                    Termos do Programa Membro Fundador Lyberhot
                  </a>
                  {' '}e com a{' '}
                  <a href="/legal/pagamentos" target="_blank" rel="noopener noreferrer" className="text-[#C79F5E] hover:underline font-semibold">
                    Política de Pagamentos e Afiliados
                  </a>.
                </span>
              </label>
              {!acceptedCreatorTerms && (
                <p className="text-xs text-red-400 mt-2 ml-8">
                  ⚠️ Aceite os termos para continuar
                </p>
              )}
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setStep(1)}
                disabled={uploading}
                className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white py-4 rounded-full font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Voltar
              </button>
              <button
                data-testid="complete-setup-btn"
                onClick={handleComplete}
                disabled={uploading || !acceptedCreatorTerms}
                className={`flex-1 py-4 rounded-full font-bold text-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
                  acceptedCreatorTerms
                    ? 'bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] hover:from-red-700 hover:to-pink-700 text-white hover:scale-105'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                {uploading ? 'Salvando...' : 'Finalizar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const VerificationKYC = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [idType, setIdType] = useState('rg');
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [idFrontPreview, setIdFrontPreview] = useState(null);
  const [idBackPreview, setIdBackPreview] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Arquivo muito grande! Máximo 20MB para imagens, 1GB para vídeos');
        return;
      }
      
      const preview = URL.createObjectURL(file);
      
      if (type === 'front') {
        setIdFront(file);
        setIdFrontPreview(preview);
      } else if (type === 'back') {
        setIdBack(file);
        setIdBackPreview(preview);
      } else if (type === 'selfie') {
        setSelfie(file);
        setSelfiePreview(preview);
      }
    }
  };

  const handleSubmit = async () => {
    if (!idFront || !selfie) {
      toast.error('Por favor, envie todos os documentos obrigatórios');
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('id_type', idType);
      formData.append('id_front', idFront);
      if (idBack) formData.append('id_back', idBack);
      formData.append('selfie', selfie);

      const token = localStorage.getItem('token');
      await axios.post(`${API}/verification/submit`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Documentos enviados! Aguarde a análise.');
      await checkAuth();
      navigate('/dashboard');
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar documentos');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    toast.info('Você pode completar a verificação depois em Configurações');
    navigate('/dashboard');
  };

  if (!user || user.role !== 'creator') {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <div className="max-w-3xl mx-auto py-8 sm:py-12 px-4">
        <div className="text-center mb-6 sm:mb-8">
          <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">🔐</div>
          <h1 className="text-2xl sm:text-4xl font-bold mb-3 sm:mb-4">Verificação de Identidade</h1>
          <p className="text-base sm:text-xl text-gray-400 px-2">
            Para criar conteúdo pago, precisamos verificar sua identidade
          </p>
        </div>

        {step === 1 && (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-4 sm:p-8 rounded-2xl">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Tipo de Documento</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <button
                  onClick={() => setIdType('rg')}
                  className={`p-4 sm:p-6 rounded-xl border-2 transition-all ${
                    idType === 'rg'
                      ? 'border-[#C79F5E] bg-[#C79F5E]/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="text-3xl sm:text-4xl mb-2">📄</div>
                  <div className="font-semibold">RG</div>
                  <div className="text-xs sm:text-sm text-gray-400">Carteira de Identidade</div>
                </button>
                <button
                  onClick={() => setIdType('cnh')}
                  className={`p-4 sm:p-6 rounded-xl border-2 transition-all ${
                    idType === 'cnh'
                      ? 'border-[#C79F5E] bg-[#C79F5E]/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="text-3xl sm:text-4xl mb-2">🚗</div>
                  <div className="font-semibold">CNH</div>
                  <div className="text-xs sm:text-sm text-gray-400">Carteira de Motorista</div>
                </button>
                <button
                  onClick={() => setIdType('passport')}
                  className={`p-4 sm:p-6 rounded-xl border-2 transition-all ${
                    idType === 'passport'
                      ? 'border-[#C79F5E] bg-[#C79F5E]/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="text-3xl sm:text-4xl mb-2">🛂</div>
                  <div className="font-semibold">Passaporte</div>
                  <div className="text-xs sm:text-sm text-gray-400">Documento Internacional</div>
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSkip}
                className="flex-1 glass hover:bg-white/10 text-white py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-base"
              >
                Fazer Depois
              </button>
              <button
                onClick={() => setStep(2)}
                className="flex-1 bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] hover:from-[#6B2D5C] hover:to-[#C79F5E] text-white py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-base"
              >
                Continuar
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-4 sm:p-8 rounded-2xl space-y-4 sm:space-y-6">
              <div>
                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Frente do Documento *</h3>
                <div className="border-2 border-dashed border-white/20 rounded-xl p-6 sm:p-8 text-center hover:border-[#C79F5E] transition-all cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    onChange={(e) => handleFileChange(e, 'front')}
                    className="hidden"
                    id="id-front"
                  />
                  <label htmlFor="id-front" className="cursor-pointer">
                    {idFrontPreview ? (
                      <img src={idFrontPreview} alt="Frente" className="max-h-40 sm:max-h-48 mx-auto rounded-lg" />
                    ) : (
                      <>
                        <div className="text-3xl sm:text-4xl mb-2">📸</div>
                        <div className="text-gray-400 text-sm sm:text-base">Clique para enviar</div>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {idType !== 'passport' && (
                <div>
                  <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Verso do Documento</h3>
                  <div className="border-2 border-dashed border-white/20 rounded-xl p-6 sm:p-8 text-center hover:border-[#C79F5E] transition-all cursor-pointer">
                    <input
                      type="file"
                      accept="image/*,.heic,.heif"
                      onChange={(e) => handleFileChange(e, 'back')}
                      className="hidden"
                      id="id-back"
                    />
                    <label htmlFor="id-back" className="cursor-pointer">
                      {idBackPreview ? (
                        <img src={idBackPreview} alt="Verso" className="max-h-40 sm:max-h-48 mx-auto rounded-lg" />
                      ) : (
                        <>
                          <div className="text-3xl sm:text-4xl mb-2">📸</div>
                          <div className="text-gray-400 text-sm sm:text-base">Clique para enviar</div>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 glass hover:bg-white/10 text-white py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-base"
              >
                Voltar
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!idFront}
                className="flex-1 bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] hover:from-[#6B2D5C] hover:to-[#C79F5E] text-white py-3 sm:py-4 rounded-full font-semibold disabled:opacity-50 text-sm sm:text-base"
              >
                Continuar
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-4 sm:p-8 rounded-2xl">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Selfie *</h2>
              <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">
                Tire uma selfie segurando seu documento ao lado do rosto
              </p>
              <div className="border-2 border-dashed border-white/20 rounded-xl p-6 sm:p-8 text-center hover:border-[#C79F5E] transition-all cursor-pointer">
                <input
                  type="file"
                  accept="image/*,.heic,.heif"
                  capture="user"
                  onChange={(e) => handleFileChange(e, 'selfie')}
                  className="hidden"
                  id="selfie"
                />
                <label htmlFor="selfie" className="cursor-pointer">
                  {selfiePreview ? (
                    <img src={selfiePreview} alt="Selfie" className="max-h-48 sm:max-h-64 mx-auto rounded-lg" />
                  ) : (
                    <>
                      <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">🤳</div>
                      <div className="text-gray-400 text-sm sm:text-lg">Clique para tirar selfie</div>
                    </>
                  )}
                </label>
              </div>
            </div>

            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-4 sm:p-6 rounded-xl bg-blue-500/10 border border-blue-500/20">
              <div className="flex gap-3">
                <div className="text-xl sm:text-2xl">ℹ️</div>
                <div className="text-xs sm:text-sm text-gray-300">
                  <strong>Dicas para uma boa selfie:</strong>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Boa iluminação</li>
                    <li>Rosto visível e nítido</li>
                    <li>Documento legível ao lado do rosto</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(2)}
                className="flex-1 glass hover:bg-white/10 text-white py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-base"
              >
                Voltar
              </button>
              <button
                onClick={handleSubmit}
                disabled={!selfie || submitting}
                className="flex-1 bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] hover:from-[#6B2D5C] hover:to-[#C79F5E] text-white py-3 sm:py-4 rounded-full font-semibold disabled:opacity-50 text-sm sm:text-base"
              >
                {submitting ? 'Enviando...' : 'Enviar Verificação'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminModeration = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [flags, setFlags] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'pending', 'all', 'auto_blocked'
  const [selectedFlag, setSelectedFlag] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/feed');
      return;
    }
    loadData();
  }, [user, filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Carregar flags
      const flagsResponse = await axios.get(
        `${API}/admin/moderation/flags${filter !== 'all' ? `?status=${filter}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFlags(flagsResponse.data);

      // Carregar estatísticas
      const statsResponse = await axios.get(
        `${API}/admin/moderation/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error loading moderation data:', error);
      toast.error('Erro ao carregar dados de moderação');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (flagId, action, note = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/moderation/flags/${flagId}/review?action=${action}${note ? `&note=${encodeURIComponent(note)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(action === 'approve' ? 'Conteúdo aprovado!' : 'Conteúdo rejeitado!');
      setSelectedFlag(null);
      loadData();
    } catch (error) {
      console.error('Error reviewing flag:', error);
      toast.error('Erro ao revisar conteúdo');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-600';
      case 'medium': return 'bg-yellow-600';
      case 'low': return 'bg-green-600';
      default: return 'bg-gray-600';
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'sexual': '🔞 Sexual',
      'hate': '😡 Ódio',
      'harassment': '😠 Assédio',
      'self-harm': '⚠️ Auto-lesão',
      'sexual/minors': '🚫 Menores',
      'hate/threatening': '💢 Ameaças de Ódio',
      'violence/graphic': '🩸 Violência Gráfica',
      'violence': '⚔️ Violência',
      'self-harm/intent': '🆘 Intenção Auto-lesão',
      'self-harm/instructions': '📋 Instruções Auto-lesão',
      'harassment/threatening': '🔪 Ameaças'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white">
        <Navbar NotificationsPanel={NotificationsPanel} />
        <div className="max-w-7xl mx-auto py-8 px-4">
          <p className="text-gray-400">Carregando moderação...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <Navbar NotificationsPanel={NotificationsPanel} />
      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="text-[#C79F5E] hover:text-[#FFA500] mb-4 flex items-center gap-2"
          >
            ← Voltar para Dashboard
          </button>
          <h1 className="text-4xl font-bold mb-2 text-gradient">
            🛡️ Moderação de Conteúdo
          </h1>
          <p className="text-gray-400">Sistema automático de moderação com IA</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl">
              <p className="text-sm text-gray-400 mb-1">Total Flags</p>
              <p className="text-3xl font-bold text-[#C79F5E]">{stats.total_flags}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl">
              <p className="text-sm text-gray-400 mb-1">Pendentes</p>
              <p className="text-3xl font-bold text-yellow-500">{stats.pending}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl">
              <p className="text-sm text-gray-400 mb-1">Bloqueados</p>
              <p className="text-3xl font-bold text-red-500">{stats.auto_blocked}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl">
              <p className="text-sm text-gray-400 mb-1">Aprovados</p>
              <p className="text-3xl font-bold text-green-500">{stats.approved}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl">
              <p className="text-sm text-gray-400 mb-1">Rejeitados</p>
              <p className="text-3xl font-bold text-gray-500">{stats.rejected}</p>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="flex gap-2 mb-6">
          {['pending', 'auto_blocked', 'all'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === f
                  ? 'bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              {f === 'pending' && '⏳ Pendentes'}
              {f === 'auto_blocked' && '🚫 Bloqueados'}
              {f === 'all' && '📋 Todos'}
            </button>
          ))}
        </div>

        {/* Lista de Flags */}
        <div className="space-y-4">
          {flags.length === 0 ? (
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-8 rounded-2xl text-center">
              <p className="text-gray-400">Nenhum conteúdo flagado</p>
            </div>
          ) : (
            flags.map((flag) => (
              <div key={flag.id} className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    {/* Avatar do usuário */}
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-[#4D1A4A] to-[#6B2D5C]">
                      {flag.user?.picture ? (
                        <img src={flag.user.picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xl">
                          {flag.user?.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="font-bold">{flag.user?.name || 'Usuário Desconhecido'}</p>
                      <p className="text-sm text-gray-400">{flag.user?.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-1 rounded-full ${getSeverityColor(flag.severity)}`}>
                          {flag.severity.toUpperCase()}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-white/10">
                          {flag.content_type.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(flag.flagged_at).toLocaleString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Status Badge */}
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    flag.status === 'pending' ? 'bg-yellow-600' :
                    flag.status === 'auto_blocked' ? 'bg-red-600' :
                    flag.status === 'approved' ? 'bg-green-600' :
                    'bg-gray-600'
                  }`}>
                    {flag.status === 'pending' && '⏳ Pendente'}
                    {flag.status === 'auto_blocked' && '🚫 Bloqueado'}
                    {flag.status === 'approved' && '✅ Aprovado'}
                    {flag.status === 'rejected' && '❌ Rejeitado'}
                  </span>
                </div>

                {/* Categorias Flagadas */}
                <div className="mb-4">
                  <p className="text-sm text-gray-400 mb-2">Categorias detectadas:</p>
                  <div className="flex flex-wrap gap-2">
                    {flag.categories?.categories && Object.entries(flag.categories.categories)
                      .filter(([_, value]) => value === true)
                      .map(([category, _]) => (
                        <span key={category} className="px-3 py-1 bg-red-600/20 text-red-400 rounded-full text-sm">
                          {getCategoryLabel(category)}
                        </span>
                      ))}
                  </div>
                </div>

                {/* Conteúdo */}
                {flag.content && (
                  <div className="bg-black/30 p-4 rounded-xl mb-4">
                    <p className="text-sm text-gray-400 mb-2">Conteúdo:</p>
                    <p className="text-white">{flag.content.content || flag.content.text || 'Sem texto'}</p>
                  </div>
                )}

                {/* Ações */}
                {flag.status === 'pending' || flag.status === 'auto_blocked' ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleReview(flag.id, 'approve')}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-semibold transition-all"
                    >
                      ✅ Aprovar
                    </button>
                    <button
                      onClick={() => handleReview(flag.id, 'reject')}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg font-semibold transition-all"
                    >
                      ❌ Rejeitar e Deletar
                    </button>
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">
                    Revisado por {flag.reviewed_by} em {new Date(flag.reviewed_at).toLocaleString('pt-BR')}
                    {flag.review_note && <p className="mt-1">Nota: {flag.review_note}</p>}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const AdminControlRoom = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, financialRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/stats`, { headers }),
        axios.get(`${API}/admin/dashboard/financial`, { headers })
      ]);
      
      setStats(statsRes.data);
      setFinancial(financialRes.data);
    } catch (error) {
      console.error('Dashboard error:', error);
      if (error.response?.status === 403) {
        toast.error('Acesso restrito a administradores');
        navigate('/dashboard');
      } else {
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#C79F5E] mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando Sala de Controle...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <Navbar NotificationsPanel={NotificationsPanel} />
      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-[#C79F5E]">
                🎛️ Sala de Controle LYBERHOT
              </h1>
              <p className="text-gray-400 mt-2">Dashboard administrativo e gestão da plataforma</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/admin/candidatos')}
                className="bg-gradient-to-r from-[#D4AF37] to-[#8B7355] hover:shadow-lg hover:shadow-[#D4AF37]/30 text-black px-6 py-3 rounded-full font-semibold transition-all"
              >
                📋 Triagem de Candidatos
              </button>
              <button
                onClick={() => navigate('/admin/creators')}
                className="bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] hover:shadow-lg text-black px-6 py-3 rounded-full font-semibold transition-all"
              >
                ➕ Criar Criador
              </button>
              <button
                onClick={() => navigate('/admin/moderation')}
                className="bg-[#4D1A4A]/30 hover:bg-[#4D1A4A]/50 border border-[#4D1A4A] text-white px-6 py-3 rounded-full font-semibold transition-all"
              >
                🛡️ Moderação
              </button>
              <button
                onClick={() => navigate('/admin/verifications')}
                className="bg-[#C79F5E]/20 hover:bg-[#C79F5E]/30 border border-[#C79F5E]/50 text-[#C79F5E] px-6 py-3 rounded-full font-semibold transition-all"
              >
                📋 Verificações KYC
              </button>
              <button
                onClick={() => navigate('/admin/withdrawals')}
                className="bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 text-green-400 px-6 py-3 rounded-full font-semibold transition-all"
              >
                💸 Gestão de Saques
              </button>
              <button
                onClick={() => navigate('/admin/masters')}
                className="bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 text-purple-400 px-6 py-3 rounded-full font-semibold transition-all"
              >
                👑 Usuários Master
              </button>
              <button
                onClick={() => navigate('/admin/banners')}
                data-testid="admin-banners-link"
                className="bg-pink-500/20 hover:bg-pink-500/30 border border-pink-500/50 text-pink-400 px-6 py-3 rounded-full font-semibold transition-all"
              >
                📋 Banners & Eventos
              </button>
            </div>
          </div>
        </div>

        {stats && financial && (
          <div className="space-y-6">
            {/* Financeiro */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">💰 Gestão Financeira</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Receita Total */}
                <div className="bg-[#1A1A1A] border border-[#C79F5E]/30 p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Receita Total</span>
                    <span className="text-2xl">💵</span>
                  </div>
                  <div className="text-4xl font-bold text-[#C79F5E]">
                    R$ {financial.summary.total_revenue.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    {stats.transactions.successful} transações
                  </div>
                </div>

                {/* Receita LYBERHOT (15%) */}
                <div className="bg-[#1A1A1A] border border-[#4D1A4A]/50 p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Receita LYBERHOT (15%)</span>
                    <span className="text-2xl">🏢</span>
                  </div>
                  <div className="text-4xl font-bold text-[#C79F5E]">
                    R$ {financial.summary.platform_revenue.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Comissão da plataforma
                  </div>
                </div>

                {/* Receita Criadores (85%) */}
                <div className="bg-[#1A1A1A] border border-green-500/30 p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Receita Criadores (85%)</span>
                    <span className="text-2xl">👨‍🎨</span>
                  </div>
                  <div className="text-4xl font-bold text-green-400">
                    R$ {financial.summary.creators_revenue.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Pagamentos aos criadores
                  </div>
                </div>
              </div>

              {/* Top Criadores */}
              {financial.top_creators.length > 0 && (
                <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-xl mb-6">
                  <h3 className="text-xl font-bold mb-4 text-[#C79F5E]">🏆 Top 10 Criadores por Receita</h3>
                  <div className="space-y-3">
                    {financial.top_creators.map((creator, index) => (
                      <div key={creator.id} className="flex items-center justify-between p-4 bg-[#0D0D0D] border border-[#C79F5E]/10 rounded-lg hover:border-[#C79F5E]/30 transition-all">
                        <div className="flex items-center gap-4">
                          <div className="text-2xl font-bold text-[#C79F5E]">#{index + 1}</div>
                          <div>
                            <div className="font-semibold">{creator.name}</div>
                            <div className="text-sm text-gray-400">{creator.email}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-green-400">R$ {creator.revenue.toFixed(2)}</div>
                          <div className="text-sm text-gray-400">{creator.subscribers} assinantes</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transações Recentes */}
              {financial.recent_transactions.length > 0 && (
                <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-xl">
                  <h3 className="text-xl font-bold mb-4">📜 Transações Recentes</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4 text-gray-400 font-semibold">Data</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-semibold">Usuário</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-semibold">Valor</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financial.recent_transactions.slice(0, 10).map((transaction, index) => (
                          <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 px-4 text-sm">
                              {transaction.created_at ? new Date(transaction.created_at).toLocaleString('pt-BR') : 'N/A'}
                            </td>
                            <td className="py-3 px-4 text-sm">
                              <div>{transaction.user_name || 'N/A'}</div>
                              <div className="text-xs text-gray-500">{transaction.user_email}</div>
                            </td>
                            <td className="py-3 px-4 text-sm font-semibold">
                              R$ {transaction.amount?.toFixed(2) || '0.00'}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                transaction.status === 'paid' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-red-500/20 text-red-400'
                              }`}>
                                {transaction.status === 'paid' ? 'Pago' : 'Pendente'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Métricas Principais */}
            <div>
              <h2 className="text-2xl font-bold mb-4">📊 Métricas da Plataforma</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Total de Usuários */}
                <button
                  onClick={() => navigate('/admin/users')}
                  className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-xl hover:scale-105 transition-all text-left w-full"
                >
                  <div className="text-4xl mb-2">👥</div>
                  <div className="text-3xl font-bold text-[#C79F5E]">{stats.users.total}</div>
                  <div className="text-gray-400 text-sm">Usuários Totais</div>
                  <div className="mt-3 text-xs text-gray-500">
                    {stats.users.creators} criadores • {stats.users.fans} fãs
                  </div>
                  <div className="mt-3 text-xs text-blue-400">→ Clique para gerenciar</div>
                </button>

                {/* Criadores */}
                <button
                  onClick={() => navigate('/admin/creators')}
                  className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-xl hover:scale-105 transition-all text-left w-full"
                >
                  <div className="text-4xl mb-2">⭐</div>
                  <div className="text-3xl font-bold text-[#6B2D5C]">{stats.users.creators}</div>
                  <div className="text-gray-400 text-sm">Criadores</div>
                  <div className="mt-3 text-xs text-gray-500">
                    {stats.verifications.approved} verificados
                  </div>
                  <div className="mt-3 text-xs text-blue-400">→ Clique para detalhes</div>
                </button>

                {/* Assinaturas Ativas */}
                <button
                  onClick={() => navigate('/admin/subscriptions')}
                  className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-xl hover:scale-105 transition-all text-left w-full"
                >
                  <div className="text-4xl mb-2">💳</div>
                  <div className="text-3xl font-bold text-green-400">{stats.subscriptions.active}</div>
                  <div className="text-gray-400 text-sm">Assinaturas Ativas</div>
                  <div className="mt-3 text-xs text-gray-500">
                    de {stats.subscriptions.total} totais
                  </div>
                  <div className="mt-3 text-xs text-blue-400">→ Clique para ver lista</div>
                </button>

                {/* Conteúdo */}
                <button
                  onClick={() => navigate('/admin/content')}
                  className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-xl hover:scale-105 transition-all text-left w-full"
                >
                  <div className="text-4xl mb-2">📝</div>
                  <div className="text-3xl font-bold text-purple-400">{stats.content.total_posts}</div>
                  <div className="text-gray-400 text-sm">Posts Criados</div>
                  <div className="mt-3 text-xs text-gray-500">
                    {stats.content.premium_posts} premium
                  </div>
                  <div className="mt-3 text-xs text-blue-400">→ Clique para moderar</div>
                </button>
              </div>
            </div>

            {/* Ações Rápidas */}
            <div>
              <h2 className="text-2xl font-bold mb-4">⚡ Ações Rápidas</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <button
                  onClick={() => navigate('/admin/creators')}
                  className="bg-gradient-to-r from-[#4D1A4A]/30 to-[#C79F5E]/30 border border-[#C79F5E]/50 p-6 rounded-xl hover:scale-105 transition-all text-left"
                >
                  <div className="text-3xl mb-2">➕</div>
                  <div className="font-bold mb-1 text-[#C79F5E]">Criar Criador</div>
                  <div className="text-sm text-gray-400">Novo perfil gerenciado</div>
                </button>

                <button
                  onClick={() => navigate('/admin/verifications')}
                  className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-xl hover:bg-white/10 transition-all text-left"
                >
                  <div className="text-3xl mb-2">📋</div>
                  <div className="font-bold mb-1">Verificações KYC</div>
                  <div className="text-sm text-gray-400">{stats.verifications.pending} pendentes</div>
                </button>

                <button
                  onClick={() => window.location.href = '/admin/content'}
                  className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-xl hover:bg-white/10 transition-all text-left"
                >
                  <div className="text-3xl mb-2">🔍</div>
                  <div className="font-bold mb-1">Moderar Conteúdo</div>
                  <div className="text-sm text-gray-400">Ver todo conteúdo</div>
                </button>

                <button
                  onClick={() => window.location.href = '/admin/users'}
                  className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-xl hover:bg-white/10 transition-all text-left"
                >
                  <div className="text-3xl mb-2">👥</div>
                  <div className="font-bold mb-1">Gerenciar Usuários</div>
                  <div className="text-sm text-gray-400">{stats.users.total} usuários</div>
                </button>

                <button
                  onClick={() => navigate('/admin/banners')}
                  data-testid="admin-banners-quick"
                  className="bg-gradient-to-r from-pink-500/20 to-fuchsia-500/20 border border-pink-500/40 p-6 rounded-xl hover:scale-105 transition-all text-left"
                >
                  <div className="text-3xl mb-2">🎉</div>
                  <div className="font-bold mb-1 text-pink-400">Banners & Eventos</div>
                  <div className="text-sm text-gray-400">Criar banners e festas</div>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Seção de Padrinhos/Madrinhas Hot */}
        {user && (
          <div className="mt-8">
            <AdminGodparents adminId={user.id} />
          </div>
        )}
      </div>
    </div>
  );
};

const AdminUsers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, creator, fan

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadUsers();
  }, [user, filter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const roleFilter = filter !== 'all' ? `?role_filter=${filter}` : '';
      const response = await axios.get(`${API}/admin/users/all${roleFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Load users error:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUser = async (userId, name) => {
    const reason = prompt('Motivo do bloqueio:');
    if (!reason) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/users/${userId}/block`,
        { reason },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }}
      );
      toast.success(`${name} bloqueado`);
      loadUsers();
    } catch (error) {
      toast.error('Erro ao bloquear usuário');
    }
  };

  const handleUnblockUser = async (userId, name) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/${userId}/unblock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${name} desbloqueado`);
      loadUsers();
    } catch (error) {
      toast.error('Erro ao desbloquear usuário');
    }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!confirm(`Tem certeza que deseja deletar a conta de ${name}? Esta ação é irreversível!`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Conta de ${name} deletada`);
      loadUsers();
    } catch (error) {
      toast.error('Erro ao deletar conta');
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <Navbar NotificationsPanel={NotificationsPanel} />
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold">👥 Gerenciar Usuários</h1>
            <p className="text-gray-400 mt-2">Lista completa de todos os usuários da plataforma</p>
          </div>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="bg-[#1A1A1A] border border-[#C79F5E]/20 hover:bg-white/10 px-6 py-3 rounded-full font-semibold"
          >
            ← Voltar ao Dashboard
          </button>
        </div>

        {/* Filtros */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-6 py-2 rounded-full font-semibold transition-all ${
              filter === 'all'
                ? 'bg-gradient-to-r from-[#6B2D5C] to-[#C79F5E] text-white'
                : 'glass hover:bg-white/10'
            }`}
          >
            Todos ({users.length})
          </button>
          <button
            onClick={() => setFilter('creator')}
            className={`px-6 py-2 rounded-full font-semibold transition-all ${
              filter === 'creator'
                ? 'bg-gradient-to-r from-[#6B2D5C] to-[#C79F5E] text-white'
                : 'glass hover:bg-white/10'
            }`}
          >
            Criadores
          </button>
          <button
            onClick={() => setFilter('fan')}
            className={`px-6 py-2 rounded-full font-semibold transition-all ${
              filter === 'fan'
                ? 'bg-gradient-to-r from-[#6B2D5C] to-[#C79F5E] text-white'
                : 'glass hover:bg-white/10'
            }`}
          >
            Fãs
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C79F5E] mx-auto"></div>
          </div>
        ) : (
          <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="text-left py-4 px-6 font-semibold">Usuário</th>
                    <th className="text-left py-4 px-6 font-semibold">Email</th>
                    <th className="text-left py-4 px-6 font-semibold">Role</th>
                    <th className="text-left py-4 px-6 font-semibold">Status</th>
                    <th className="text-left py-4 px-6 font-semibold">Cadastro</th>
                    <th className="text-right py-4 px-6 font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((usr) => (
                    <tr key={usr.id} className="border-b border-white/5 hover:bg-white/5 transition-all">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#6B2D5C] to-[#C79F5E] flex items-center justify-center">
                            {usr.picture ? (
                              <img src={usr.picture} alt={usr.name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <span className="font-bold">{usr.name?.[0]?.toUpperCase()}</span>
                            )}
                          </div>
                          <div>
                            <div className="font-semibold">{usr.name}</div>
                            {usr.username && (
                              <div className="text-xs text-gray-500">@{usr.username}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-400">{usr.email}</td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          usr.role === 'creator' 
                            ? 'bg-[#6B2D5C]/20 text-[#6B2D5C]'
                            : usr.role === 'admin'
                            ? 'bg-[#C79F5E]/20 text-[#C79F5E]'
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {usr.role === 'creator' ? 'Criador' : usr.role === 'admin' ? 'Admin' : 'Fã'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col gap-1">
                          {usr.blocked && (
                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full text-xs">
                              🚫 Bloqueado
                            </span>
                          )}
                          {usr.role === 'creator' && usr.verification_status === 'approved' && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-xs">
                              ✓ Verificado
                            </span>
                          )}
                          {usr.role === 'creator' && usr.verification_status === 'pending' && (
                            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">
                              ⏳ Pendente
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-400">
                        {new Date(usr.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex gap-2 justify-end">
                          {usr.role === 'creator' && (
                            <button
                              onClick={() => navigate('/admin/creators')}
                              className="px-3 py-1 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 text-blue-400 rounded-lg text-xs font-semibold transition-all"
                            >
                              Ver Detalhes
                            </button>
                          )}
                          {!usr.blocked && usr.role !== 'admin' && (
                            <button
                              onClick={() => handleBlockUser(usr.id, usr.name)}
                              className="px-3 py-1 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/50 text-yellow-400 rounded-lg text-xs font-semibold transition-all"
                            >
                              Bloquear
                            </button>
                          )}
                          {usr.blocked && (
                            <button
                              onClick={() => handleUnblockUser(usr.id, usr.name)}
                              className="px-3 py-1 bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 text-green-400 rounded-lg text-xs font-semibold transition-all"
                            >
                              Desbloquear
                            </button>
                          )}
                          {usr.role !== 'admin' && (
                            <button
                              onClick={() => handleDeleteUser(usr.id, usr.name)}
                              className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 text-red-400 rounded-lg text-xs font-semibold transition-all"
                            >
                              Deletar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminCreators = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [managedCreators, setManagedCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [creatorDetails, setCreatorDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'managed'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingCreator, setEditingCreator] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  
  // Form states
  const [newCreator, setNewCreator] = useState({
    name: '',
    email: '',
    bio: '',
    category: 'outros',
    price_monthly: 19.99,
    price_quarterly: 49.99,
    price_annual: 149.99
  });

  // Helper para URL de imagem
  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    if (image.startsWith('/uploads')) {
      const token = localStorage.getItem('token');
      const baseUrl = `${BACKEND_URL}/api${image}`;
      // Adicionar token para posts premium
      if (image.includes('/posts/') && token) {
        return `${baseUrl}?token=${token}`;
      }
      return baseUrl;
    }
    if (image.startsWith('data:')) return image;
    return `data:image/jpeg;base64,${image}`;
  };
  
  const [newPost, setNewPost] = useState({
    content: '',
    media_urls: [],
    is_premium: false
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadCreators();
    loadManagedCreators();
  }, [user]);

  const loadCreators = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/creators/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreators(response.data.creators);
    } catch (error) {
      console.error('Load creators error:', error);
      toast.error('Erro ao carregar criadores');
    } finally {
      setLoading(false);
    }
  };

  const loadManagedCreators = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/creators/managed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setManagedCreators(response.data.creators || []);
    } catch (error) {
      console.error('Load managed creators error:', error);
    }
  };

  const loadCreatorDetails = async (creatorId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/creator/${creatorId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatorDetails(response.data);
    } catch (error) {
      console.error('Load creator details error:', error);
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleCreateCreator = async () => {
    if (!newCreator.name || !newCreator.email) {
      toast.error('Nome e email são obrigatórios');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/creators/create`, newCreator, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Criador criado com sucesso!');
      setShowCreateModal(false);
      setNewCreator({
        name: '',
        email: '',
        bio: '',
        category: 'outros',
        price_monthly: 19.99,
        price_quarterly: 49.99,
        price_annual: 149.99
      });
      loadCreators();
      loadManagedCreators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar criador');
    }
  };

  const handleEditCreator = async () => {
    if (!editingCreator) return;
    
    try {
      const token = localStorage.getItem('token');
      setUploadingMedia(true);
      
      // Upload avatar if selected
      if (avatarFile) {
        const formData = new FormData();
        formData.append('file', avatarFile);
        await axios.post(`${API}/admin/creators/${editingCreator.id}/upload-avatar`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Upload cover if selected
      if (coverFile) {
        const formData = new FormData();
        formData.append('file', coverFile);
        await axios.post(`${API}/admin/creators/${editingCreator.id}/upload-cover`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Update other fields
      const params = new URLSearchParams();
      if (editingCreator.name) params.append('name', editingCreator.name);
      if (editingCreator.bio !== undefined) params.append('bio', editingCreator.bio);
      if (editingCreator.category) params.append('category', editingCreator.category);
      if (editingCreator.price_monthly) params.append('price_monthly', editingCreator.price_monthly);
      if (editingCreator.price_quarterly) params.append('price_quarterly', editingCreator.price_quarterly);
      if (editingCreator.price_annual) params.append('price_annual', editingCreator.price_annual);
      
      await axios.put(`${API}/admin/creators/${editingCreator.id}?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Criador atualizado!');
      setShowEditModal(false);
      setEditingCreator(null);
      setAvatarFile(null);
      setCoverFile(null);
      setAvatarPreview(null);
      setCoverPreview(null);
      loadCreators();
      loadManagedCreators();
    } catch (error) {
      toast.error('Erro ao atualizar criador');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handlePostAsCreator = async () => {
    if (!selectedCreator || !newPost.content) {
      toast.error('Conteúdo é obrigatório');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/creators/${selectedCreator.id}/post`, newPost, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post criado com sucesso!');
      setShowPostModal(false);
      setNewPost({ content: '', media_urls: [], is_premium: false });
      loadCreators();
    } catch (error) {
      toast.error('Erro ao criar post');
    }
  };

  const handleBlockUser = async (userId, name) => {
    const reason = prompt('Motivo do bloqueio:');
    if (!reason) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/users/${userId}/block`,
        { reason },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }}
      );
      toast.success(`${name} bloqueado`);
      loadCreators();
      setSelectedCreator(null);
    } catch (error) {
      toast.error('Erro ao bloquear usuário');
    }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!confirm(`Tem certeza que deseja deletar a conta de ${name}? Esta ação é irreversível!`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Conta de ${name} deletada`);
      loadCreators();
      loadManagedCreators();
      setSelectedCreator(null);
    } catch (error) {
      toast.error('Erro ao deletar conta');
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white pb-20 md:pb-0">
      <Navbar NotificationsPanel={NotificationsPanel} />
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold">⭐ Criadores da Plataforma</h1>
            <p className="text-gray-400 mt-2">Gerencie e crie perfis de criadores</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black px-6 py-3 rounded-full font-semibold hover:shadow-lg transition-all"
            >
              ➕ Criar Criador
            </button>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="bg-[#1A1A1A] border border-[#C79F5E]/20 hover:bg-white/10 px-6 py-3 rounded-full font-semibold"
            >
              ← Voltar
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-full font-semibold transition-all ${
              activeTab === 'all' 
                ? 'bg-[#C79F5E] text-black' 
                : 'bg-[#1A1A1A] text-gray-400 hover:text-white'
            }`}
          >
            Todos os Criadores ({creators.length})
          </button>
          <button
            onClick={() => setActiveTab('managed')}
            className={`px-4 py-2 rounded-full font-semibold transition-all ${
              activeTab === 'managed' 
                ? 'bg-[#C79F5E] text-black' 
                : 'bg-[#1A1A1A] text-gray-400 hover:text-white'
            }`}
          >
            🔧 Gerenciados ({managedCreators.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C79F5E] mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {(activeTab === 'all' ? creators : managedCreators).map((creator) => (
              <div key={creator.id} className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-xl">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#6B2D5C] to-[#C79F5E] flex items-center justify-center text-2xl overflow-hidden">
                        {creator.picture ? (
                          <img src={getImageUrl(creator.picture)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          creator.name?.[0]?.toUpperCase()
                        )}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold flex items-center gap-2">
                          {creator.name}
                          {(creator.verified || creator.admin_managed || creator.created_by_admin) && (
                            <span className="text-[#C79F5E] text-sm">✓</span>
                          )}
                          {creator.admin_managed && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full text-xs">🔧 Admin</span>
                          )}
                        </h3>
                        <p className="text-gray-400 text-sm">{creator.email}</p>
                        <div className="flex gap-2 mt-1">
                          {creator.verification_status === 'approved' && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-xs">✓ Verificado</span>
                          )}
                          {creator.blocked && (
                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full text-xs">🚫 Bloqueado</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-white/5 p-3 rounded-lg">
                        <div className="text-2xl font-bold text-green-400">{creator.stats?.active_subscribers || creator.subscriber_count || 0}</div>
                        <div className="text-xs text-gray-400">Assinantes</div>
                      </div>
                      <div className="bg-white/5 p-3 rounded-lg">
                        <div className="text-2xl font-bold text-purple-400">{creator.stats?.total_posts || creator.post_count || 0}</div>
                        <div className="text-xs text-gray-400">Posts</div>
                      </div>
                      <div className="bg-white/5 p-3 rounded-lg">
                        <div className="text-2xl font-bold text-blue-400">R$ {creator.price_monthly?.toFixed(2) || '19.99'}</div>
                        <div className="text-xs text-gray-400">Mensal</div>
                      </div>
                      <div className="bg-white/5 p-3 rounded-lg">
                        <div className="text-2xl font-bold text-[#C79F5E]">R$ {(creator.stats?.creator_revenue || creator.total_revenue || 0).toFixed(2)}</div>
                        <div className="text-xs text-gray-400">Receita</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => {
                        setSelectedCreator(creator);
                        setShowPostModal(true);
                      }}
                      className="bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 text-green-400 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
                    >
                      📝 Postar
                    </button>
                    <button
                      onClick={() => {
                        setEditingCreator({...creator});
                        setShowEditModal(true);
                      }}
                      className="bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 text-blue-400 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
                    >
                      ✏️ Editar
                    </button>
                    <button
                      onClick={() => navigate(`/creator/${creator.id}`)}
                      className="bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 text-purple-400 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
                    >
                      👁️ Ver Perfil
                    </button>
                    {!creator.blocked ? (
                      <button
                        onClick={() => handleBlockUser(creator.id, creator.name)}
                        className="bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/50 text-yellow-400 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
                      >
                        🚫 Bloquear
                      </button>
                    ) : (
                      <button
                        onClick={async () => {
                          const token = localStorage.getItem('token');
                          await axios.post(`${API}/admin/users/${creator.id}/unblock`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success('Usuário desbloqueado');
                          loadCreators();
                        }}
                        className="bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 text-green-400 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
                      >
                        ✓ Desbloquear
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteUser(creator.id, creator.name)}
                      className="bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 text-red-400 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
                    >
                      🗑️ Deletar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal de Detalhes */}
        {selectedCreator && creatorDetails && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 max-w-6xl w-full p-6 rounded-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-3xl font-bold">{selectedCreator.name}</h2>
                  <p className="text-gray-400">{selectedCreator.email}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedCreator(null);
                    setCreatorDetails(null);
                  }}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ✕
                </button>
              </div>

              {/* Resumo Financeiro */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-4 rounded-xl border border-[#C79F5E]/30">
                  <div className="text-sm text-gray-400 mb-1">Receita Total</div>
                  <div className="text-2xl font-bold text-[#C79F5E]">R$ {creatorDetails.revenue.total.toFixed(2)}</div>
                </div>
                <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-4 rounded-xl border border-green-500/30">
                  <div className="text-sm text-gray-400 mb-1">Receita Criador (85%)</div>
                  <div className="text-2xl font-bold text-green-400">R$ {creatorDetails.revenue.creator_share.toFixed(2)}</div>
                </div>
                <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-4 rounded-xl border border-[#6B2D5C]/30">
                  <div className="text-sm text-gray-400 mb-1">Comissão LYBERHOT (15%)</div>
                  <div className="text-2xl font-bold text-[#6B2D5C]">R$ {creatorDetails.revenue.platform_share.toFixed(2)}</div>
                </div>
              </div>

              {/* Assinantes */}
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-3">💳 Assinantes ({creatorDetails.subscribers.length})</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {creatorDetails.subscribers.map((sub, idx) => (
                    <div key={idx} className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-3 rounded-lg flex justify-between items-center">
                      <div>
                        <div className="font-semibold">{sub.fan_name}</div>
                        <div className="text-sm text-gray-400">{sub.fan_email}</div>
                      </div>
                      <div className="text-right">
                        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          sub.status === 'active' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {sub.status === 'active' ? 'Ativo' : 'Inativo'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {sub.tier === 'monthly' ? 'Mensal' : sub.tier === 'quarterly' ? 'Trimestral' : 'Anual'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Posts Recentes */}
              <div>
                <h3 className="text-xl font-bold mb-3">📝 Posts Recentes ({creatorDetails.posts.length})</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {creatorDetails.posts.map((post, idx) => (
                    <div key={idx} className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-3 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm">{post.content?.substring(0, 100)}...</p>
                          <div className="text-xs text-gray-500 mt-1">
                            {new Date(post.created_at).toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                        {post.is_premium && (
                          <span className="px-2 py-1 bg-[#C79F5E]/20 text-[#C79F5E] rounded-full text-xs ml-2">
                            Premium
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Criar Criador */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 max-w-lg w-full p-6 rounded-2xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">➕ Criar Novo Criador</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white text-2xl">✕</button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Nome *</label>
                  <input
                    type="text"
                    value={newCreator.name}
                    onChange={(e) => setNewCreator({...newCreator, name: e.target.value})}
                    className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-3 text-white"
                    placeholder="Nome do criador"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Email *</label>
                  <input
                    type="email"
                    value={newCreator.email}
                    onChange={(e) => setNewCreator({...newCreator, email: e.target.value})}
                    className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-3 text-white"
                    placeholder="email@exemplo.com"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Bio</label>
                  <textarea
                    value={newCreator.bio}
                    onChange={(e) => setNewCreator({...newCreator, bio: e.target.value})}
                    className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-3 text-white resize-none"
                    rows={3}
                    placeholder="Descrição do perfil"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Categoria</label>
                  <select
                    value={newCreator.category}
                    onChange={(e) => setNewCreator({...newCreator, category: e.target.value})}
                    className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-3 text-white"
                  >
                    <option value="mulheres">Mulheres</option>
                    <option value="homens">Homens</option>
                    <option value="casais">Casais</option>
                    <option value="trans">Trans</option>
                    <option value="outros">Outros</option>
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Mensal</label>
                    <input
                      type="number"
                      value={newCreator.price_monthly}
                      onChange={(e) => setNewCreator({...newCreator, price_monthly: parseFloat(e.target.value)})}
                      className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Trimestral</label>
                    <input
                      type="number"
                      value={newCreator.price_quarterly}
                      onChange={(e) => setNewCreator({...newCreator, price_quarterly: parseFloat(e.target.value)})}
                      className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Anual</label>
                    <input
                      type="number"
                      value={newCreator.price_annual}
                      onChange={(e) => setNewCreator({...newCreator, price_annual: parseFloat(e.target.value)})}
                      className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">* Senha padrão: admin123 (alterar depois)</p>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-[#0D0D0D] border border-[#C79F5E]/20 px-6 py-3 rounded-lg font-semibold"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateCreator}
                  className="flex-1 bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black px-6 py-3 rounded-lg font-semibold"
                >
                  Criar Criador
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Editar Criador */}
        {showEditModal && editingCreator && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 max-w-lg w-full p-6 rounded-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">✏️ Editar Criador</h2>
                <button onClick={() => { setShowEditModal(false); setEditingCreator(null); setAvatarFile(null); setCoverFile(null); setAvatarPreview(null); setCoverPreview(null); }} className="text-gray-400 hover:text-white text-2xl">✕</button>
              </div>
              
              <div className="space-y-4">
                {/* Cover Image Upload */}
                <div>
                  <label className="text-sm text-gray-400 block mb-2">Imagem de Capa</label>
                  <div 
                    className="relative h-32 bg-gradient-to-r from-[#4D1A4A]/40 to-[#4B0000]/40 rounded-lg overflow-hidden cursor-pointer border-2 border-dashed border-[#C79F5E]/30 hover:border-[#C79F5E]/60 transition-all"
                    onClick={() => document.getElementById('admin-cover-input').click()}
                  >
                    {(coverPreview || editingCreator.cover_image) ? (
                      <img 
                        src={coverPreview || getImageUrl(editingCreator.cover_image)} 
                        alt="Capa" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <span className="text-2xl mb-1">🖼️</span>
                        <span className="text-sm">Clique para adicionar capa</span>
                      </div>
                    )}
                    <input 
                      id="admin-cover-input"
                      type="file" 
                      accept="image/*,.heic,.heif" 
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setCoverFile(file);
                          setCoverPreview(URL.createObjectURL(file));
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Avatar Upload */}
                <div>
                  <label className="text-sm text-gray-400 block mb-2">Foto de Perfil</label>
                  <div className="flex items-center gap-4">
                    <div 
                      className="relative w-20 h-20 rounded-full overflow-hidden cursor-pointer border-2 border-dashed border-[#C79F5E]/30 hover:border-[#C79F5E]/60 transition-all bg-gradient-to-r from-[#6B2D5C] to-[#C79F5E]"
                      onClick={() => document.getElementById('admin-avatar-input').click()}
                    >
                      {(avatarPreview || editingCreator.picture) ? (
                        <img 
                          src={avatarPreview || getImageUrl(editingCreator.picture)} 
                          alt="Avatar" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-2xl text-white">
                          {editingCreator.name?.[0]?.toUpperCase() || '?'}
                        </div>
                      )}
                      <input 
                        id="admin-avatar-input"
                        type="file" 
                        accept="image/*,.heic,.heif" 
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setAvatarFile(file);
                            setAvatarPreview(URL.createObjectURL(file));
                          }
                        }}
                      />
                    </div>
                    <div className="text-sm text-gray-400">
                      <p>Clique para alterar</p>
                      <p className="text-xs">Máximo 10MB</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-400 block mb-1">Nome</label>
                  <input
                    type="text"
                    value={editingCreator.name || ''}
                    onChange={(e) => setEditingCreator({...editingCreator, name: e.target.value})}
                    className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-3 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Bio</label>
                  <textarea
                    value={editingCreator.bio || ''}
                    onChange={(e) => setEditingCreator({...editingCreator, bio: e.target.value})}
                    className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-3 text-white resize-none"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Categoria</label>
                  <select
                    value={editingCreator.category || 'outros'}
                    onChange={(e) => setEditingCreator({...editingCreator, category: e.target.value})}
                    className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-3 text-white"
                  >
                    <option value="mulheres">Mulheres</option>
                    <option value="homens">Homens</option>
                    <option value="casais">Casais</option>
                    <option value="trans">Trans</option>
                    <option value="outros">Outros</option>
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Mensal</label>
                    <input
                      type="number"
                      value={editingCreator.price_monthly || 19.99}
                      onChange={(e) => setEditingCreator({...editingCreator, price_monthly: parseFloat(e.target.value)})}
                      className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Trimestral</label>
                    <input
                      type="number"
                      value={editingCreator.price_quarterly || 49.99}
                      onChange={(e) => setEditingCreator({...editingCreator, price_quarterly: parseFloat(e.target.value)})}
                      className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Anual</label>
                    <input
                      type="number"
                      value={editingCreator.price_annual || 149.99}
                      onChange={(e) => setEditingCreator({...editingCreator, price_annual: parseFloat(e.target.value)})}
                      className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => { setShowEditModal(false); setEditingCreator(null); setAvatarFile(null); setCoverFile(null); setAvatarPreview(null); setCoverPreview(null); }}
                  className="flex-1 bg-[#0D0D0D] border border-[#C79F5E]/20 px-6 py-3 rounded-lg font-semibold"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleEditCreator}
                  disabled={uploadingMedia}
                  className="flex-1 bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black px-6 py-3 rounded-lg font-semibold disabled:opacity-50"
                >
                  {uploadingMedia ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Postar como Criador */}
        {showPostModal && selectedCreator && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 max-w-lg w-full p-6 rounded-2xl">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold">📝 Criar Post</h2>
                  <p className="text-gray-400 text-sm">Postando como: {selectedCreator.name}</p>
                </div>
                <button onClick={() => { setShowPostModal(false); setSelectedCreator(null); }} className="text-gray-400 hover:text-white text-2xl">✕</button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Conteúdo *</label>
                  <textarea
                    value={newPost.content}
                    onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                    className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-3 text-white resize-none"
                    rows={4}
                    placeholder="Escreva o conteúdo do post... Use @nome para mencionar criadores"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">URL da Mídia (opcional)</label>
                  <input
                    type="text"
                    value={newPost.media_urls[0] || ''}
                    onChange={(e) => setNewPost({...newPost, media_urls: e.target.value ? [e.target.value] : []})}
                    className="w-full bg-[#0D0D0D] border border-[#C79F5E]/20 rounded-lg px-4 py-3 text-white"
                    placeholder="https://exemplo.com/imagem.jpg"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_premium"
                    checked={newPost.is_premium}
                    onChange={(e) => setNewPost({...newPost, is_premium: e.target.checked})}
                    className="w-5 h-5 rounded accent-[#C79F5E]"
                  />
                  <label htmlFor="is_premium" className="text-gray-300">Conteúdo Premium (apenas assinantes)</label>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => { setShowPostModal(false); setSelectedCreator(null); }}
                  className="flex-1 bg-[#0D0D0D] border border-[#C79F5E]/20 px-6 py-3 rounded-lg font-semibold"
                >
                  Cancelar
                </button>
                <button
                  onClick={handlePostAsCreator}
                  className="flex-1 bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black px-6 py-3 rounded-lg font-semibold"
                >
                  Publicar Post
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminVerifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    loadPendingVerifications();
  }, []);

  const loadPendingVerifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/verifications/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingVerifications(response.data.pending_verifications);
    } catch (error) {
      console.error('Load verifications error:', error);
      toast.error('Erro ao carregar verificações');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userIdOrEmail) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/verifications/approve`,
        { user_id: userIdOrEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Verificação aprovada!');
      loadPendingVerifications();
      setSelectedUser(null);
    } catch (error) {
      console.error('Erro ao aprovar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao aprovar');
    }
  };

  const handleReject = async (userId) => {
    const reason = prompt('Motivo da rejeição:');
    if (!reason) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/verifications/reject`,
        { user_id: userId, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Verificação rejeitada');
      loadPendingVerifications();
      setSelectedUser(null);
    } catch (error) {
      toast.error('Erro ao rejeitar');
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <Navbar NotificationsPanel={NotificationsPanel} />
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Painel Admin - Verificações</h1>
          <p className="text-gray-400">Revisar documentos de identidade dos criadores</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C79F5E] mx-auto"></div>
            <p className="text-gray-400 mt-4">Carregando...</p>
          </div>
        ) : pendingVerifications.length === 0 ? (
          <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-12 rounded-2xl text-center">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-2xl font-bold mb-2">Tudo em Dia!</h3>
            <p className="text-gray-400">Não há verificações pendentes no momento.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {pendingVerifications.map((userDoc) => (
              <div key={userDoc.id} className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-6 rounded-2xl">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{userDoc.name}</h3>
                    <p className="text-gray-400 text-sm">{userDoc.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      ID: {userDoc.id}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-semibold">
                    Pendente
                  </span>
                </div>

                {userDoc.verification_documents && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-400 mb-2">
                        <strong>Tipo de Documento:</strong> {userDoc.verification_documents.id_type?.toUpperCase()}
                      </p>
                      <p className="text-xs text-gray-500">
                        Enviado em: {new Date(userDoc.verification_documents.submitted_at).toLocaleString('pt-BR')}
                      </p>
                    </div>

                    <button
                      onClick={() => setSelectedUser(userDoc)}
                      className="w-full bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 text-blue-400 py-2 rounded-lg font-semibold transition-all"
                    >
                      Ver Documentos
                    </button>

                    <div className="flex gap-3">
                      <button
                        onClick={() => handleApprove(userDoc.id)}
                        className="flex-1 bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 text-green-400 py-2 rounded-lg font-semibold transition-all"
                      >
                        ✓ Aprovar
                      </button>
                      <button
                        onClick={() => handleReject(userDoc.id)}
                        className="flex-1 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 text-red-400 py-2 rounded-lg font-semibold transition-all"
                      >
                        ✕ Rejeitar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Modal de Visualização de Documentos */}
        {selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
            <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 max-w-4xl w-full p-6 rounded-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold">{selectedUser.name}</h2>
                  <p className="text-gray-400">{selectedUser.email}</p>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ✕
                </button>
              </div>

              {selectedUser.verification_documents && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-bold mb-3">Frente do Documento</h3>
                    <img
                      src={
                        selectedUser.verification_documents.id_front?.startsWith('data:') 
                          ? selectedUser.verification_documents.id_front
                          : selectedUser.verification_documents.id_front?.startsWith('http')
                          ? selectedUser.verification_documents.id_front
                          : `${API}/${selectedUser.verification_documents.id_front?.replace(/^\//, '')}`
                      }
                      alt="ID Front"
                      className="w-full rounded-lg border border-white/20 cursor-pointer hover:scale-105 transition-transform"
                      onClick={(e) => window.open(e.target.src, '_blank')}
                      onError={(e) => {
                        console.error('Erro ao carregar imagem:', e.target.src);
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="%23333" width="400" height="300"/><text fill="%23fff" x="50%" y="50%" text-anchor="middle">Erro ao carregar imagem</text></svg>';
                      }}
                    />
                  </div>

                  {selectedUser.verification_documents.id_back && (
                    <div>
                      <h3 className="text-lg font-bold mb-3">Verso do Documento</h3>
                      <img
                        src={
                          selectedUser.verification_documents.id_back?.startsWith('data:') 
                            ? selectedUser.verification_documents.id_back
                            : selectedUser.verification_documents.id_back?.startsWith('http')
                            ? selectedUser.verification_documents.id_back
                            : `${API}/${selectedUser.verification_documents.id_back?.replace(/^\//, '')}`
                        }
                        alt="ID Back"
                        className="w-full rounded-lg border border-white/20 cursor-pointer hover:scale-105 transition-transform"
                        onClick={(e) => window.open(e.target.src, '_blank')}
                        onError={(e) => {
                          console.error('Erro ao carregar imagem:', e.target.src);
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="%23333" width="400" height="300"/><text fill="%23fff" x="50%" y="50%" text-anchor="middle">Erro ao carregar imagem</text></svg>';
                        }}
                      />
                    </div>
                  )}

                  <div>
                    <h3 className="text-lg font-bold mb-3">Selfie com Documento</h3>
                    <img
                      src={
                        selectedUser.verification_documents.selfie?.startsWith('data:') 
                          ? selectedUser.verification_documents.selfie
                          : selectedUser.verification_documents.selfie?.startsWith('http')
                          ? selectedUser.verification_documents.selfie
                          : `${API}/${encodeURI(selectedUser.verification_documents.selfie?.replace(/^\//, ''))}`
                      }
                      alt="Selfie"
                      className="w-full rounded-lg border border-white/20 cursor-pointer hover:scale-105 transition-transform"
                      onClick={(e) => window.open(e.target.src, '_blank')}
                      onError={(e) => {
                        console.error('Erro ao carregar imagem:', e.target.src);
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="%23333" width="400" height="300"/><text fill="%23fff" x="50%" y="50%" text-anchor="middle">Erro ao carregar imagem</text></svg>';
                      }}
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => handleApprove(selectedUser.email || selectedUser.id)}
                      className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white py-3 rounded-full font-semibold"
                    >
                      ✓ Aprovar Verificação
                    </button>
                    <button
                      onClick={() => handleReject(selectedUser.email || selectedUser.id)}
                      className="flex-1 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white py-3 rounded-full font-semibold"
                    >
                      ✕ Rejeitar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Verificar se WebSocket está habilitado (desabilitado em ambientes de preview)
  const wsEnabled = process.env.REACT_APP_WEBSOCKET_ENABLED !== 'false';

  // Função para iniciar polling HTTP (fallback quando WebSocket não funciona)
  const startPolling = () => {
    if (pollingIntervalRef.current) return; // Já está rodando
    
    console.log('📡 Iniciando HTTP polling para chat em tempo real...');
    pollingIntervalRef.current = setInterval(() => {
      loadConversations();
      if (selectedChat) {
        loadChatMessages(selectedChat, true); // silencioso
      }
    }, 2000); // Poll a cada 2 segundos para melhor experiência
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Conectar WebSocket ou iniciar polling diretamente
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Se WebSocket não está habilitado, usar polling diretamente
    if (!wsEnabled) {
      console.log('📡 WebSocket desabilitado - usando HTTP polling');
      startPolling();
      return () => stopPolling();
    }

    // Timeout para assumir que WebSocket não funciona
    const wsTimeout = setTimeout(() => {
      if (!wsConnected) {
        console.log('⚠️ WebSocket timeout - usando HTTP polling');
        startPolling();
      }
    }, 5000);

    // Determinar URL do WebSocket baseado no BACKEND_URL
    const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsUrl = `${wsProtocol}://${BACKEND_URL.replace(/^https?:\/\//, '')}/ws/chat/${token}`;
    
    console.log('🔌 Tentando conectar WebSocket:', wsUrl);
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ WebSocket conectado');
        setWsConnected(true);
        clearTimeout(wsTimeout);
        stopPolling(); // Para o polling se WebSocket conectar
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('📨 WebSocket message:', data);

        if (data.type === 'new_message') {
          const msg = data.message;
          
          if (selectedChat === msg.sender_id) {
            setMessages(prev => [...prev, msg]);
            
            ws.send(JSON.stringify({
              action: 'mark_read',
              other_user_id: msg.sender_id
            }));
          }
          
          loadConversations();
          
          if (Notification.permission === 'granted' && selectedChat !== msg.sender_id) {
            new Notification('Nova mensagem', {
              body: `${data.sender.name}: ${msg.content}`,
              icon: data.sender.picture || '/logo.png'
            });
          }
        } else if (data.type === 'message_sent') {
          setMessages(prev => [...prev, data.message]);
          loadConversations();
        } else if (data.type === 'user_typing') {
          if (selectedChat === data.user_id) {
            setIsTyping(data.is_typing);
            if (data.is_typing) {
              setTimeout(() => setIsTyping(false), 3000);
            }
          }
        } else if (data.type === 'error') {
          toast.error(data.message);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setWsConnected(false);
        startPolling(); // Iniciar polling como fallback
      };

      ws.onclose = () => {
        console.log('🔌 WebSocket desconectado');
        setWsConnected(false);
        startPolling(); // Iniciar polling como fallback
      };

      return () => {
        clearTimeout(wsTimeout);
        stopPolling();
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      };
    } catch (error) {
      console.error('❌ Erro ao criar WebSocket:', error);
      startPolling();
    }
  }, []);

  useEffect(() => {
    loadConversations();
    
    // Se veio com state para abrir um chat específico
    if (location.state?.openChatWith) {
      setSelectedChat(location.state.openChatWith);
      loadChatMessages(location.state.openChatWith);
    }
  }, [location.state]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Atualizar polling quando mudar o chat selecionado
  useEffect(() => {
    if (selectedChat && !wsConnected && pollingIntervalRef.current) {
      loadChatMessages(selectedChat, true);
    }
  }, [selectedChat, wsConnected]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Conversations error:', error);
    }
  };

  const loadChatMessages = async (userId, silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/chat/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Só atualizar se houver novas mensagens (evitar re-render desnecessário)
      const newMessages = response.data.messages;
      if (JSON.stringify(newMessages) !== JSON.stringify(messages)) {
        setMessages(newMessages);
      }
      setOtherUser(response.data.other_user);
    } catch (error) {
      console.error('Chat messages error:', error);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const handleSelectChat = (userId) => {
    setSelectedChat(userId);
    loadChatMessages(userId);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;

    const messageContent = newMessage;
    setNewMessage(''); // Limpar campo imediatamente para melhor UX

    // Usar WebSocket se conectado, senão usar HTTP fallback
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Enviar via WebSocket
      wsRef.current.send(JSON.stringify({
        action: 'send_message',
        recipient_id: selectedChat,
        content: messageContent
      }));
      
      // Parar indicador de digitação
      wsRef.current.send(JSON.stringify({
        action: 'typing',
        recipient_id: selectedChat,
        is_typing: false
      }));
    } else {
      // Fallback para HTTP
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API}/messages/send`,
          {
            recipient_id: selectedChat,
            content: messageContent
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Adicionar mensagem à lista
        setMessages(prev => [...prev, response.data]);
        loadConversations();
      } catch (error) {
        console.error('Send message error:', error);
        toast.error('Erro ao enviar mensagem');
        setNewMessage(messageContent); // Restaurar mensagem se falhar
      }
    }
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    
    // Notificar que está digitando via WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && selectedChat) {
      wsRef.current.send(JSON.stringify({
        action: 'typing',
        recipient_id: selectedChat,
        is_typing: true
      }));
      
      // Limpar timeout anterior
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Parar de digitar após 2 segundos de inatividade
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            action: 'typing',
            recipient_id: selectedChat,
            is_typing: false
          }));
        }
      }, 2000);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    if (image.startsWith('/uploads')) {
      const token = localStorage.getItem('token');
      const baseUrl = `${BACKEND_URL}/api${image}`;
      // Adicionar token para posts premium
      if (image.includes('/posts/') && token) {
        return `${baseUrl}?token=${token}`;
      }
      return baseUrl;
    }
    return image;
  };

  return (
    <div data-testid="messages-page" className="min-h-screen bg-[#0D0D0D] text-white">
      <Navbar NotificationsPanel={NotificationsPanel} />
      <div className="max-w-7xl mx-auto py-4 px-4 h-[calc(100vh-80px)]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-full">
          {/* Lista de Conversas */}
          <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 rounded-2xl overflow-hidden flex flex-col">
            <div className="p-4 border-b border-[#C79F5E]/20">
              <h2 className="text-xl font-bold flex items-center gap-2 text-[#C79F5E]">
                Mensagens
                {!wsConnected && (
                  <span className="text-xs bg-[#C79F5E]/20 text-[#C79F5E] px-2 py-0.5 rounded-full">
                    HTTP
                  </span>
                )}
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <p className="text-center text-gray-400 p-8">Nenhuma conversa ainda</p>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.user.id}
                    onClick={() => handleSelectChat(conv.user.id)}
                    className={`p-4 border-b border-[#C79F5E]/10 cursor-pointer transition-colors hover:bg-[#C79F5E]/10 ${
                      selectedChat === conv.user.id ? 'bg-[#C79F5E]/20' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-[#4D1A4A] to-[#C79F5E] flex-shrink-0">
                        {conv.user.picture ? (
                          <img src={getImageUrl(conv.user.picture)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xl">
                            {conv.user.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold truncate">{conv.user.name}</p>
                          {conv.unread_count > 0 && (
                            <span className="bg-[#C79F5E] text-black text-xs px-2 py-1 rounded-full font-bold">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 truncate">{conv.last_message?.content}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Área de Chat */}
          <div className="md:col-span-2 bg-[#1A1A1A] border border-[#C79F5E]/20 rounded-2xl overflow-hidden flex flex-col">
            {selectedChat && otherUser ? (
              <>
                {/* Header do Chat */}
                <div className="p-4 border-b border-[#C79F5E]/20 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#4D1A4A] to-[#C79F5E]">
                    {otherUser.picture ? (
                      <img src={getImageUrl(otherUser.picture)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {otherUser.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-bold">{otherUser.name}</p>
                      {wsConnected && (
                        <span className="w-2 h-2 bg-green-500 rounded-full" title="Online"></span>
                      )}
                    </div>
                    {isTyping ? (
                      <p className="text-xs text-[#C79F5E] flex items-center gap-1">
                        <span className="animate-pulse">●</span>
                        Digitando...
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400">{otherUser.role === 'creator' ? 'Criador' : 'Fã'}</p>
                    )}
                  </div>
                </div>

                {/* Mensagens */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg) => {
                    const isMine = msg.sender_id === user.id;
                    return (
                      <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[70%] p-3 rounded-2xl ${
                            isMine
                              ? 'bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] text-white'
                              : 'bg-white/10 text-white'
                          }`}
                        >
                          <MentionText text={msg.content} className="break-words" />
                          <p className={`text-xs mt-1 ${isMine ? 'text-white/70' : 'text-gray-400'}`}>
                            {new Date(msg.created_at).toLocaleTimeString('pt-BR', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input de Mensagem */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={handleTyping}
                      placeholder="Digite sua mensagem... use @ para mencionar"
                      className="flex-1 bg-white/10 border border-white/20 focus:border-[#C79F5E] focus:ring-2 focus:ring-[#C79F5E]/50 rounded-full px-4 py-3 text-white placeholder:text-gray-300 font-medium"
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] text-white px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Enviar
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <div className="text-6xl mb-4">💬</div>
                  <p className="text-xl font-semibold mb-2">Selecione uma conversa</p>
                  <p className="text-sm">Escolha um contato para começar a conversar</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Stories Component
const StoriesBar = () => {
  const [creatorsWithStories, setCreatorsWithStories] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [showStoryModal, setShowStoryModal] = useState(false);

  useEffect(() => {
    loadStoriesBar();
  }, []);

  const loadStoriesBar = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stories/active/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatorsWithStories(response.data);
    } catch (error) {
      console.error('Stories bar error:', error);
    }
  };

  const openStoryModal = async (creatorData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stories/${creatorData.creator.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedCreator({
        ...creatorData.creator,
        stories: response.data
      });
      setCurrentStoryIndex(0);
      setShowStoryModal(true);
    } catch (error) {
      console.error('Load stories error:', error);
    }
  };

  const viewStory = async (storyId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/stories/${storyId}/view`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('View story error:', error);
    }
  };

  const nextStory = () => {
    if (currentStoryIndex < selectedCreator.stories.length - 1) {
      setCurrentStoryIndex(currentStoryIndex + 1);
    } else {
      setShowStoryModal(false);
    }
  };

  const prevStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    if (image.startsWith('/uploads')) {
      const token = localStorage.getItem('token');
      const baseUrl = `${BACKEND_URL}/api${image}`;
      // Adicionar token para posts premium
      if (image.includes('/posts/') && token) {
        return `${baseUrl}?token=${token}`;
      }
      return baseUrl;
    }
    return image;
  };

  if (creatorsWithStories.length === 0) return null;

  return (
    <>
      <div className="overflow-x-auto scrollbar-hide py-4 border-b border-white/5">
        <div className="flex gap-4 px-4">
          {creatorsWithStories.map((item) => (
            <div
              key={item.creator.id}
              onClick={() => openStoryModal(item)}
              className="flex-shrink-0 cursor-pointer group"
            >
              <div className="relative">
                {/* Ring ao redor */}
                <div className={`w-20 h-20 rounded-full p-0.5 ${
                  item.has_unviewed 
                    ? 'bg-gradient-to-tr from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E]'
                    : 'bg-gray-600'
                }`}>
                  <div className="w-full h-full rounded-full overflow-hidden bg-[#1A1A1A] border-2 border-[#1A1A1A]">
                    {item.creator.picture ? (
                      <img
                        src={getImageUrl(item.creator.picture)}
                        alt={item.creator.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#4D1A4A] to-[#6B2D5C] flex items-center justify-center text-white font-bold text-xl">
                        {item.creator.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
                {/* Badge de contagem */}
                {item.stories_count > 1 && (
                  <div className="absolute bottom-0 right-0 bg-[#C79F5E] text-black text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {item.stories_count}
                  </div>
                )}
              </div>
              <p className="text-xs text-center mt-2 text-gray-300 truncate w-20">
                {item.creator.name}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Story Modal */}
      {showStoryModal && selectedCreator && selectedCreator.stories[currentStoryIndex] && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          {/* Header */}
          <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white">
                {selectedCreator.picture ? (
                  <img src={getImageUrl(selectedCreator.picture)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#4D1A4A] to-[#6B2D5C] flex items-center justify-center text-white font-bold">
                    {selectedCreator.name.charAt(0)}
                  </div>
                )}
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{selectedCreator.name}</p>
                <p className="text-white/70 text-xs">
                  {new Date(selectedCreator.stories[currentStoryIndex].created_at).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowStoryModal(false)}
              className="text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress bars */}
          <div className="absolute top-16 left-4 right-4 z-10 flex gap-1">
            {selectedCreator.stories.map((_, index) => (
              <div key={index} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-white rounded-full transition-all ${
                    index === currentStoryIndex ? 'w-full' : index < currentStoryIndex ? 'w-full' : 'w-0'
                  }`}
                />
              </div>
            ))}
          </div>

          {/* Story Media */}
          <div className="relative w-full h-full flex items-center justify-center">
            {selectedCreator.stories[currentStoryIndex].media_type === 'video' ? (
              <video
                key={selectedCreator.stories[currentStoryIndex].id}
                src={getImageUrl(selectedCreator.stories[currentStoryIndex].media_url)}
                className="max-w-full max-h-full object-contain"
                autoPlay
                muted
                playsInline
                controls
                onEnded={nextStory}
                onLoadedData={() => viewStory(selectedCreator.stories[currentStoryIndex].id)}
              />
            ) : (
              <img
                src={getImageUrl(selectedCreator.stories[currentStoryIndex].media_url)}
                alt="Story"
                className="max-w-full max-h-full object-contain"
                onLoad={() => viewStory(selectedCreator.stories[currentStoryIndex].id)}
              />
            )}

            {/* Navigation */}
            <button
              onClick={prevStory}
              className="absolute left-4 text-white/70 hover:text-white transition-colors"
              disabled={currentStoryIndex === 0}
            >
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextStory}
              className="absolute right-4 text-white/70 hover:text-white transition-colors"
            >
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// Notifications Panel Component
const NotificationsPanel = ({ show, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (show) {
      loadNotifications();
    }
  }, [show]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Notifications error:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Atualizar localmente
      setNotifications(notifications.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Mark read error:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Mark all read error:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
      onClose();
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'new_message': return '💬';
      case 'new_subscriber': return '🎉';
      case 'new_post': return '📸';
      case 'new_story': return '⭐';
      case 'subscription_expiring': return '⏰';
      case 'live_started': return '🔴';
      case 'new_follower': return '👥';
      case 'tip_received': return '💰';
      case 'chat_request': return '💌';
      default: return '🔔';
    }
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!show) return null;

  return (
    <div className="absolute top-16 right-0 w-[340px] sm:w-96 max-h-[70vh] sm:max-h-[600px] bg-[#1A1A1A] rounded-2xl border border-white/10 shadow-2xl overflow-hidden z-50" data-testid="notifications-panel">
      {/* Header */}
      <div className="p-4 border-b border-white/10 bg-gradient-to-r from-[#4D1A4A]/20 to-[#6B2D5C]/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-white">Notificações</h3>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-[#C79F5E] hover:text-[#C79F5E]/80 font-semibold transition-colors"
            >
              ✓ Marcar todas como lidas
            </button>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#C79F5E] border-t-transparent mx-auto"></div>
        </div>
      )}

      {/* Notifications List */}
      <div className="overflow-y-auto max-h-[calc(70vh-80px)] sm:max-h-[500px]">
        {!loading && notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <div className="text-5xl mb-3">🔔</div>
            <p className="font-medium">Nenhuma notificação</p>
            <p className="text-sm mt-1">Você está em dia!</p>
          </div>
        ) : (
          <>
            {/* Unread notifications */}
            {notifications.filter(n => !n.read).length > 0 && (
              <div>
                <div className="px-4 py-2 bg-[#0D0D0D] text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Novas
                </div>
                {notifications.filter(n => !n.read).map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="p-4 border-b border-white/5 cursor-pointer transition-all bg-gradient-to-r from-[#6B2D5C]/20 to-[#4D1A4A]/10 hover:from-[#6B2D5C]/30 hover:to-[#4D1A4A]/20"
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#6B2D5C]/30 flex items-center justify-center text-xl flex-shrink-0">
                        {getIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-bold text-white text-sm leading-tight">{notification.title}</p>
                          <span className="text-xs text-[#C79F5E] font-medium whitespace-nowrap">
                            {getTimeAgo(notification.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-300 mt-1 line-clamp-2">{notification.message}</p>
                      </div>
                      <div className="w-2.5 h-2.5 bg-[#C79F5E] rounded-full flex-shrink-0 mt-1 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Read notifications */}
            {notifications.filter(n => n.read).length > 0 && (
              <div>
                <div className="px-4 py-2 bg-[#0D0D0D] text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Anteriores
                </div>
                {notifications.filter(n => n.read).map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="p-4 border-b border-white/5 cursor-pointer transition-colors bg-transparent hover:bg-white/5 opacity-70 hover:opacity-100"
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-xl flex-shrink-0">
                        {getIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-gray-300 text-sm leading-tight">{notification.title}</p>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {getTimeAgo(notification.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 mt-1 line-clamp-2">{notification.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Feed Page component starts here

// Payment Success Component
const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      navigate('/');
    }
  }, [location]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 segundos

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        setPaymentDetails(response.data);
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }

      // Continuar polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Payment status error:', error);
      setPaymentStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {paymentStatus === 'checking' && (
          <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-8 rounded-2xl text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#C79F5E] mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold mb-2">Verificando Pagamento...</h2>
            <p className="text-gray-400">Aguarde enquanto confirmamos sua assinatura</p>
          </div>
        )}

        {paymentStatus === 'success' && (
          <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-8 rounded-2xl text-center">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-3xl font-bold mb-4 text-green-400">Pagamento Confirmado!</h2>
            <p className="text-gray-300 mb-6">
              Sua assinatura <span className="text-[#C79F5E] font-semibold">{paymentDetails?.subscription_plan}</span> foi ativada com sucesso!
            </p>
            <div className="bg-white/5 p-4 rounded-xl mb-6">
              <p className="text-gray-400 text-sm mb-1">Valor pago:</p>
              <p className="text-2xl font-bold text-[#C79F5E]">
                R$ {paymentDetails?.amount?.toFixed(2)}
              </p>
            </div>
            <button
              onClick={() => navigate('/feed')}
              className="w-full bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] text-white px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform"
            >
              Ir para o Feed
            </button>
          </div>
        )}

        {paymentStatus === 'expired' && (
          <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-8 rounded-2xl text-center">
            <div className="text-6xl mb-4">⏰</div>
            <h2 className="text-3xl font-bold mb-4 text-yellow-400">Sessão Expirada</h2>
            <p className="text-gray-300 mb-6">
              A sessão de pagamento expirou. Por favor, tente novamente.
            </p>
            <button
              onClick={() => navigate('/creators')}
              className="w-full bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] text-white px-6 py-3 rounded-full font-semibold"
            >
              Ver Criadores
            </button>
          </div>
        )}

        {(paymentStatus === 'error' || paymentStatus === 'timeout') && (
          <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-8 rounded-2xl text-center">
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-3xl font-bold mb-4 text-red-400">Erro no Pagamento</h2>
            <p className="text-gray-300 mb-6">
              Não foi possível verificar o status do pagamento. Por favor, verifique seu email ou entre em contato com o suporte.
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] text-white px-6 py-3 rounded-full font-semibold"
            >
              Voltar ao Início
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Creator Landing Page Component (Public)
const CreatorLanding = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [creator, setCreator] = useState(null);
  const [previewPosts, setPreviewPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCreatorLanding();
    trackClick();
  }, [username]);

  const trackClick = async () => {
    try {
      await axios.post(`${API}/analytics/track-click/${username}`);
    } catch (error) {
      console.error('Track click error:', error);
    }
  };

  const loadCreatorLanding = async () => {
    try {
      const response = await axios.get(`${API}/c/${username}`);
      setCreator(response.data.creator);
      setPreviewPosts(response.data.preview_posts);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Landing error:', error);
      toast.error('Criador não encontrado');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    if (image.startsWith('/uploads')) {
      const token = localStorage.getItem('token');
      const baseUrl = `${BACKEND_URL}/api${image}`;
      // Adicionar token para posts premium
      if (image.includes('/posts/') && token) {
        return `${baseUrl}?token=${token}`;
      }
      return baseUrl;
    }
    return image;
  };

  const handleJoin = () => {
    // Se já estiver logado, ir direto ao perfil
    if (user && creator) {
      navigate(`/creator/${creator.id}`);
      return;
    }
    // Se não estiver logado, redirecionar para registro com ref parameter
    navigate(`/?ref=${username}&mode=register`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-xl text-gray-400">Carregando...</div>
      </div>
    );
  }

  if (!creator) return null;

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      {/* Cover Image */}
      <div className="relative h-64 md:h-96 bg-gradient-to-br from-[#4D1A4A] via-[#6B2D5C] to-[#4B0000]">
        {creator.cover_image && (
          <img
            src={getImageUrl(creator.cover_image)}
            alt="Cover"
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        
        {/* Logo no canto */}
        <div className="absolute top-4 left-4">
          <img src="/lyber-logo.png" alt="LYBERHOT" className="h-10 w-auto" />
        </div>
      </div>

      {/* Creator Info */}
      <div className="max-w-4xl mx-auto px-4 -mt-20 relative z-10">
        <div className="flex flex-col md:flex-row items-center md:items-end gap-6 mb-8">
          {/* Avatar */}
          <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-[#1A1A1A] bg-gradient-to-br from-[#4D1A4A] to-[#6B2D5C] flex-shrink-0">
            {creator.picture ? (
              <img
                src={getImageUrl(creator.picture)}
                alt={creator.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-white">
                {creator.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          {/* Name and Stats */}
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">{creator.name}</h1>
            {creator.bio && (
              <p className="text-gray-300 mb-4">{creator.bio}</p>
            )}
            <div className="flex gap-6 justify-center md:justify-start text-sm">
              <div>
                <span className="text-2xl font-bold text-[#C79F5E]">{stats.total_posts}</span>
                <p className="text-gray-400">Posts</p>
              </div>
              <div>
                <span className="text-2xl font-bold text-[#C79F5E]">{stats.subscribers}</span>
                <p className="text-gray-400">Assinantes</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-[#1A1A1A] border border-[#C79F5E]/20 p-8 rounded-2xl mb-8 text-center">
          <h2 className="text-2xl font-bold mb-4">🔥 Conteúdo Exclusivo</h2>
          <p className="text-gray-300 mb-6">
            {user ? (
              <>Acesse o perfil completo de <span className="text-[#C79F5E] font-semibold">{creator.name}</span></>
            ) : (
              <>Cadastre-se gratuitamente para acessar todo o conteúdo de <span className="text-[#C79F5E] font-semibold">{creator.name}</span></>
            )}
          </p>
          <button
            onClick={handleJoin}
            className="bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] text-white px-8 py-4 rounded-full text-lg font-bold hover:scale-105 transition-transform"
          >
            {user ? 'Ver Perfil' : 'Cadastrar Grátis'}
          </button>
        </div>

        {/* Preview Posts */}
        {previewPosts.length > 0 && (
          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-4">📸 Preview do Conteúdo</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {previewPosts.map((post, index) => (
                <div key={index} className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer">
                  {post.media_urls && post.media_urls[0] ? (
                    <img
                      src={getImageUrl(post.media_urls[0])}
                      alt="Preview"
                      className="w-full h-full object-cover blur-md group-hover:blur-lg transition-all"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#4D1A4A]/40 to-[#6B2D5C]/40" />
                  )}
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-4xl mb-2">🔒</div>
                      <p className="text-sm font-semibold">Cadastre-se para ver</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-xl text-gray-400">Carregando...</div>
      </div>
    );
  }

  return user ? children : <Navigate to="/" />;
};

function App() {
  // Inicializar proteções de segurança
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const security = new SecurityProtection();
      console.log('🛡️ Proteções de segurança ativadas');
    }
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <ViewerLimitsProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/c/:username" element={<CreatorLanding />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route 
            path="/payment" 
            element={
              <Suspense fallback={<div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#C79F5E] border-t-transparent rounded-full"></div></div>}>
                <PaymentPage />
              </Suspense>
            }
          />
          <Route path="/auth-callback" element={<AuthCallback />} />
          <Route
            path="/reset-password"
            element={<Suspense fallback={<LazyLoadingSpinner />}><ResetPasswordPage /></Suspense>}
          />
          <Route
            path="/creator-setup"
            element={
              <ProtectedRoute>
                <CreatorSetup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quiz"
            element={
              <ProtectedRoute>
                <ProfileQuiz />
              </ProtectedRoute>
            }
          />
          <Route
            path="/feed"
            element={<Suspense fallback={<LazyLoadingSpinner />}><FeedPage /></Suspense>}
          />
          <Route
            path="/live/:liveId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LazyLoadingSpinner />}><LiveStreamPage /></Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/lives"
            element={<Suspense fallback={<LazyLoadingSpinner />}><LivesPage /></Suspense>}
          />
          <Route
            path="/creators"
            element={
              <ProtectedRoute>
                <Creators />
              </ProtectedRoute>
            }
          />
          <Route
            path="/criadores"
            element={
              <ProtectedRoute>
                <Creators />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/:id"
            element={<CreatorProfilePage />}
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/verification"
            element={
              <ProtectedRoute>
                <VerificationKYC />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/verifications"
            element={
              <ProtectedRoute>
                <AdminVerifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/creators"
            element={
              <ProtectedRoute>
                <AdminCreators />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/moderation"
            element={
              <ProtectedRoute>
                <AdminModeration />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/candidatos"
            element={
              <ProtectedRoute>
                <AdminCandidatos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/godparents"
            element={
              <ProtectedRoute>
                <AdminGodparents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/social-invites"
            element={
              <ProtectedRoute>
                <AdminSocialInvites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/social-approvals"
            element={
              <ProtectedRoute>
                <AdminSocialApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/meus-convites"
            element={
              <ProtectedRoute>
                <MyInvites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/withdrawals"
            element={
              <ProtectedRoute>
                <AdminWithdrawals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/masters"
            element={
              <ProtectedRoute>
                <AdminMasters />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/online-users"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LazyLoadingSpinner />}>
                  <OnlineUsersPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LazyLoadingSpinner />}>
                  <VisitsAnalyticsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/support"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LazyLoadingSpinner />}>
                  <AdminSupportPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/banners"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LazyLoadingSpinner />}>
                  <AdminBannersPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/eventos"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <EventsPage />
              </Suspense>
            }
          />
          <Route
            path="/banner/:id"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <BannerDetailPage />
              </Suspense>
            }
          />
          <Route
            path="/aplicar"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <AplicarPage />
              </Suspense>
            }
          />
          <Route
            path="/fundador/:token"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <FundadorCadastro />
              </Suspense>
            }
          />
          <Route
            path="/convite/:token"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <ConviteIndicado />
              </Suspense>
            }
          />
          <Route
            path="/casais-solteiras"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <CasaisSolteiras />
              </Suspense>
            }
          />
          <Route
            path="/godparent-dashboard"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <GodparentDashboardPage />
              </Suspense>
            }
          />
          <Route
            path="/ranking"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LazyLoadingSpinner />}>
                  <Leaderboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leaderboard"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LazyLoadingSpinner />}>
                  <Leaderboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator-chat"
            element={
              <ProtectedRoute>
                <CreatorChatDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LazyLoadingSpinner />}>
                  <CreatorAnalytics />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* ==================== PÁGINAS SEO ==================== */}
          <Route
            path="/criadores-adultos"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <CriadoresAdultos />
              </Suspense>
            }
          />
          <Route
            path="/monetizar-conteudo"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <MonetizarConteudo />
              </Suspense>
            }
          />
          <Route
            path="/casais-liberais"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <CasaisLiberais />
              </Suspense>
            }
          />
          <Route
            path="/plataforma-criadores"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <PlataformaCriadores />
              </Suspense>
            }
          />
          {/* ==================== FIM PÁGINAS SEO ==================== */}

          {/* ==================== PÁGINAS DE ANÚNCIOS ==================== */}
          <Route
            path="/ads-criadores"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <AdsCriadores />
              </Suspense>
            }
          />
          <Route
            path="/ads-casais"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <AdsCasais />
              </Suspense>
            }
          />
          <Route
            path="/ads-membro-fundador"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <AdsMembroFundador />
              </Suspense>
            }
          />
          {/* ==================== FIM PÁGINAS DE ANÚNCIOS ==================== */}

          {/* ==================== PÁGINAS LEGAIS ==================== */}
          <Route
            path="/legal/termos-de-uso"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <TermosDeUso />
              </Suspense>
            }
          />
          <Route
            path="/legal/privacidade"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <PoliticaPrivacidade />
              </Suspense>
            }
          />
          <Route
            path="/legal/membro-fundador"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <TermosMembroFundador />
              </Suspense>
            }
          />
          <Route
            path="/legal/pagamentos"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <PoliticaPagamentos />
              </Suspense>
            }
          />
          <Route
            path="/legal/conduta"
            element={
              <Suspense fallback={<LazyLoadingSpinner />}>
                <CodigoConduta />
              </Suspense>
            }
          />
          {/* ==================== FIM PÁGINAS LEGAIS ==================== */}
        </Routes>
        <Toaster position="top-right" theme="dark" />
        <PWAInstallPrompt />
        <UpdateNotification />
        <PixPostModal />
        <PushNotificationPrompt />
        {/* Chatbot de Suporte - disponível para todos usuários logados (exceto admins) */}
        <SupportChatbotWrapper />
        </BrowserRouter>
        </ViewerLimitsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Wrapper para o Chatbot de Suporte (precisa estar dentro do AuthProvider)
function SupportChatbotWrapper() {
  const { user } = useAuth();
  if (!user) return null;
  return <SupportChatbot user={user} />;
}

// Registrar Service Worker para PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('✅ Service Worker registrado:', registration.scope);
        
        // Forçar verificação de atualização imediata
        registration.update();
        
        // Verificar atualizações periodicamente (a cada 5 minutos)
        setInterval(() => {
          registration.update();
        }, 5 * 60 * 1000);
        
        // Verificar atualizações
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('🔄 Nova versão do Service Worker encontrada');
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Nova versão disponível - notificar usuário
              console.log('📦 Nova versão disponível!');
              // O UpdateNotification component vai mostrar a notificação
              // e o usuário pode escolher quando atualizar
            }
          });
        });
        
        // Se já tiver uma versão esperando, notificar
        if (registration.waiting) {
          console.log('📦 Versão em espera encontrada.');
        }
      })
      .catch((error) => {
        console.error('❌ Erro ao registrar Service Worker:', error);
      });
  });
  
  // Recarregar quando o Service Worker assumir controle
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!refreshing) {
      refreshing = true;
      console.log('🔄 Novo Service Worker assumiu controle. Recarregando...');
      window.location.reload();
    }
  });
}

export default App;